import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import API from "./api";
import { withSessionFetchOptions } from "./authRequest";
import { formatCurrencyAmount, getCurrencyName, normalizeOccupationCurrencyCode } from "./currencyOptions";
import EditProfile from "./EditProfile";
import { getContactDisplayValue } from "./indiaPhone";
import { formatTime12Hour } from "./timeValue";
import WatermarkedPdfRedactionScreen from "./WatermarkedPdfRedactionScreen";
import {
  canDownloadRecommendationBiodata,
  getProfileBiodataSectionCatalog,
  generateProfileBiodataPdf,
} from "./profileBiodataPdf";
import { getProfileMediaLinkTypeLabel } from "./profileMediaLinks";
import {
  formatProfileNumberValue,
  getProfileNumberDisplay,
  isProfileNoDisplaySearchMode,
  PROFILE_NO_DISPLAY_SEARCH_KEY,
  RAW_PROFILE_NUMBER_FIELD,
} from "./profileNumber";
import { hasPermission } from "./rbac";
import { MAX_UPLOAD_FILE_SIZE_LABEL, prepareFilesForUpload } from "./uploadFileCompression";

const WATERMARK_PREVIEW_BATCH_SIZE = 10;
const NOTES_PAGE_SIZE = 12;
const PROFILE_TAB_STICKY_TOP = 86;

const REMARK_SUGGESTIONS = [
  "Interested in profile",
  "Not interested in profile",
  "Called, no response",
  "Called, will call back",
  "Meeting scheduled",
  "Meeting done, positive response",
  "Meeting done, negative response",
  "Profile on hold",
  "Awaiting family approval",
  "Biodata sent",
  "Biodata received",
  "Alliance accepted",
  "Alliance rejected",
  "Follow up required",
  "Profile shortlisted",
  "Profile rejected",
];

const FALLBACK_NOTE_META = {
  note_types: ["general", "call", "meeting", "match_share", "feedback", "follow_up", "internal"],
  outcomes: ["open", "follow_up_required", "callback", "interested", "not_interested", "shared", "resolved", "no_response"],
  follow_up_statuses: ["none", "pending", "scheduled", "completed", "cancelled"],
  defaults: {
    note_type: "general",
  },
};

const FALLBACK_RECOMMENDATION_META = {
  recommendation_statuses: ["recommended", "shared", "viewed", "call", "interested", "meeting_planned", "meeting_done", "matched", "rejected", "cancelled"],
  match_statuses: ["recommended", "shared", "viewed", "call", "interested", "meeting_planned", "meeting_done", "matched", "rejected", "cancelled"],
  outcome_sources: ["manual"],
  contact_sides: ["both"],
  defaults: {
    recommendation_status: "recommended",
    outcome_source: "manual",
    contact_side: "both",
  },
};

const NOTE_TYPE_COPY = {
  general: {
    label: "General",
    icon: "Note",
    helper: "Use for internal context that does not fit a specific CRM interaction.",
    templates: [
      "Family prefers same city match with strong education background.",
      "Important biodata details were rechecked with the family today.",
    ],
  },
  call: {
    label: "Call",
    icon: "Call",
    helper: "Capture phone conversations, callback promises, and spoken objections.",
    templates: [
      "Called family, discussed profile details and next steps.",
      "Phone call completed. Family requested time to review biodata.",
      "Called but no answer. Need another call attempt.",
    ],
  },
  meeting: {
    label: "Meeting",
    icon: "Meet",
    helper: "Log meetings, family visits, and discussion outcomes.",
    templates: [
      "Meeting completed. Families had a positive first discussion.",
      "Meeting scheduled after sharing horoscope and biodata.",
      "Meeting done. Family requested one more conversation before deciding.",
    ],
  },
  match_share: {
    label: "Match Share",
    icon: "Share",
    helper: "Track biodata sharing, match recommendations, and family responses.",
    templates: [
      "Shared biodata with the family and explained key highlights.",
      "Shared shortlisted matches for review and awaiting response.",
      "Biodata sent through WhatsApp and family acknowledged receipt.",
    ],
  },
  feedback: {
    label: "Feedback",
    icon: "Feedback",
    helper: "Capture direct feedback, objections, preferences, and conversion signals.",
    templates: [
      "Family liked education and values but wants same-state preference.",
      "Candidate feedback received. Interested, but timing is not final yet.",
      "Family declined due to location preference mismatch.",
    ],
  },
  follow_up: {
    label: "Follow Up",
    icon: "Follow-up",
    helper: "Use when the main purpose is scheduling or tracking the next action.",
    templates: [
      "Follow up with family after they review the biodata internally.",
      "Recheck interest after the weekend discussion with elders.",
      "Pending callback. Reminder set for next touchpoint.",
    ],
  },
  internal: {
    label: "Internal",
    icon: "Internal",
    helper: "Private operational notes for the admin team.",
    templates: [
      "Sensitive family concern shared internally. Handle with discretion.",
      "Keep this profile in priority queue for senior follow-up.",
    ],
  },
};

const PROFILE_TABS = [
  { id: "overview", label: "Overview" },
  { id: "contacts", label: "Contacts" },
  { id: "career", label: "Education & Career" },
  { id: "family", label: "Family" },
  { id: "relatives", label: "Relatives" },
  { id: "addresses", label: "Addresses" },
  { id: "preferences", label: "Preferences" },
  { id: "media", label: "Media & Documents" },
  { id: "recommendations", label: "Recommendations" },
  { id: "crm", label: "CRM & Package" },
];

const FAMILY_RELATION_TYPES = new Set(["father", "mother", "brother", "sister"]);

function sanitizeWatermarkedFileNamePart(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_]+/g, "")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getWatermarkedImageExtension(document) {
  const mimeType = String(document?.mime_type || "").trim().toLowerCase();
  if (mimeType === "image/jpeg" || mimeType === "image/jpg") return "jpg";
  if (mimeType === "image/png") return "png";
  if (mimeType === "image/webp") return "webp";
  if (mimeType === "image/tiff") return "tiff";
  if (mimeType === "image/avif") return "avif";

  const candidates = [document?.original_file_name, document?.storage_key, document?.file_url]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);

  for (const candidate of candidates) {
    if (candidate.endsWith(".jpeg") || candidate.endsWith(".jpg")) return "jpg";
    if (candidate.endsWith(".png")) return "png";
    if (candidate.endsWith(".webp")) return "webp";
    if (candidate.endsWith(".tiff") || candidate.endsWith(".tif")) return "tiff";
    if (candidate.endsWith(".avif")) return "avif";
  }

  return "png";
}

function buildWatermarkedBiodataFileName(profile, document = null) {
  const fullName = sanitizeWatermarkedFileNamePart(getProfileDisplayName(profile)) || "Profile";
  if (document && isImageMedia(document)) {
    return `JKM_${fullName}_watermarked_Bio_Data.${getWatermarkedImageExtension(document)}`;
  }
  return `JKM_${fullName}_watermarked_Bio_Data.pdf`;
}

function buildProfileBiodataFileName(profile) {
  const fullName = sanitizeWatermarkedFileNamePart(getProfileDisplayName(profile)) || "Profile";
  return `JKM_PROFILE_${fullName}_BIO_DATA.pdf`;
}

function isPdfMedia(document) {
  const mimeType = String(document?.mime_type || "").trim().toLowerCase();
  const possiblePdfSources = [
    document?.file_url,
    document?.storage_key,
    document?.original_file_name,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  const isPdf =
    mimeType === "application/pdf" ||
    possiblePdfSources.some((value) => value.endsWith(".pdf"));

  return isPdf;
}

function isImageMedia(document) {
  const mimeType = String(document?.mime_type || "").trim().toLowerCase();
  if (mimeType.startsWith("image/")) return true;

  const possibleImageSources = [
    document?.file_url,
    document?.storage_key,
    document?.original_file_name,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);

  return possibleImageSources.some((value) => value.match(/\.(jpg|jpeg|png|webp|gif|bmp|svg|tif|tiff|avif)$/i));
}

function isBiodataDocumentMedia(document) {
  if (!isPdfMedia(document) && !isImageMedia(document)) return false;

  const hints = [
    document?.document_type_name,
    document?.template_key,
    document?.original_file_name,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);

  return hints.some((value) => value.includes("biodata") || value.includes("bio data"));
}

function getWatermarkedDownloadLabel(document) {
  return isImageMedia(document) ? "Download Watermarked Image" : "Download Watermarked PDF";
}

function getDocumentDisplayName(document) {
  return (
    String(document?.original_file_name || "").trim() ||
    String(document?.document_type_name || "").trim() ||
    String(document?.template_key || "").trim() ||
    (document?.id ? `Document #${document.id}` : "Document")
  );
}

function buildRecommendationTargetLabel(recommendation) {
  const name = String(recommendation?.to_profile_name || "").trim();
  const profileNo = formatProfileNumberValue(recommendation?.to_profile_no, recommendation?.to_profile_gender);
  if (name && profileNo) return `${name} (#${profileNo})`;
  if (name) return name;
  if (profileNo) return `#${profileNo}`;
  return "Recommended profile";
}

function buildRecommendationNoteContextMap(recommendations = []) {
  const map = new Map();

  safeArray(recommendations).forEach((item) => {
    const recommendationId = item?.id ? String(item.id) : "";
    const targetLabel = buildRecommendationTargetLabel(item);
    const recommendationStatusLabel = formatRecommendationStatusLabel(item?.status || "recommended");

    if (item?.note_id) {
      map.set(String(item.note_id), {
        recommendationId,
        badge: "Recommendation",
        label: `${recommendationStatusLabel} profile ${targetLabel}`,
        meta: recommendationStatusLabel,
      });
    }

    safeArray(item?.outcomes).forEach((outcome) => {
      if (!outcome?.note_id) return;
      map.set(String(outcome.note_id), {
        recommendationId,
        badge: "Recommendation Update",
        label: `${formatRecommendationStatusLabel(outcome.match_status || item?.status || "recommended")} for ${targetLabel}`,
        meta: outcome?.matched_at ? formatDateTime(outcome.matched_at) : "",
      });
    });
  });

  return map;
}

function buildRecommendationTaskTitle(recommendation) {
  const targetLabel = buildRecommendationTargetLabel(recommendation);
  return targetLabel ? `Follow up ${targetLabel}` : "Follow up recommendation";
}

function dedupeTasksById(tasks = []) {
  const seen = new Map();
  safeArray(tasks).forEach((task) => {
    const key = String(task?.id || "").trim();
    if (!key || seen.has(key)) return;
    seen.set(key, task);
  });
  return Array.from(seen.values()).sort((left, right) => {
    const leftTime = new Date(left?.updated_at || left?.created_at || 0).getTime();
    const rightTime = new Date(right?.updated_at || right?.created_at || 0).getTime();
    return rightTime - leftTime;
  });
}

function isTaskVisibleToCurrentUser(task, user) {
  const actorId = Number(user?.id || 0);
  if (!actorId) return false;
  return [
    task?.assigned_to_user_id,
    task?.assigned_by_user_id,
    task?.created_by_user_id,
  ].some((value) => Number(value || 0) === actorId);
}

function formatRecommendationTaskUserLabel(user) {
  return [
    String(user?.display_name || "").trim(),
    String(user?.effective_role_name || "").trim(),
    String(user?.email || "").trim(),
  ]
    .filter(Boolean)
    .join(" - ");
}

function getProfileRecommendationTaskStatusLabel(status) {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "in_progress") return "In Progress";
  if (normalized === "completed") return "Completed";
  return "Pending";
}

function getProfileRecommendationTaskStatusStyle(status) {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "completed") {
    return {
      ...S.noteStatusBadge,
      background: C.successBg,
      color: C.successText,
    };
  }
  if (normalized === "in_progress") {
    return {
      ...S.noteStatusBadge,
      background: "#fff5e8",
      color: C.accentDark,
    };
  }
  return {
    ...S.noteStatusBadge,
    background: C.accentLight,
    color: C.accentDark,
  };
}

function formatRecommendationTaskActivity(item) {
  const action = String(item?.action_type || "").trim();
  if (action === "created") return "Task created";
  if (action === "updated") return "Task details updated";
  if (action === "reassigned") return "Task reassigned";
  if (action === "completed") return "Task completed";
  if (action === "status_changed") {
    if (String(item?.old_status || "").trim() === String(item?.new_status || "").trim()) {
      return `Update added while task remains ${getProfileRecommendationTaskStatusLabel(item?.new_status)}`;
    }
    return `Status changed from ${getProfileRecommendationTaskStatusLabel(item?.old_status)} to ${getProfileRecommendationTaskStatusLabel(item?.new_status)}`;
  }
  return action ? action.replace(/_/g, " ") : "Task updated";
}

function parseHistoryPayload(value) {
  if (value && typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_) {
      return {};
    }
  }
  return {};
}

function formatDocumentHistoryAction(action, entry = null) {
  const normalized = String(action || "").trim().toUpperCase();
  if (normalized === "DOWNLOAD_PROFILE_DOCUMENT") return "Downloaded file";
  if (normalized === "OPEN_PROFILE_DOCUMENT") return "Opened file";
  if (normalized === "DOWNLOAD_WATERMARKED_BIODATA_PDF") {
    const payload = parseHistoryPayload(entry?.new_data || entry?.newData);
    return payload?.format === "image" ? "Downloaded watermarked image" : "Downloaded watermarked PDF";
  }
  if (normalized === "VIEW_WATERMARKED_PDF_PREVIEW") {
    const payload = parseHistoryPayload(entry?.new_data || entry?.newData);
    return payload?.format === "image" ? "Previewed watermarked image" : "Previewed watermarked PDF";
  }
  return normalized ? normalized.replace(/_/g, " ").toLowerCase() : "Activity";
}

function getDocumentHistoryEntryName(entry, fallbackDocument = null) {
  const parsePayload = (value) => {
    if (value && typeof value === "object") return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch (_) {
        return {};
      }
    }
    return {};
  };
  const payload = Object.keys(parsePayload(entry?.new_data)).length
    ? parsePayload(entry?.new_data)
    : parsePayload(entry?.new_value);

  return (
    String(payload?.original_file_name || "").trim() ||
    String(payload?.document_name || "").trim() ||
    String(payload?.document_type_name || "").trim() ||
    getDocumentDisplayName(fallbackDocument)
  );
}

async function extractApiErrorMessage(error, fallbackMessage) {
  const directMessage =
    error?.response?.data?.error ||
    error?.response?.data?.detail ||
    error?.message;

  if (directMessage && typeof directMessage === "string") {
    return directMessage;
  }

  const blob = error?.response?.data;
  if (blob instanceof Blob) {
    try {
      const text = await blob.text();
      const parsed = JSON.parse(text);
      if (parsed?.error || parsed?.detail) {
        return parsed.error || parsed.detail;
      }
    } catch (_) {
      // Ignore blob parsing issues and fall through to the fallback message.
    }
  }

  return fallbackMessage;
}

export default function ProfileDetail({
  profileId,
  mode = "view",
  initialTab = null,
  token,
  onBack,
  backLabel = "Back to Search",
  returnView = "search",
  onOpenAssignedToMe,
  onOpenAssignedByMe,
  onTabChange = null,
  onModeChange = null,
  user,
  onViewProfile,
}) {
  const canEdit =
    hasPermission(user, "profiles", "edit") ||
    hasPermission(user, "profiles", "edit", "duplicate_check") ||
    hasPermission(user, "profiles", "edit", "basic_details") ||
    hasPermission(user, "profiles", "edit", "community") ||
    hasPermission(user, "profiles", "edit", "address_contacts") ||
    hasPermission(user, "profiles", "edit", "contacts") ||
    hasPermission(user, "profiles", "edit", "addresses") ||
    hasPermission(user, "profiles", "edit", "education_career") ||
    hasPermission(user, "profiles", "edit", "family_relatives") ||
    hasPermission(user, "profiles", "edit", "preferences") ||
    hasPermission(user, "profiles", "edit", "persona") ||
    hasPermission(user, "profiles", "edit", "payments") ||
    hasPermission(user, "profiles", "edit", "media") ||
    hasPermission(user, "profiles", "edit", "tags") ||
    hasPermission(user, "profiles", "edit", "photos") ||
    hasPermission(user, "profiles", "edit", "documents") ||
    hasPermission(user, "profiles", "edit", "primary_photo") ||
    hasPermission(user, "profiles", "edit", "family_details");
  const canAddPhotos = hasPermission(user, "profiles", "edit", "media") || hasPermission(user, "profiles", "edit", "photos");
  const canSetPrimaryPhoto = hasPermission(user, "profiles", "edit", "media") || hasPermission(user, "profiles", "edit", "primary_photo");
  const canViewPrimaryPhoto = hasPermission(user, "profiles", "view", "primary_photo");
  const canViewPhotos = hasPermission(user, "profiles", "view", "photos");
  const canViewDocuments = hasPermission(user, "profiles", "view", "documents");
  const canViewTags = hasPermission(user, "profiles", "view", "tags");
  const canViewAlerts = hasPermission(user, "profiles", "view", "alerts");
  const canViewCompleteness = hasPermission(user, "profiles", "view", "completeness");
  const canViewOverviewTab = hasPermission(user, "profiles", "view", "overview_tab");
  const canViewContactsTab = hasPermission(user, "profiles", "view", "contacts_tab");
  const canViewCareerTab = hasPermission(user, "profiles", "view", "career_tab");
  const canViewFamilyTab = hasPermission(user, "profiles", "view", "family_tab");
  const canViewAddressesTab = hasPermission(user, "profiles", "view", "addresses_tab");
  const canViewPreferencesTab = hasPermission(user, "profiles", "view", "preferences_tab");
  const canViewMediaTab = hasPermission(user, "profiles", "view", "media_tab");
  const canViewRecommendationsProfileTab = hasPermission(user, "profiles", "view", "recommendations_tab");
  const canViewCrmTab = hasPermission(user, "profiles", "view", "crm_tab");
  const canViewPhone = false;
  const canViewEmail = false;
  const phoneVisible = false;
  const emailVisible = false;
  const viewingPhone = false;
  const viewingEmail = false;
  const primaryMobile = "";
  const emailContact = "";
  const setPhoneVisible = () => {};
  const setEmailVisible = () => {};
  const handleViewPhone = () => {};
  const handleViewEmail = () => {};
  const hiddenContactFields = [];
  const hasRestrictedContactsNotice = false;
  const canCopyPhone = hasPermission(user, "profiles", "copy", "phone");
  const canCopyEmail = hasPermission(user, "profiles", "copy", "email");
  const canDownloadProfiles = hasPermission(user, "profiles", "download");
  const canViewDocumentHistory = hasPermission(user, "audit", "view");
  const canViewNotes = hasPermission(user, "notes", "view");
  const canAddNotes = hasPermission(user, "notes", "create");
  const canViewTasks = hasPermission(user, "tasks", "view");
  const canCreateTasks = hasPermission(user, "tasks", "create");
  const canGiveRecommendations = hasPermission(user, "recommendations", "give");
  const canViewRecommendationsPermission =
    hasPermission(user, "recommendations", "view") ||
    hasPermission(user, "recommendations", "view", "tab");
  const canEditRecommendations =
    hasPermission(user, "recommendations", "edit") ||
    canAddNotes;
  const canDownloadRecommendationBiodataPermission =
    hasPermission(user, "recommendations", "download") ||
    hasPermission(user, "recommendations", "download", "biodata") ||
    canDownloadProfiles;
  const canDownloadWatermarkedBiodataDocuments = canDownloadRecommendationBiodataPermission;
  const canViewRecommendations =
    canViewRecommendationsProfileTab ||
    canViewRecommendationsPermission ||
    canViewNotes;
  const canViewRecommendationsTab =
    canViewRecommendationsProfileTab ||
    canViewRecommendationsPermission ||
    (canViewCrmTab && (canViewNotes || canAddNotes));
  const showSearchReturnBar = returnView === "search";

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedGalleryPhoto, setSelectedGalleryPhoto] = useState(null);
  const [selectedDocumentImage, setSelectedDocumentImage] = useState(null);

  const [notes, setNotes] = useState([]);
  const [newRemark, setNewRemark] = useState("");
  const [addingRemark, setAddingRemark] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteMeta, setNoteMeta] = useState(FALLBACK_NOTE_META);
  const [noteFilters, setNoteFilters] = useState({
    note_type: "all",
    outcome: "all",
    follow_up: "all",
    follow_up_status: "all",
  });
  const [noteDraft, setNoteDraft] = useState({
    note_type: FALLBACK_NOTE_META.defaults.note_type,
    outcome: "",
    follow_up_at: "",
  });
  const [followUpSourceNote, setFollowUpSourceNote] = useState(null);
  const [resolveFollowUpSource, setResolveFollowUpSource] = useState(true);
  const [rescheduleDraft, setRescheduleDraft] = useState({ noteId: null, value: "" });
  const [noteActionBusyId, setNoteActionBusyId] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationMeta, setRecommendationMeta] = useState(FALLBACK_RECOMMENDATION_META);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [recommendationSearch, setRecommendationSearch] = useState("");
  const [recommendationSearchMode, setRecommendationSearchMode] = useState(PROFILE_NO_DISPLAY_SEARCH_KEY);
  const [recommendationResults, setRecommendationResults] = useState([]);
  const [recommendationSearchLoading, setRecommendationSearchLoading] = useState(false);
  const [selectedRecommendationTargets, setSelectedRecommendationTargets] = useState([]);
  const [recommendationNotesByTarget, setRecommendationNotesByTarget] = useState({});
  const [savingRecommendation, setSavingRecommendation] = useState(false);
  const [recommendationSearchError, setRecommendationSearchError] = useState("");
  const [recommendationSearchFeedback, setRecommendationSearchFeedback] = useState("");
  const [recommendationOutcomeDrafts, setRecommendationOutcomeDrafts] = useState({});
  const [recommendationOutcomeBusyId, setRecommendationOutcomeBusyId] = useState(null);
  const [recommendationDownloadBusyId, setRecommendationDownloadBusyId] = useState(null);
  const [biodataOptionModalOpen, setBiodataOptionModalOpen] = useState(false);
  const [biodataOptionModalLoading, setBiodataOptionModalLoading] = useState(false);
  const [biodataOptionModalError, setBiodataOptionModalError] = useState("");
  const [pendingBiodataRecommendation, setPendingBiodataRecommendation] = useState(null);
  const [pendingBiodataProfile, setPendingBiodataProfile] = useState(null);
  const [biodataOptionSelections, setBiodataOptionSelections] = useState({});
  const [watermarkedDocumentBusyId, setWatermarkedDocumentBusyId] = useState(null);
  const [watermarkedEditorOpen, setWatermarkedEditorOpen] = useState(false);
  const [pendingWatermarkedDocument, setPendingWatermarkedDocument] = useState(null);
  const [watermarkedPreviewLoading, setWatermarkedPreviewLoading] = useState(false);
  const [watermarkedPreviewLoadingMore, setWatermarkedPreviewLoadingMore] = useState(false);
  const [watermarkedPreviewError, setWatermarkedPreviewError] = useState("");
  const [watermarkedPreviewLoadMoreError, setWatermarkedPreviewLoadMoreError] = useState("");
  const [watermarkedPreviewPages, setWatermarkedPreviewPages] = useState([]);
  const [watermarkedPreviewTotalPages, setWatermarkedPreviewTotalPages] = useState(0);
  const [watermarkedPreviewHasMorePages, setWatermarkedPreviewHasMorePages] = useState(false);
  const [watermarkedPreviewNextPageStart, setWatermarkedPreviewNextPageStart] = useState(null);
  const [documentActionBusyKey, setDocumentActionBusyKey] = useState("");
  const [documentHistoryOpen, setDocumentHistoryOpen] = useState(false);
  const [documentHistoryLoading, setDocumentHistoryLoading] = useState(false);
  const [documentHistoryError, setDocumentHistoryError] = useState("");
  const [documentHistoryEntries, setDocumentHistoryEntries] = useState([]);
  const [documentHistoryMeta, setDocumentHistoryMeta] = useState(null);
  const [documentHistoryDocument, setDocumentHistoryDocument] = useState(null);
  const availableWatermarkRemovalOptions = [];
  const watermarkedDownloadSelections = {};
  const [showAlertsOverlay, setShowAlertsOverlay] = useState(false);

  const [showCompletenessOverlay, setShowCompletenessOverlay] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const [primaryIndex, setPrimaryIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [preparingUploadFiles, setPreparingUploadFiles] = useState(false);
  const [uploadPreparationStatus, setUploadPreparationStatus] = useState("");
  const [settingPrimaryId, setSettingPrimaryId] = useState(null);
  const fileInputRef = useRef(null);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [hoveredSuggestion, setHoveredSuggestion] = useState(null);
  const remarkInputRef = useRef(null);

  const [lookups, setLookups] = useState({
    locations: [],
    cityTiers: [],
    castes: [],
    sakhas: [],
    educationCategories: [],
    visaStatuses: [],
    visaNames: [],
  });

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );
  const visibleTabs = useMemo(
    () =>
      PROFILE_TABS.filter((tab) => {
        if (tab.id === "overview") return canViewOverviewTab;
        if (tab.id === "contacts") return canViewContactsTab;
        if (tab.id === "career") return canViewCareerTab;
        if (tab.id === "family") return canViewFamilyTab;
        if (tab.id === "relatives") return canViewFamilyTab;
        if (tab.id === "addresses") return canViewAddressesTab;
        if (tab.id === "preferences") return canViewPreferencesTab;
        if (tab.id === "media") return canViewMediaTab && (canViewPhotos || canViewDocuments);
        if (tab.id === "recommendations") return canViewRecommendationsTab;
        if (tab.id === "crm") return canViewCrmTab;
        return false;
      }),
    [
      canViewAddressesTab,
      canViewCareerTab,
      canViewContactsTab,
      canViewCrmTab,
      canViewDocuments,
      canViewFamilyTab,
      canViewMediaTab,
      canViewOverviewTab,
      canViewPhotos,
      canViewPreferencesTab,
      canViewRecommendationsTab,
    ]
  );
  useEffect(() => {
    const requestedTab = String(initialTab || "").trim().toLowerCase();
    if (!requestedTab) return;
    if (!visibleTabs.some((tab) => tab.id === requestedTab)) return;
    setActiveTab(requestedTab);
  }, [initialTab, profileId, visibleTabs]);
  useEffect(() => {
    if (visibleTabs.some((tab) => tab.id === activeTab)) return;
    setActiveTab(visibleTabs[0]?.id || "overview");
  }, [activeTab, visibleTabs]);
  useEffect(() => {
    onTabChange?.(activeTab);
  }, [activeTab, onTabChange]);

  const buildBiodataOptionList = useCallback(
    (profileData) => {
      if (!profileData) return [];

      const sectionCatalog = getProfileBiodataSectionCatalog(profileData, {
        lookups,
        includePhone: true,
        includeEmail: true,
        includeAddresses: true,
        includeRelativePhone: true,
        includeRelativeEmail: true,
      });
      const labelBySectionId = new Map(
        sectionCatalog
          .filter((section) => section?.id && section?.title)
          .map((section) => [section.id, section.title])
      );
      const photoCandidates = [
        profileData?.primary_photo_url_cache,
        profileData?.photo,
        ...safeArray(profileData?.media).map((item) => item?.file_url || item?.url || item?.src),
        ...safeArray(profileData?.files).map((item) => item?.file_url || item?.url || item?.src),
      ].filter(Boolean);
      const options = sectionCatalog.map((section) => ({
        id: section.id,
        label: labelBySectionId.get(section.id) || section.title || "Section",
      }));

      if (photoCandidates.length > 0) {
        options.splice(2, 0, { id: "profile_photo", label: "Images" });
      }

      return options;
    },
    [lookups]
  );

  const biodataOptionList = useMemo(
    () => buildBiodataOptionList(pendingBiodataProfile),
    [buildBiodataOptionList, pendingBiodataProfile]
  );

  const fetchNotes = useCallback(
    async (filters = noteFilters) => {
      if (!canViewNotes) {
        setNotes([]);
        return;
      }

      setNotesLoading(true);

      try {
        const params = {
          include_meta: 1,
          limit: 200,
        };

        if (filters.note_type && filters.note_type !== "all") params.note_type = filters.note_type;
        if (filters.outcome === "none") params.outcome = "null";
        else if (filters.outcome && filters.outcome !== "all") params.outcome = filters.outcome;
        if (filters.follow_up && filters.follow_up !== "all") params.follow_up = filters.follow_up;
        if (filters.follow_up_status && filters.follow_up_status !== "all") params.follow_up_status = filters.follow_up_status;

        const res = await API.get(`/profiles/${profileId}/notes`, {
          ...authHeaders,
          params,
        });

        const data = res.data;
        setNotes(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []);
        if (data?.allowed_values?.note_types?.length || data?.allowed_values?.outcomes?.length) {
        setNoteMeta((current) => ({
          note_types: data.allowed_values.note_types?.length ? data.allowed_values.note_types : current.note_types,
          outcomes: data.allowed_values.outcomes?.length ? data.allowed_values.outcomes : current.outcomes,
          follow_up_statuses: data.allowed_values.follow_up_statuses?.length ? data.allowed_values.follow_up_statuses : current.follow_up_statuses,
          defaults: current.defaults,
        }));
      }
      } catch (err) {
        console.error("Notes load failed:", err);
      } finally {
        setNotesLoading(false);
      }
    },
    [authHeaders, canViewNotes, noteFilters, profileId]
  );

  const fetchRecommendations = useCallback(async () => {
    if (!canViewRecommendations) {
      setRecommendations([]);
      return;
    }

    setRecommendationsLoading(true);

    try {
      const res = await API.get(`/profiles/${profileId}/recommendations`, {
        ...authHeaders,
        params: { include_meta: 1 },
      });
      const data = res.data || {};
      setRecommendations(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []);
      if (data?.allowed_values) {
        setRecommendationMeta({
          recommendation_statuses: Array.isArray(data.allowed_values.recommendation_statuses) && data.allowed_values.recommendation_statuses.length
            ? data.allowed_values.recommendation_statuses
            : FALLBACK_RECOMMENDATION_META.recommendation_statuses,
          match_statuses: Array.isArray(data.allowed_values.match_statuses) && data.allowed_values.match_statuses.length
            ? data.allowed_values.match_statuses
            : FALLBACK_RECOMMENDATION_META.match_statuses,
          outcome_sources: Array.isArray(data.allowed_values.outcome_sources) && data.allowed_values.outcome_sources.length
            ? data.allowed_values.outcome_sources
            : FALLBACK_RECOMMENDATION_META.outcome_sources,
          contact_sides: Array.isArray(data.allowed_values.contact_sides) && data.allowed_values.contact_sides.length
            ? data.allowed_values.contact_sides
            : FALLBACK_RECOMMENDATION_META.contact_sides,
          defaults: {
            recommendation_status: data?.defaults?.recommendation_status || FALLBACK_RECOMMENDATION_META.defaults.recommendation_status,
            outcome_source: data?.defaults?.outcome_source || FALLBACK_RECOMMENDATION_META.defaults.outcome_source,
            contact_side: data?.defaults?.contact_side || FALLBACK_RECOMMENDATION_META.defaults.contact_side,
          },
        });
      }
    } catch (err) {
      console.error("Recommendations load failed:", err);
    } finally {
      setRecommendationsLoading(false);
    }
  }, [authHeaders, canViewRecommendations, profileId]);

  const fetchProfile = useCallback(() => {
    setLoading(true);
    setError("");

    API.get(`/profiles/${profileId}`, authHeaders)
      .then((res) => {
        const data = res.data;
        setProfile(data);
      })
      .catch((err) => {
        console.error("Profile load failed:", err);
        setError(err.response?.data?.error || "Failed to load profile.");
      })
      .finally(() => setLoading(false));
  }, [profileId, authHeaders]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (!canViewNotes && !canAddNotes) return undefined;
    let mounted = true;

    API.get("/profile-notes/meta", authHeaders)
      .then((res) => {
        if (!mounted) return;
        const data = res.data || {};
        setNoteMeta({
          note_types: Array.isArray(data.note_types) && data.note_types.length ? data.note_types : FALLBACK_NOTE_META.note_types,
          outcomes: Array.isArray(data.outcomes) && data.outcomes.length ? data.outcomes : FALLBACK_NOTE_META.outcomes,
          follow_up_statuses: Array.isArray(data.follow_up_statuses) && data.follow_up_statuses.length ? data.follow_up_statuses : FALLBACK_NOTE_META.follow_up_statuses,
          defaults: {
            note_type: data?.defaults?.note_type || FALLBACK_NOTE_META.defaults.note_type,
          },
        });
      })
      .catch(() => {
        if (!mounted) return;
        setNoteMeta(FALLBACK_NOTE_META);
      });

    return () => {
      mounted = false;
    };
  }, [authHeaders, canAddNotes, canViewNotes]);

  useEffect(() => {
    if (!canViewNotes) return;
    fetchNotes(noteFilters);
  }, [canViewNotes, fetchNotes, noteFilters]);

  useEffect(() => {
    if (!canViewRecommendations) return;
    fetchRecommendations();
  }, [canViewRecommendations, fetchRecommendations]);

  useEffect(() => {
    setNoteDraft((current) => {
      const nextType = noteMeta.defaults?.note_type || noteMeta.note_types?.[0] || FALLBACK_NOTE_META.defaults.note_type;
      if (noteMeta.note_types.includes(current.note_type)) return current;
      return {
        ...current,
        note_type: nextType,
      };
    });
  }, [noteMeta]);

  useEffect(() => {
    let mounted = true;

    Promise.allSettled([
      API.get("/locations"),
      API.get("/city-tiers"),
      API.get("/castes"),
      API.get("/sakhas"),
      API.get("/education-categories"),
      API.get("/visa-statuses"),
      API.get("/visa-names"),
    ]).then((results) => {
      if (!mounted) return;

      setLookups({
        locations: Array.isArray(results[0].value?.data) ? results[0].value.data : [],
        cityTiers: Array.isArray(results[1].value?.data) ? results[1].value.data : [],
        castes: Array.isArray(results[2].value?.data) ? results[2].value.data : [],
        sakhas: Array.isArray(results[3].value?.data) ? results[3].value.data : [],
        educationCategories: Array.isArray(results[4].value?.data) ? results[4].value.data : [],
        visaStatuses: Array.isArray(results[5].value?.data) ? results[5].value.data : [],
        visaNames: Array.isArray(results[6].value?.data) ? results[6].value.data : [],
      });
    });

    return () => {
      mounted = false;
    };
  }, []);

  const contacts = useMemo(() => safeArray(profile?.contacts), [profile]);
  const media = useMemo(() => safeArray(profile?.media || profile?.files), [profile]);
  const mediaLinks = useMemo(() => safeArray(profile?.media_links), [profile]);
  const occupations = useMemo(() => getProfileOccupations(profile), [profile]);
  const primaryOccupation = occupations.find((occupation) => occupation.is_primary) || occupations[0] || null;
  const currentAddress = useMemo(() => pickCompletenessLocationAddress(profile?.addresses), [profile]);
  const photos = useMemo(() => media.filter((m) => !m.media_type || m.media_type === "photo"), [media]);
  const primaryPhoto = photos.find((f) => f.is_primary)?.file_url || profile?.primary_photo_url_cache || null;

  const documents = useMemo(
    () =>
      media.filter((m) => {
        const mediaType = String(m?.media_type || "").trim().toLowerCase();
        if (mediaType === "photo") return false;
        return Boolean(mediaType || m?.document_type_name || m?.template_key);
      }),
    [media]
  );
  const alerts = useMemo(() => safeArray(profile?.alerts), [profile]);
  const openAlerts = useMemo(
    () => alerts.filter((item) => normalizeAlertStatus(item?.status) === "open"),
    [alerts]
  );
  const linkedRecommendationProfileIds = useMemo(
    () => new Set(
      recommendations
        .map((item) => String(item?.to_profile_id || ""))
        .filter(Boolean)
    ),
    [recommendations]
  );
  const selectedRecommendationTargetIds = useMemo(
    () => new Set(selectedRecommendationTargets.map((item) => String(item?.id || "")).filter(Boolean)),
    [selectedRecommendationTargets]
  );
  const recommendationNoteContextMap = useMemo(
    () => buildRecommendationNoteContextMap(recommendations),
    [recommendations]
  );
  const showAssignmentReturnBar = returnView === "assigned-profiles";

  const completenessPct = getCompletenessPct(profile);
  const missingItems = safeArray(profile?.completeness?.missing);
  const completenessOverlaySections = useMemo(
    () => buildCompletenessOverlaySections(profile, missingItems),
    [missingItems, profile]
  );
  const lookupMaps = useMemo(() => buildLookupMaps(lookups), [lookups]);
  const currentAddressDisplay = currentAddress ? formatAddress(currentAddress, profile, lookupMaps) : "";
  const sampradayDisplay = profile?.sampraday_name || "";
  const sampradayDetailsDisplay = profile?.sampraday_details || "";

  const dueFollowUpCount = useMemo(
    () =>
      notes.filter((note) => {
        if (!note?.follow_up_at) return false;
        const followUpDate = new Date(note.follow_up_at);
        return !Number.isNaN(followUpDate.getTime()) && followUpDate.getTime() <= Date.now();
      }).length,
    [notes]
  );
  const scheduledFollowUpCount = useMemo(
    () => notes.filter((note) => !!note?.follow_up_at).length,
    [notes]
  );
  const communicationCount = useMemo(
    () =>
      notes.filter((note) =>
        ["call", "meeting", "match_share"].includes(String(note?.note_type || "").trim().toLowerCase())
      ).length,
    [notes]
  );
  const feedbackCount = useMemo(
    () =>
      notes.filter((note) => String(note?.note_type || "").trim().toLowerCase() === "feedback").length,
    [notes]
  );

  const computedSuggestions = useMemo(() => {
    const noteTypeKey = String(noteDraft.note_type || "").trim().toLowerCase();
    const outcomeKey = String(noteDraft.outcome || "").trim().toLowerCase();
    const latestNote = notes[0];
    const suggestions = [];
    const typeTemplates = NOTE_TYPE_COPY[noteTypeKey]?.templates || [];

    suggestions.push(...typeTemplates);

    if (noteTypeKey === "call") {
      suggestions.push("Called family and introduced the profile. Next response awaited.");
      suggestions.push("Call completed. Family asked to share biodata before deciding.");
    }

    if (noteTypeKey === "meeting") {
      suggestions.push("Meeting scheduled with both families after profile shortlist.");
      suggestions.push("Meeting completed. Compatibility discussion was encouraging.");
    }

    if (noteTypeKey === "match_share") {
      suggestions.push("Shared match details and highlighted family preferences.");
      suggestions.push("Recommended this profile as a suitable shortlist option.");
    }

    if (noteTypeKey === "feedback") {
      suggestions.push("Collected direct feedback and updated future match preference.");
      suggestions.push("Feedback captured from family discussion for next recommendation round.");
    }

    if (outcomeKey === "callback") {
      suggestions.push("Family asked for a callback after discussing with elders.");
    }

    if (outcomeKey === "follow_up_required") {
      suggestions.push("Next follow-up required after family reviews biodata internally.");
    }

    if (outcomeKey === "not_interested") {
      suggestions.push("Family declined the profile after internal discussion.");
    }

    if (outcomeKey === "interested") {
      suggestions.push("Strong positive signal received. Move to next matching step.");
    }

    if (dueFollowUpCount > 0) {
      suggestions.push(`There are ${dueFollowUpCount} due follow-ups pending on this profile.`);
    }

    if (latestNote?.content) {
      suggestions.push(`Latest context: ${String(latestNote.content).slice(0, 88)}`);
    }

    return Array.from(new Set(suggestions.filter(Boolean)));
  }, [dueFollowUpCount, noteDraft.note_type, noteDraft.outcome, notes]);

  const handleRemarkChange = (event) => {
    const value = event.target.value;
    setNewRemark(value);

    const filtered = value.trim()
      ? computedSuggestions.filter((s) => s.toLowerCase().includes(value.toLowerCase()))
      : computedSuggestions.length
        ? computedSuggestions
        : REMARK_SUGGESTIONS;

    setFilteredSuggestions(filtered);
    setShowSuggestions(filtered.length > 0);
  };

  const handleSuggestionClick = (suggestion) => {
    setNewRemark(suggestion);
    setShowSuggestions(false);
    remarkInputRef.current?.focus();
  };

  const handleDraftFieldChange = (field) => (event) => {
    const value = event.target.value;
    setNoteDraft((current) => ({
      ...current,
      [field]: value,
    }));

    if (field === "note_type") {
      const suggestionPool = NOTE_TYPE_COPY[String(value || "").trim().toLowerCase()]?.templates || computedSuggestions;
      setFilteredSuggestions(suggestionPool);
      setShowSuggestions(suggestionPool.length > 0);
    }
  };

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setNoteFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const resetDraft = useCallback(() => {
    setNewRemark("");
    setNoteDraft({
      note_type: noteMeta.defaults?.note_type || noteMeta.note_types?.[0] || FALLBACK_NOTE_META.defaults.note_type,
      outcome: "",
      follow_up_at: "",
    });
    setFollowUpSourceNote(null);
    setResolveFollowUpSource(true);
    setRescheduleDraft({ noteId: null, value: "" });
    setFilteredSuggestions(computedSuggestions);
    setShowSuggestions(false);
  }, [computedSuggestions, noteMeta]);

  const handleStartFollowUp = useCallback((note) => {
    if (!note) return;
    const nextType = noteMeta.note_types.includes("follow_up")
      ? "follow_up"
      : noteMeta.defaults?.note_type || noteMeta.note_types?.[0] || FALLBACK_NOTE_META.defaults.note_type;

    setFollowUpSourceNote(note);
    setResolveFollowUpSource(true);
    setNoteDraft({
      note_type: nextType,
      outcome: "",
      follow_up_at: "",
    });
    setNewRemark(`Follow-up on previous note: ${note.content || ""}`.trim());
    setShowSuggestions(false);

    setTimeout(() => {
      remarkInputRef.current?.focus();
      remarkInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 40);
  }, [noteMeta, setNewRemark]);

  const handleStartReschedule = useCallback((note) => {
    if (!note?.id) return;
    setRescheduleDraft({
      noteId: note.id,
      value: toDateTimeLocalValue(note.follow_up_at),
    });
  }, []);

  const handleSaveReschedule = async (note) => {
    if (!note?.id || !rescheduleDraft.value) return;

    try {
      setNoteActionBusyId(note.id);
      await API.patch(
        `/profile-notes/${note.id}/follow-up/reschedule`,
        {
          follow_up_at: new Date(rescheduleDraft.value).toISOString(),
        },
        authHeaders
      );
      setRescheduleDraft({ noteId: null, value: "" });
      await fetchNotes(noteFilters);
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to reschedule follow-up.");
    } finally {
      setNoteActionBusyId(null);
    }
  };

  const handleCompleteFollowUp = async (note) => {
    if (!note?.id) return;

    try {
      setNoteActionBusyId(note.id);
      await API.patch(`/profile-notes/${note.id}/follow-up/complete`, {}, authHeaders);
      if (followUpSourceNote?.id === note.id) {
        resetDraft();
      }
      await fetchNotes(noteFilters);
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to complete follow-up.");
    } finally {
      setNoteActionBusyId(null);
    }
  };

  const handleCancelFollowUp = async (note) => {
    if (!note?.id) return;

    try {
      setNoteActionBusyId(note.id);
      await API.patch(`/profile-notes/${note.id}/follow-up/cancel`, {}, authHeaders);
      if (followUpSourceNote?.id === note.id) {
        resetDraft();
      }
      await fetchNotes(noteFilters);
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to cancel follow-up.");
    } finally {
      setNoteActionBusyId(null);
    }
  };

  const handleAddRemark = async () => {
    if (!newRemark.trim()) return;

    try {
      setAddingRemark(true);

      await API.post(
        `/profiles/${profileId}/notes`,
        {
          note: newRemark.trim(),
          note_type: noteDraft.note_type,
          outcome: noteDraft.outcome || null,
          follow_up_at: noteDraft.follow_up_at ? new Date(noteDraft.follow_up_at).toISOString() : null,
          follow_up_status: deriveDraftFollowUpStatus(noteDraft),
          parent_note_id: followUpSourceNote?.id || null,
        },
        authHeaders
      );

      if (followUpSourceNote?.id && resolveFollowUpSource) {
        await API.patch(
          `/profile-notes/${followUpSourceNote.id}/follow-up/complete`,
          {},
          authHeaders
        );
      }

      resetDraft();
      if (canViewNotes) {
        await fetchNotes(noteFilters);
      } else {
        fetchProfile();
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to add note.");
    } finally {
      setAddingRemark(false);
    }
  };

  useEffect(() => {
    setFilteredSuggestions(computedSuggestions.length ? computedSuggestions : REMARK_SUGGESTIONS);
  }, [computedSuggestions]);

  useEffect(() => {
    if (activeTab !== "recommendations" || !canGiveRecommendations) return undefined;

    const query = String(recommendationSearch || "").trim();
    const minLength = isProfileNoDisplaySearchMode(recommendationSearchMode) ? 1 : 2;
    if (query.length < minLength) {
      setRecommendationResults([]);
      setRecommendationSearchLoading(false);
      setRecommendationSearchError("");
      setRecommendationSearchFeedback("");
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setRecommendationSearchLoading(true);
      setRecommendationSearchError("");
      try {
        const res = await API.get("/profiles/search", {
          ...authHeaders,
          params: {
            q: query,
            limit: 8,
            search_by: recommendationSearchMode,
            source: "profile-recommendations",
          },
        });
        if (cancelled) return;
        const items = Array.isArray(res.data?.items) ? res.data.items : Array.isArray(res.data) ? res.data : [];
        const selfMatches = items.some((item) => String(item?.id || "") === String(profileId));
        const alreadyRecommendedMatches = items.some((item) => linkedRecommendationProfileIds.has(String(item?.id || "")));
        const alreadySelectedMatches = items.some((item) => selectedRecommendationTargetIds.has(String(item?.id || "")));
        const filteredItems = items
          .filter((item) => {
          const itemId = String(item?.id || "");
          return (
            itemId &&
            itemId !== String(profileId) &&
            !selectedRecommendationTargetIds.has(itemId)
          );
          })
          .map((item) => ({
            ...item,
            recommendationAlreadyGiven: linkedRecommendationProfileIds.has(String(item?.id || "")),
          }));

        setRecommendationResults(filteredItems);

        if (filteredItems.length > 0) {
          setRecommendationSearchFeedback("");
        } else if (selfMatches && alreadyRecommendedMatches) {
          setRecommendationSearchFeedback("Matching profiles were filtered out because the current profile cannot recommend itself and some results are already recommended.");
        } else if (selfMatches) {
          setRecommendationSearchFeedback("This search matches the current profile, which cannot be recommended to itself.");
        } else if (alreadyRecommendedMatches) {
          setRecommendationSearchFeedback("Matching profiles are already linked in recommendations for this profile.");
        } else if (alreadySelectedMatches) {
          setRecommendationSearchFeedback("Matching profiles are already selected for this batch.");
        } else {
          setRecommendationSearchFeedback("No matching profiles available for recommendation.");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Recommendation search failed:", err);
        setRecommendationResults([]);
        setRecommendationSearchError(err?.response?.data?.error || "Could not search profiles right now.");
        setRecommendationSearchFeedback("");
      } finally {
        if (!cancelled) setRecommendationSearchLoading(false);
      }
    }, 260);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeTab, authHeaders, canGiveRecommendations, linkedRecommendationProfileIds, profileId, recommendationSearch, recommendationSearchMode, selectedRecommendationTargetIds]);

  const handleSelectRecommendationTarget = useCallback((target) => {
    if (!target) {
      setSelectedRecommendationTargets([]);
      setRecommendationResults([]);
      setRecommendationNotesByTarget({});
      return;
    }

    setSelectedRecommendationTargets((current) => {
      const targetId = String(target?.id || "");
      if (
        !targetId ||
        target?.recommendationAlreadyGiven === true ||
        current.some((item) => String(item?.id || "") === targetId)
      ) return current;
      return [...current, target];
    });
    setRecommendationSearch("");
    setRecommendationResults([]);
    setRecommendationSearchFeedback("");
  }, []);

  const handleRemoveRecommendationTarget = useCallback((targetId) => {
    const normalizedTargetId = String(targetId || "");
    const nextTargets = selectedRecommendationTargets.filter((item) => String(item?.id || "") !== normalizedTargetId);
    setSelectedRecommendationTargets(nextTargets);
    setRecommendationNotesByTarget((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, normalizedTargetId)) return current;
      const next = { ...current };
      delete next[normalizedTargetId];
      return next;
    });
  }, [selectedRecommendationTargets]);

  const handleRecommendationTargetNoteChange = useCallback((targetId, value) => {
    const normalizedTargetId = String(targetId || "");
    if (!normalizedTargetId) return;
    setRecommendationNotesByTarget((current) => ({
      ...current,
      [normalizedTargetId]: value,
    }));
  }, []);

  const handleCreateRecommendation = async () => {
    const targets = selectedRecommendationTargets
      .map((item) => {
        const targetId = String(item?.id || "").trim();
        if (!targetId) return null;
        return {
          to_profile_id: targetId,
          note: String(recommendationNotesByTarget[targetId] || "").trim() || null,
        };
      })
      .filter(Boolean);
    if (!canGiveRecommendations || targets.length === 0) return;

    try {
      setSavingRecommendation(true);
      const res = await API.post(
        `/profiles/${profileId}/recommendations`,
        {
          recommendations: targets,
        },
        authHeaders
      );
      const createdCount = Number(res.data?.created_count || 0);
      const skippedCount = Number(res.data?.skipped_count || 0);
      setRecommendationSearch("");
      setRecommendationResults([]);
      setSelectedRecommendationTargets([]);
      setRecommendationNotesByTarget({});
      await Promise.all([
        fetchRecommendations(),
        canViewNotes ? fetchNotes(noteFilters) : Promise.resolve(),
      ]);
      if (!createdCount && skippedCount) {
        alert("Selected profiles were already recommended from this profile.");
      }
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to create recommendation.");
    } finally {
      setSavingRecommendation(false);
    }
  };

  const handleRecommendationOutcomeDraftChange = useCallback((recommendationId, field, value) => {
    setRecommendationOutcomeDrafts((current) => ({
      ...current,
      [recommendationId]: {
        status: current[recommendationId]?.status || "",
        notes: current[recommendationId]?.notes || "",
        contactSide:
          current[recommendationId]?.contactSide ||
          recommendationMeta.defaults?.contact_side ||
          "both",
        [field]: value,
      },
    }));
  }, [recommendationMeta.defaults?.contact_side]);

  const handleSaveRecommendationOutcome = async (recommendation) => {
    const recommendationId = recommendation?.id;
    const draft = recommendationOutcomeDrafts[recommendationId] || {};
    if (!canEditRecommendations || !recommendationId || !draft.status) return;

    try {
      setRecommendationOutcomeBusyId(recommendationId);
      await API.post(
        `/recommendations/${recommendationId}/outcomes`,
        {
          match_status: draft.status,
          outcome_notes: String(draft.notes || "").trim() || null,
          outcome_source: recommendationMeta.defaults?.outcome_source || "manual",
          contact_side: draft.contactSide || recommendationMeta.defaults?.contact_side || "both",
        },
        authHeaders
      );
      setRecommendationOutcomeDrafts((current) => {
        const next = { ...current };
        delete next[recommendationId];
        return next;
      });
      await Promise.all([
        fetchRecommendations(),
        canViewNotes ? fetchNotes(noteFilters) : Promise.resolve(),
      ]);
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to save recommendation outcome.");
    } finally {
      setRecommendationOutcomeBusyId(null);
    }
  };

  const closeBiodataOptionModal = useCallback((force = false) => {
    if (!force && recommendationDownloadBusyId === pendingBiodataRecommendation?.id) return;
    setBiodataOptionModalOpen(false);
    setBiodataOptionModalLoading(false);
    setBiodataOptionModalError("");
    setPendingBiodataRecommendation(null);
    setPendingBiodataProfile(null);
    setBiodataOptionSelections({});
  }, [pendingBiodataRecommendation?.id, recommendationDownloadBusyId]);

  const handleBiodataOptionSelectionChange = useCallback((optionId, checked) => {
    setBiodataOptionSelections((current) => ({
      ...current,
      [optionId]: checked,
    }));
  }, []);

  const handleDownloadRecommendationBiodata = async (recommendation) => {
    const recommendationId = recommendation?.id;
    const targetId = recommendation?.to_profile_id;
    if (!canDownloadRecommendationBiodataPermission || !recommendationId || !targetId) return;

    try {
      setBiodataOptionModalOpen(true);
      setBiodataOptionModalLoading(true);
      setBiodataOptionModalError("");
      setPendingBiodataRecommendation(recommendation);
      setPendingBiodataProfile(null);
      setBiodataOptionSelections({});

      const res = await API.get(`/profiles/${targetId}`, {
        ...authHeaders,
        params: {
          recommendation_id: recommendationId,
          source_profile_id: profileId,
        },
      });
      const nextProfile = res.data;
      const nextOptions = buildBiodataOptionList(nextProfile);
      if (!nextOptions.length) {
        setBiodataOptionModalError("No biodata sections are available on this profile.");
      }
      setPendingBiodataProfile(nextProfile);
      setBiodataOptionSelections(
        nextOptions.reduce((acc, option) => {
          acc[option.id] = true;
          return acc;
        }, {})
      );
    } catch (err) {
      console.error("Biodata PDF generation failed:", err);
      setBiodataOptionModalError(
        err?.response?.data?.error || err?.message || "Failed to prepare biodata options."
      );
    } finally {
      setBiodataOptionModalLoading(false);
    }
  };

  const handleConfirmRecommendationBiodataDownload = async () => {
    const recommendationId = pendingBiodataRecommendation?.id;
    const targetId = pendingBiodataRecommendation?.to_profile_id;
    if (!canDownloadRecommendationBiodataPermission || !recommendationId || !targetId || !pendingBiodataProfile) return;

    const selectedSectionIds = biodataOptionList
      .filter((option) => option.id !== "profile_photo" && biodataOptionSelections[option.id] === true)
      .map((option) => option.id);

    if (!selectedSectionIds.length) {
      setBiodataOptionModalError("Select at least one biodata section to include.");
      return;
    }

    try {
      setRecommendationDownloadBusyId(recommendationId);
      setBiodataOptionModalError("");
      await API.post(
        `/profiles/${targetId}/biodata-download`,
        {
          recommendation_id: recommendationId,
          source_profile_id: profileId,
        },
        authHeaders
      );
      await generateProfileBiodataPdf(pendingBiodataProfile, {
        lookups,
        includePhone: true,
        includeEmail: true,
        includeAddresses: true,
        includeRelativePhone: true,
        includeRelativeEmail: true,
        includePhoto: biodataOptionSelections.profile_photo === true,
        includedSectionIds: selectedSectionIds,
        fileName: buildProfileBiodataFileName(pendingBiodataProfile),
        accessContext: {
          recommendationId,
          sourceProfileId: profileId,
        },
      });
      closeBiodataOptionModal(true);
    } catch (err) {
      console.error("Biodata PDF generation failed:", err);
      setBiodataOptionModalError(
        err?.response?.data?.error || err?.message || "Failed to download biodata PDF."
      );
    } finally {
      setRecommendationDownloadBusyId(null);
    }
  };

  const loadWatermarkedPreviewPages = useCallback(async (
    docItem,
    { startPage = 1, append = false } = {}
  ) => {
    if (!docItem?.id || !profileId) return [];

    if (append) {
      setWatermarkedPreviewLoadingMore(true);
      setWatermarkedPreviewLoadMoreError("");
    } else {
      setWatermarkedPreviewLoading(true);
      setWatermarkedPreviewError("");
      setWatermarkedPreviewLoadMoreError("");
    }

    try {
      const res = await API.get(
        `/profiles/${profileId}/documents/${docItem.id}/watermarked-preview`,
        {
          ...authHeaders,
          params: {
            startPage,
            pageCount: WATERMARK_PREVIEW_BATCH_SIZE,
          },
        }
      );
      const nextPages = Array.isArray(res.data?.pages) ? res.data.pages : [];

      setWatermarkedPreviewTotalPages(Number(res.data?.page_count || nextPages.length || 0));
      setWatermarkedPreviewHasMorePages(Boolean(res.data?.has_more_pages));
      setWatermarkedPreviewNextPageStart(
        Number(res.data?.next_page_start || 0) > 0 ? Number(res.data.next_page_start) : null
      );
      setWatermarkedPreviewPages((current) => {
        if (!append) return nextPages;

        const merged = new Map();
        [...current, ...nextPages].forEach((page) => {
          if (page?.pageNumber) merged.set(page.pageNumber, page);
        });
        return Array.from(merged.values()).sort((left, right) => left.pageNumber - right.pageNumber);
      });

      return nextPages;
    } catch (err) {
      if (append) {
        setWatermarkedPreviewLoadMoreError(
          await extractApiErrorMessage(err, "Failed to load more preview pages.")
        );
      } else {
        throw err;
      }
      return [];
    } finally {
      if (append) {
        setWatermarkedPreviewLoadingMore(false);
      } else {
        setWatermarkedPreviewLoading(false);
      }
    }
  }, [authHeaders, profileId]);

  const openWatermarkedDownloadOptions = async (docItem) => {
    if (!canDownloadWatermarkedBiodataDocuments || !docItem?.id || !isBiodataDocumentMedia(docItem)) return;
    setPendingWatermarkedDocument(docItem);
    setWatermarkedPreviewPages([]);
    setWatermarkedPreviewError("");
    setWatermarkedPreviewLoadMoreError("");
    setWatermarkedPreviewTotalPages(0);
    setWatermarkedPreviewHasMorePages(false);
    setWatermarkedPreviewNextPageStart(null);
    setWatermarkedPreviewLoading(true);
    setWatermarkedEditorOpen(true);

    try {
      await loadWatermarkedPreviewPages(docItem, { startPage: 1, append: false });
    } catch (err) {
      console.error("Watermarked preview load failed:", err);
      setWatermarkedPreviewError(
        await extractApiErrorMessage(err, "Failed to load the biodata preview.")
      );
    } finally {
      setWatermarkedPreviewLoading(false);
    }
  };

  const closeWatermarkedDownloadOptions = () => {
    if (watermarkedDocumentBusyId) return;
    setWatermarkedEditorOpen(false);
    setPendingWatermarkedDocument(null);
    setWatermarkedPreviewPages([]);
    setWatermarkedPreviewError("");
    setWatermarkedPreviewLoadMoreError("");
    setWatermarkedPreviewLoading(false);
    setWatermarkedPreviewLoadingMore(false);
    setWatermarkedPreviewTotalPages(0);
    setWatermarkedPreviewHasMorePages(false);
    setWatermarkedPreviewNextPageStart(null);
  };
  const handleWatermarkedSelectionChange = () => {};

  const handleDownloadWatermarkedDocument = async (
    redactions,
    docItem = pendingWatermarkedDocument
  ) => {
    const documentId = docItem?.id;
    if (!canDownloadWatermarkedBiodataDocuments || !documentId || !isBiodataDocumentMedia(docItem) || !profile) return;

    try {
      setWatermarkedDocumentBusyId(documentId);
      const res = await API.post(
        `/profiles/${profileId}/documents/${documentId}/watermarked-download`,
        {
          redactions: Array.isArray(redactions) ? redactions : [],
        },
        {
          ...authHeaders,
          responseType: "blob",
        }
      );

      const responseContentType = String(res.headers?.["content-type"] || "").trim() || "application/octet-stream";
      const blob = new Blob([res.data], { type: responseContentType });
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = buildWatermarkedBiodataFileName(profile, docItem);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(downloadUrl);
      closeWatermarkedDownloadOptions();
    } catch (err) {
      console.error("Watermarked file download failed:", err);
      const errorMessage = await extractApiErrorMessage(
        err,
        isImageMedia(docItem)
          ? "Failed to download the watermarked image."
          : "Failed to download the watermarked PDF."
      );
      alert(errorMessage);
    } finally {
      setWatermarkedDocumentBusyId(null);
    }
  };

  const loadDocumentHistory = useCallback(
    async (docItem) => {
      if (!docItem?.id) return null;

      const res = await API.get(
        `/profiles/${profileId}/documents/${docItem.id}/history`,
        {
          ...authHeaders,
          params: {
            include_meta: 1,
          },
        }
      );

      setDocumentHistoryEntries(Array.isArray(res.data?.items) ? res.data.items : []);
      setDocumentHistoryMeta(res.data?.document || null);
      return res.data || null;
    },
    [authHeaders, profileId]
  );

  const closeDocumentHistory = useCallback(() => {
    setDocumentHistoryOpen(false);
    setDocumentHistoryLoading(false);
    setDocumentHistoryError("");
    setDocumentHistoryEntries([]);
    setDocumentHistoryMeta(null);
    setDocumentHistoryDocument(null);
  }, []);

  const openDocumentHistory = useCallback(
    async (docItem) => {
      if (!canViewDocumentHistory || !docItem?.id) return;

      setDocumentHistoryDocument(docItem);
      setDocumentHistoryOpen(true);
      setDocumentHistoryLoading(true);
      setDocumentHistoryError("");
      setDocumentHistoryEntries([]);
      setDocumentHistoryMeta(null);

      try {
        await loadDocumentHistory(docItem);
      } catch (err) {
        console.error("Document history load failed:", err);
        setDocumentHistoryError(
          await extractApiErrorMessage(err, "Failed to load document history.")
        );
      } finally {
        setDocumentHistoryLoading(false);
      }
    },
    [canViewDocumentHistory, loadDocumentHistory]
  );

  const handleDocumentFileAction = useCallback(
    async (docItem, disposition) => {
      if (!docItem?.id) return;

      const documentIsImage = isImageMedia(docItem);
      const normalizedDisposition = disposition === "attachment" ? "attachment" : "inline";
      const busyKey = `${normalizedDisposition}:${docItem.id}`;
      const previewWindow =
        normalizedDisposition === "inline" && !documentIsImage
          ? window.open("", "_blank", "noopener,noreferrer")
          : null;

      try {
        setDocumentActionBusyKey(busyKey);
        const res = await API.get(
          `/profiles/${profileId}/documents/${docItem.id}/file`,
          {
            ...authHeaders,
            params: {
              disposition: normalizedDisposition,
            },
            responseType: "blob",
          }
        );

        const blob = new Blob([res.data], {
          type: res.headers?.["content-type"] || docItem.mime_type || "application/octet-stream",
        });
        const objectUrl = window.URL.createObjectURL(blob);

        if (normalizedDisposition === "inline") {
          if (documentIsImage) {
            setSelectedDocumentImage((current) => {
              if (current?.preview_url) {
                window.URL.revokeObjectURL(current.preview_url);
              }
              return {
                ...docItem,
                preview_url: objectUrl,
              };
            });
          } else {
            if (previewWindow && !previewWindow.closed) {
              previewWindow.location = objectUrl;
            } else {
              window.open(objectUrl, "_blank", "noopener,noreferrer");
            }
            window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
          }
        } else {
          const anchor = document.createElement("a");
          anchor.href = objectUrl;
          anchor.download = docItem.original_file_name || getDocumentDisplayName(docItem);
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
          window.URL.revokeObjectURL(objectUrl);
        }

        if (documentHistoryOpen && documentHistoryDocument?.id === docItem.id) {
          loadDocumentHistory(docItem).catch(() => {});
        }
      } catch (err) {
        if (previewWindow && !previewWindow.closed) {
          previewWindow.close();
        }
        console.error("Document action failed:", err);
        alert(
          await extractApiErrorMessage(
            err,
            normalizedDisposition === "attachment"
              ? "Failed to download the document."
              : "Failed to open the document."
          )
        );
      } finally {
        setDocumentActionBusyKey("");
      }
    },
    [authHeaders, documentHistoryDocument, documentHistoryOpen, loadDocumentHistory, profileId]
  );

const handleFileSelect = async (event) => {
  const input = event.target;
  const selected = Array.from(input.files || []);
  setUploadStatus("");
  setUploadPreparationStatus("");

  if (!selected.length) {
    input.value = "";
    return;
  }

  const validSizeFiles = await prepareFilesForUpload(selected, {
    allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    allowedTypeLabel: "JPEG, PNG and WEBP",
    fileKindLabel: "photo",
    onStatusChange: ({ active, message }) => {
      setPreparingUploadFiles(active);
      setUploadPreparationStatus(active ? message : "");
    },
  });

  const remaining = Math.max(0, 10 - imageFiles.length);

  if (remaining <= 0) {
    alert("Maximum 10 photos are allowed in one upload.");
    input.value = "";
    return;
  }

  const entries = validSizeFiles.slice(0, remaining).map((file) => ({
    file,
    previewUrl: URL.createObjectURL(file),
    name: file.name,
    size: file.size,
    type: file.type,
  }));

  if (!entries.length) {
    input.value = "";
    return;
  }

  setImageFiles((prev) => [...prev, ...entries].slice(0, 10));
  setPrimaryIndex((current) =>
    imageFiles.length === 0 && current === 0 ? 0 : current
  );

  setPreparingUploadFiles(false);
  setUploadPreparationStatus("");
  input.value = "";
};

  const removeImage = (idx) => {
    setImageFiles((prev) => {
      URL.revokeObjectURL(prev[idx].previewUrl);
      return prev.filter((_, i) => i !== idx);
    });

    setPrimaryIndex((pi) => {
      if (pi === idx) return 0;
      if (pi > idx) return pi - 1;
      return pi;
    });
  };

  const closeUpload = () => {
    if (preparingUploadFiles) return;
    imageFiles.forEach((file) => URL.revokeObjectURL(file.previewUrl));
    setImageFiles([]);
    setPrimaryIndex(0);
    setUploadStatus("");
    setUploadPreparationStatus("");
    setShowUpload(false);
  };

const handleUpload = async () => {
  if (!canAddPhotos) return;

  const uploadItems = imageFiles.filter(
    (item) =>
      item &&
      item.file &&
      typeof item.file === "object" &&
      item.file.size > 0 &&
      ["image/jpeg", "image/png", "image/webp"].includes(item.file.type)
  );

  if (!uploadItems.length) {
    setUploadStatus("Please select at least one valid photo.");
    return;
  }

  setUploading(true);
  setUploadStatus(
    `Uploading ${uploadItems.length} photo${uploadItems.length > 1 ? "s" : ""}...`
  );

  try {
    const formData = new FormData();

    uploadItems.forEach((item) => {
      formData.append("images", item.file, item.file.name || "profile-photo");
    });

    const safePrimaryIndex =
      Number.isInteger(primaryIndex) &&
      primaryIndex >= 0 &&
      primaryIndex < uploadItems.length
        ? primaryIndex
        : 0;

    formData.append("primary_image_index", String(safePrimaryIndex));
    formData.append("primaryImageIndex", String(safePrimaryIndex));

    // Use native fetch for this multipart request.
    // Do not set Content-Type; the browser must set multipart boundary automatically.
    const baseURL = API.defaults?.baseURL || "/api";

    const response = await fetch(
      `${baseURL}/profiles/${profileId}/images`,
      withSessionFetchOptions({
        method: "POST",
        body: formData,
      })
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || "Image upload failed");
    }

    setUploadStatus("Done. Reloading...");

    setTimeout(() => {
      closeUpload();
      fetchProfile();
    }, 700);
  } catch (err) {
    setUploadStatus("");
    alert("Upload failed: " + (err.message || "Image upload failed"));
  } finally {
    setUploading(false);
  }
};

  const handleSetPrimary = async (fileId) => {
    if (!canSetPrimaryPhoto || !fileId || settingPrimaryId === fileId) return;

    setSettingPrimaryId(fileId);

    try {
      await API.patch(`/profiles/${profileId}/images/${fileId}/primary`, {}, authHeaders);
      fetchProfile();
    } catch (err) {
      alert("Failed to set primary image: " + (err?.response?.data?.error || err.message));
    } finally {
      setSettingPrimaryId(null);
    }
  };

  useEffect(() => {
    if (!selectedGalleryPhoto && !selectedDocumentImage && !showCompletenessOverlay && !showAlertsOverlay) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setSelectedGalleryPhoto(null);
        setSelectedDocumentImage(null);
        setShowCompletenessOverlay(false);
        setShowAlertsOverlay(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [selectedDocumentImage, selectedGalleryPhoto, showAlertsOverlay, showCompletenessOverlay]);

  useEffect(() => {
    return () => {
      if (selectedDocumentImage?.preview_url) {
        window.URL.revokeObjectURL(selectedDocumentImage.preview_url);
      }
    };
  }, [selectedDocumentImage]);

  useEffect(() => {
    const hasOpenOverlay =
      selectedDocumentImage ||
      showCompletenessOverlay ||
      showAlertsOverlay ||
      showUpload ||
      biodataOptionModalOpen ||
      documentHistoryOpen;
    if (!hasOpenOverlay) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [biodataOptionModalOpen, documentHistoryOpen, selectedDocumentImage, showAlertsOverlay, showCompletenessOverlay, showUpload]);

  if (mode === "edit") {
    return (
      <EditProfile
        profileId={profileId}
        token={token}
        user={user}
        onBack={() => onModeChange?.("view")}
        onViewProfile={onViewProfile}
        onSaved={() => {
          fetchProfile();
        }}
      />
    );
  }

  if (loading) {
    return (
      <div style={S.centerWrap}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={S.spinner} />
        <p style={S.loadingText}>Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={S.centerWrap}>
        <p style={S.errorText}>{error}</p>
        <button style={S.backBtn} onClick={onBack}>{`< ${backLabel}`}</button>
      </div>
    );
  }

  if (!profile) return null;

  if (watermarkedEditorOpen) {
    return (
      <div style={S.watermarkPage}>
        <style>{`
          @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
        <div style={S.watermarkWorkspaceWrap}>
          <WatermarkedPdfRedactionScreen
            open
            pages={watermarkedPreviewPages}
            loading={watermarkedPreviewLoading}
            loadingMorePages={watermarkedPreviewLoadingMore}
            error={watermarkedPreviewError}
            loadMoreError={watermarkedPreviewLoadMoreError}
            busy={watermarkedDocumentBusyId === pendingWatermarkedDocument?.id}
            totalPageCount={watermarkedPreviewTotalPages}
            hasMorePages={watermarkedPreviewHasMorePages}
            fileName={profile ? buildWatermarkedBiodataFileName(profile, pendingWatermarkedDocument) : "Watermarked_Bio_Data.pdf"}
            confirmLabel={getWatermarkedDownloadLabel(pendingWatermarkedDocument)}
            onClose={closeWatermarkedDownloadOptions}
            onLoadMorePages={() =>
              loadWatermarkedPreviewPages(pendingWatermarkedDocument, {
                startPage: watermarkedPreviewNextPageStart || watermarkedPreviewPages.length + 1,
                append: true,
              })
            }
            onConfirm={(redactions) =>
              handleDownloadWatermarkedDocument(redactions, pendingWatermarkedDocument)
            }
          />
        </div>
      </div>
    );
  }

  const fullName = [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(" ");
  const age = getAge(profile.birth_date || profile.dob);
  const initials = `${profile.first_name?.[0] || ""}${profile.last_name?.[0] || ""}`.toUpperCase();
  const avatarColor = pickColor(profile.first_name || "A");

  const locationDisplay =
    profile.location_display_cache ||
    formatProfileLocation(profile, lookupMaps) ||
    [profile.city, profile.state, profile.country].filter(Boolean).join(", ");

  const birthPlaceDisplay =
    formatProfileBirthPlace(profile, lookupMaps) ||
    profile.birth_place ||
    [profile.birth_city, profile.birth_state, profile.birth_country].filter(Boolean).join(", ");

  return (
    <div style={S.page}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes bellSwing {
          0%, 100% { transform: rotate(0deg); }
          10% { transform: rotate(16deg); }
          20% { transform: rotate(-13deg); }
          30% { transform: rotate(10deg); }
          40% { transform: rotate(-7deg); }
          50% { transform: rotate(4deg); }
          60% { transform: rotate(-2deg); }
        }
        @keyframes bellPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(215,60,46,0.18); }
          50% { box-shadow: 0 0 0 10px rgba(215,60,46,0); }
        }
        .sug-item:hover { background: #fff2ef !important; color: #D73C2E !important; }
        .profile-tab:focus, .profile-tab:active { outline: none !important; box-shadow: none !important; }
        .profile-tab::-moz-focus-inner { border: 0; }
        @media (max-width: 980px) {
          .profile-return-bar {
            align-items: flex-start !important;
            flex-direction: column !important;
            padding-right: 22px !important;
          }
          .profile-return-back {
            position: static !important;
          }
          .profile-hero { grid-template-columns: 1fr !important; }
          .profile-actions { align-items: flex-start !important; flex-direction: row !important; flex-wrap: wrap !important; }
          .profile-signal-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .profile-overview-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .profile-grid-2, .profile-card-grid, .recommendation-preview-grid { grid-template-columns: 1fr !important; }
          .note-stat-grid, .note-composer-grid, .recommendation-search-list { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 620px) {
          .profile-signal-grid { grid-template-columns: 1fr !important; }
          .profile-overview-grid { grid-template-columns: 1fr !important; }
          .profile-overview-item-wide { grid-column: span 1 !important; }
          .profile-tab-content { padding: 20px !important; }
          .note-stat-grid, .note-composer-grid, .recommendation-search-list, .recommendation-preview-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {showUpload && canAddPhotos && (
        <UploadOverlay
          profileNo={getProfileNumberDisplay(profile)}
          imageFiles={imageFiles}
          primaryIndex={primaryIndex}
          uploading={uploading}
          preparingUploadFiles={preparingUploadFiles}
          uploadStatus={uploadStatus}
          uploadPreparationStatus={uploadPreparationStatus}
          fileInputRef={fileInputRef}
          setPrimaryIndex={setPrimaryIndex}
          handleFileSelect={handleFileSelect}
          removeImage={removeImage}
          closeUpload={closeUpload}
          handleUpload={handleUpload}
        />
      )}

      {showCompletenessOverlay && canViewCompleteness && (
        <CompletenessOverlay
          pct={completenessPct}
          sections={completenessOverlaySections}
          onClose={() => setShowCompletenessOverlay(false)}
        />
      )}

      {showAlertsOverlay && canViewAlerts && (
        <AlertsOverlay
          alerts={openAlerts}
          onClose={() => setShowAlertsOverlay(false)}
        />
      )}

      {showAssignmentReturnBar && (
        <div style={S.topBar}>
          <div style={S.assignmentReturnBar} className="profile-return-bar">
            <div>
              <p style={S.assignmentReturnEyebrow}>Assignment Workspace</p>
              <div style={S.assignmentReturnTabs}>
                <button
                  type="button"
                  style={{ ...S.assignmentReturnTab, ...S.assignmentReturnTabActive }}
                  onClick={typeof onOpenAssignedToMe === "function" ? onOpenAssignedToMe : onBack}
                >
                  Assigned To Me
                </button>
                <button
                  type="button"
                  style={S.assignmentReturnTab}
                  onClick={typeof onOpenAssignedByMe === "function" ? onOpenAssignedByMe : undefined}
                >
                  Assigned By Me
                </button>
              </div>
            </div>

            <button type="button" style={S.backLink} className="profile-return-back" onClick={onBack}>
              {`< ${backLabel}`}
            </button>
          </div>
        </div>
      )}

      {showSearchReturnBar && !showAssignmentReturnBar && (
        <div style={S.topBar}>
          <button type="button" style={S.searchBackButton} onClick={onBack}>
            {`< ${backLabel}`}
          </button>
        </div>
      )}

      <div style={{ ...S.topBar, ...((showAssignmentReturnBar || showSearchReturnBar) ? S.topBarHidden : {}) }}>
        <button style={S.backLink} onClick={onBack}>{`< ${backLabel}`}</button>
      </div>

      <div style={S.wrap}>
        <div style={S.hero} className="profile-hero">
          <div style={S.photoWrap}>
            <ProfilePhoto
              src={canViewPrimaryPhoto ? primaryPhoto : null}
              initials={initials}
              avatarColor={avatarColor}
            />
          </div>

          <div style={S.heroInfo}>
            <div style={S.heroTopLine}>
              <span style={S.eyebrow}>Profile #{getProfileNumberDisplay(profile, "—")}</span>
              <span style={S.statusChip}>{profile.status || "active"}</span>
            </div>

            <h1 style={S.heroName}>{fullName || "Unnamed Profile"}</h1>

            <div style={S.pillRow}>
              {age && <Pill>{age} yrs</Pill>}
              {profile.gender && <Pill>{profile.gender}</Pill>}
              {locationDisplay && <Pill>📍 {locationDisplay}</Pill>}
              {profile.marital_status && <Pill>{profile.marital_status}</Pill>}
              {profile.is_verified && <Pill>Verified</Pill>}
              {profile.referral_source_name && <Pill>Referral: {profile.referral_source_name}</Pill>}
              {(profile.height_display || profile.height_cm) && (
                <Pill>{profile.height_display || `${profile.height_cm} cm`}</Pill>
              )}
              {profile.persona_tier_cache && <Pill>Tier: {profile.persona_tier_cache}</Pill>}
            </div>

            {profile.about_me && <p style={S.heroAbout}>{profile.about_me}</p>}

            {canViewTags && safeArray(profile.tags).length > 0 && (
              <div style={S.tagRow}>
                {profile.tags.map((tag) => (
                  <span key={tag.id || tag.name || tag} style={S.tag}>
                    {tag.name || tag}
                  </span>
                ))}
              </div>
            )}

            {String(profile.online_reg_id || "").trim() && (
              <div style={S.onlineRegRow}>
                <span style={S.onlineRegLabel}>Online Reg ID:</span>
                <span style={S.onlineRegValue}>{String(profile.online_reg_id).trim()}</span>
              </div>
            )}

            <div style={S.actionRow}>
              {canViewPhone && phoneVisible ? (
                <span style={S.phoneRevealed}>
                  <span>📞</span>
                  {primaryMobile || "No phone available"}
                  <button style={S.phoneHideBtn} onClick={() => setPhoneVisible(false)}>Hide</button>
                </span>
              ) : canViewPhone ? (
                <button style={S.phoneBtn} onClick={handleViewPhone} disabled={viewingPhone}>
                  {viewingPhone ? "Loading..." : "View Phone Number"}
                </button>
              ) : null}

              {canViewEmail && emailContact ? (
                emailVisible ? (
                  <span style={S.emailPill}>
                    ✉ {emailContact}
                    <button style={S.phoneHideBtn} onClick={() => setEmailVisible(false)}>Hide</button>
                  </span>
                ) : (
                  <button style={S.phoneBtn} onClick={handleViewEmail} disabled={viewingEmail}>
                    {viewingEmail ? "Loading..." : "View Email"}
                  </button>
                )
              ) : null}
            </div>

            {hasRestrictedContactsNotice && (
              <div style={S.policyNotice}>
                <strong style={S.policyNoticeTitle}>Contact visibility is restricted.</strong>
                <div style={S.policyNoticeBody}>
                  {hiddenContactFields.length > 0 && (
                    <span>Hidden by policy: {hiddenContactFields.join(", ")}.</span>
                  )}
                  {!canViewPhone && !hiddenContactFields.includes("phone") && (
                    <span>Your role cannot reveal phone numbers.</span>
                  )}
                  {!canViewEmail && !hiddenContactFields.includes("email") && (
                    <span>Your role cannot reveal email addresses.</span>
                  )}
                </div>
              </div>
            )}

          </div>

          <div style={S.heroActions} className="profile-actions">
            {canEdit && typeof onModeChange === "function" && (
              <button type="button" style={S.editBtn} onClick={() => onModeChange?.("edit")}>
                ✎ Edit Profile
              </button>
            )}

            {canViewCompleteness && (
              <button
                type="button"
                style={S.strengthTrigger}
                onClick={() => setShowCompletenessOverlay(true)}
              >
                <span style={S.strengthTriggerValue}>{completenessPct}% completed</span>
                <span style={S.strengthTriggerHint}>
                  {missingItems.length > 0 ? "See what to add" : "Profile looks complete"}
                </span>
                ＋ Add Photos
              </button>
            )}

            {canViewAlerts && openAlerts.length > 0 && (
              <button
                type="button"
                style={S.heroAlertBellButton}
                onClick={() => setShowAlertsOverlay(true)}
              >
                <span style={S.heroAlertBellIconWrap}>
                  <AlertBellIcon />
                </span>
                <span style={S.heroAlertBellText}>
                  <span style={S.heroAlertBellTitle}>Open Alerts</span>
                  <span style={S.heroAlertBellHint}>
                    {openAlerts.length} alert{openAlerts.length !== 1 ? "s" : ""} need attention
                  </span>
                </span>
              </button>
            )}
          </div>
        </div>


        <div style={S.detailBox}>
          <div style={S.tabBar}>
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                className="profile-tab"
                style={{ ...S.tab, ...(activeTab === tab.id ? S.tabActive : {}) }}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div style={S.tabContent} className="profile-tab-content" key={activeTab}>
            {visibleTabs.length === 0 && (
              <Empty>No profile detail tabs are available for your role.</Empty>
            )}
            {activeTab === "overview" && (
              <>
                <div style={S.overviewStack}>
                  <ProfileInfoSection
                    title="Basic Personal Details"
                    items={[
                      { label: "Gender", value: profile.gender ? labelize(profile.gender) : "" },
                      { label: "Date of Birth", value: formatDate(profile.birth_date || profile.dob) },
                      { label: "Birth Time", value: toTime(profile.birth_time) },
                      { label: "Height", value: profile.height_display || (profile.height_cm ? `${profile.height_cm} cm` : "") },
                      { label: "Birth Place", value: birthPlaceDisplay },
                      { label: "Current Location", value: locationDisplay },
                      { label: "Complexion", value: profile.complexion },
                      { label: "Blood Group", value: profile.blood_group },
                      { label: "Manglik", value: profile.manglik },
                      { label: "Marital Status", value: profile.marital_status_description || profile.marital_status },
                      { label: "Has Children", value: profile.has_children ? "Yes" : (showChildrenFields(profile.marital_status) ? "No" : "") },
                      { label: "Marital Field Details", value: profile.marital_field_details || profile.children_description, span: "wide" },
                    ]}
                  />

                  <ProfileInfoSection
                    title="Sakha Info"
                    items={[
                      { label: "Caste", value: profile.caste || profile.caste_name_cache },
                      { label: "Sakha", value: profile.sakha || profile.sakha_name_cache },
                      { label: "Sampraday", value: sampradayDisplay },
                      { label: "Sampraday Details", value: sampradayDetailsDisplay },
                      { label: "Dadisa Sakha", value: profile.dadisa_sakha_name },
                      { label: "Nanisa Sakha", value: profile.nanisa_sakha_name },
                      { label: "Mamasa Sakha", value: profile.mamasa_sakha_name },
                    ]}
                  />

                  <ProfileInfoSection
                    title="Education"
                    items={[
                      ...buildOverviewEducationItems(profile.educations, profile.education_summary),
                      { label: "Occupation", value: primaryOccupation?.occupation_details || primaryOccupation?.occupation_type || "" },
                      { label: "Company", value: primaryOccupation?.company_name || "" },
                      { label: "Work Profile", value: primaryOccupation?.work_profile || "" },
                      { label: "Income / Turnover", value: formatOccupationAmount(primaryOccupation) },
                    ]}
                  />

                  <ProfileInfoSection
                    title="Current Address"
                    items={[
                      { label: "Current Address", value: currentAddressDisplay, span: "wide" },
                    ]}
                  />

                  <ProfileInfoSection
                    title="Verification Status"
                    items={[
                      { label: "Referral Source", value: profile.referral_source_name },
                      { label: "Referral Name", value: profile.referral_name },
                      { label: "Verified At", value: formatDateTime(profile.verified_at) },
                      { label: "Verified By", value: profile.verified_by_name || profile.verified_by_email || (profile.verified_by ? `User #${profile.verified_by}` : "") },
                    ]}
                  />
                </div>
              </>
            )}

            {activeTab === "contacts" && (
              <div>
                <ContactOverviewSection
                  contacts={contacts}
                  profileId={profileId}
                  authHeaders={authHeaders}
                  canCopyPhone={canCopyPhone}
                  canCopyEmail={canCopyEmail}
                />
                {contacts.length === 0 ? <Empty>No contact details added.</Empty> : null}

                {false && <SectionHead>Identifiers</SectionHead>}
                {false ? (
                  <div style={S.cardGrid} className="profile-card-grid">
                    {profile.identifiers.map((identifier, index) => (
                      <SubCard key={identifier.id || index}>
                        <div style={S.subCardTitle}>{labelize(identifier.identifier_type)}</div>
                        <div style={S.subCardSub}>
                          {[identifier.identifier_value, identifier.source, identifier.is_active === false ? "Inactive" : "Active"]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </SubCard>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {activeTab === "career" && (
              <div>
                <div style={S.educationCareerGrid} className="profile-grid-2">
                  <EducationCareerColumnSection
                    title="Education"
                    items={buildEducationParameterItems(profile.educations, lookupMaps)}
                    emptyText="No education details added."
                  />
                  <EducationCareerColumnSection
                    title="Career"
                    items={buildCareerParameterItems(occupations)}
                    emptyText="No career details added."
                  />
                </div>

                {(String(profile.annual_income_raw || "").trim() || String(profile.family_status_raw || "").trim()) ? (
                  <>
                    <div style={{ height: 24 }} />
                    <SectionHead>Other Info</SectionHead>
                    <div style={S.grid2} className="profile-grid-2">
                      <InfoItem
                        label="Annual Income"
                        value={String(profile.annual_income_raw || "").trim()}
                      />
                      <InfoItem
                        label="Family Status"
                        value={String(profile.family_status_raw || "").trim()}
                      />
                    </div>
                  </>
                ) : null}
              </div>
            )}

            {activeTab === "family" && (
              <div>
                <SectionHead>Family</SectionHead>
                {familyMembers(profile.relatives).length > 0 ? (
                  <div style={S.familyOverviewGrid} className="profile-grid-2">
                    {familyMembers(profile.relatives).map((relative, index) => (
                      <RelativeOverviewCard key={relative.id || index} relative={relative} />
                    ))}
                  </div>
                ) : (
                  <Empty>No family details added.</Empty>
                )}
              </div>
            )}

            {activeTab === "relatives" && (
              <div>
                <SectionHead>Relatives</SectionHead>
                {sideRelatives(profile.relatives).length > 0 ? (
                  <div style={S.familyOverviewGrid} className="profile-grid-2">
                    {sideRelatives(profile.relatives).map((relative, index) => (
                      <RelativeOverviewCard key={relative.id || index} relative={relative} showFamilySide />
                    ))}
                  </div>
                ) : (
                  <Empty>No relatives added.</Empty>
                )}
              </div>
            )}

            {activeTab === "addresses" && (
              <div>
                <SectionHead>Addresses</SectionHead>
                {safeArray(profile.addresses).length > 0 ? (
                  <div style={S.familyOverviewGrid} className="profile-grid-2">
                    {profile.addresses.map((address, index) => (
                      <AddressOverviewCard
                        key={address.id || index}
                        address={address}
                        profile={profile}
                        lookupMaps={lookupMaps}
                      />
                    ))}
                  </div>
                ) : (
                  <Empty>No address records added.</Empty>
                )}
              </div>
            )}

            {activeTab === "preferences" && (
              <div>
                {profile.preferences ? (
                  <div style={S.familyOverviewGrid} className="profile-grid-2">
                    <PreferenceInfoItem
                      label="Preferred Age Difference"
                      value={
                        formatPreferredAgeDifference(profile.preferences) ||
                        (
                          profile.preferences.preferred_age_min && profile.preferences.preferred_age_max
                            ? `${profile.preferences.preferred_age_min} – ${profile.preferences.preferred_age_max} years`
                            : ""
                        )
                      }
                    />
                    <PreferenceInfoItem
                      label="Preferred Occupation Types"
                      value={safeArray(profile.preferences.preferred_occupation_types).join(", ")}
                    />
                    <PreferenceInfoItem
                      label="Non Preferred Occupation Types"
                      value={safeArray(profile.preferences.non_preferred_occupation_types).join(", ")}
                    />
                    <PreferenceInfoItem
                      label="Preferred Cities"
                      value={formatPreferenceCityIds(profile.preferences.preferred_city_ids, lookupMaps.locations)}
                    />
                    <PreferenceInfoItem
                      label="Non Preferred Cities"
                      value={formatPreferenceCityIds(profile.preferences.non_preferred_city_ids, lookupMaps.locations)}
                    />
                    <PreferenceInfoItem
                      label="Preferred Castes"
                      value={formatPreferenceIds(profile.preferences.preferred_caste_ids, lookupMaps.castes)}
                    />
                    <PreferenceInfoItem
                      label="Non Preferred Sakhas"
                      value={formatPreferenceIds(profile.preferences.non_preferred_sakha_ids, lookupMaps.sakhas)}
                    />
                    <PreferenceInfoItem
                      label="Preferred Education Categories"
                      value={formatPreferenceIds(profile.preferences.preferred_education_category_ids, lookupMaps.educationCategories)}
                    />
                    <PreferenceInfoItem
                      label="Non Preferred Education Categories"
                      value={formatPreferenceIds(profile.preferences.non_preferred_education_category_ids, lookupMaps.educationCategories)}
                    />
                    <PreferenceInfoItem label="Preferred Degrees" value={safeArray(profile.preferences.preferred_degrees).join(", ")} />
                    <PreferenceInfoItem label="Non Preferred Degrees" value={safeArray(profile.preferences.non_preferred_degrees).join(", ")} />
                    <PreferenceInfoItem label="Notes" value={profile.preferences.notes} />
                  </div>
                ) : (
                  <Empty>No partner preferences specified.</Empty>
                )}
              </div>
            )}

            {activeTab === "media" && (
              <div>
                {canViewPhotos && (
                  <>
                    <SectionHead>Photos</SectionHead>
                    {photos.length > 0 ? (
                      <div style={S.galleryGridInTab}>
                        {photos.map((file, index) => (
                          <GalleryItem
                            key={file.id || index}
                            file={file}
                            onOpen={setSelectedGalleryPhoto}
                            onSetPrimary={handleSetPrimary}
                            isSettingPrimary={settingPrimaryId === file.id}
                            canSetPrimary={canSetPrimaryPhoto}
                          />
                        ))}
                      </div>
                    ) : (
                      <Empty>No photos uploaded.</Empty>
                    )}
                  </>
                )}

                {canViewPhotos && canViewDocuments && <div style={{ height: 24 }} />}

                {canViewDocuments && (
                  <>
                    <SectionHead>Media Links</SectionHead>
                    {mediaLinks.length > 0 ? (
                      <div style={S.cardGrid} className="profile-card-grid">
                        {mediaLinks.map((link, index) => (
                          <SubCard key={link.id || `${link.link_type}-${index}`}>
                            <div style={S.subCardTitle}>{getProfileMediaLinkTypeLabel(link.link_type)}</div>
                            <div style={{ ...S.subCardSub, wordBreak: "break-word" }}>{link.link_url}</div>
                            <div style={S.documentActionRow}>
                              <a
                                href={link.link_url}
                                target="_blank"
                                rel="noreferrer"
                                style={S.inlineLinkButton}
                              >
                                Open Link
                              </a>
                            </div>
                          </SubCard>
                        ))}
                      </div>
                    ) : (
                      <Empty>No media links added.</Empty>
                    )}

                    <div style={{ height: 24 }} />

                    <SectionHead>Documents & Generated Media</SectionHead>
                {documents.length > 0 ? (
                  <div style={S.cardGrid} className="profile-card-grid">
                    {documents.map((doc, index) => (
                      <SubCard key={doc.id || index}>
                        <div style={S.documentCardHeader}>
                          <div style={S.documentCardTitleWrap}>
                            <div style={S.subCardTitle}>
                              {doc.document_type_name || doc.template_key || labelize(doc.media_type)}
                              {doc.is_primary && <span style={S.highestBadge}>Primary</span>}
                            </div>
                          </div>
                          {canViewDocumentHistory && (
                            <button
                              type="button"
                              style={S.documentHistoryButton}
                              onClick={() => openDocumentHistory(doc)}
                            >
                              History
                            </button>
                          )}
                        </div>
                        <div style={S.subCardSub}>
                          {[
                            doc.original_file_name,
                            doc.document_type_name,
                            doc.version_no ? `Version ${doc.version_no}` : "",
                            doc.mime_type,
                            formatFileSize(doc.file_size_bytes),
                            doc.generated_at ? `Generated ${formatDateTime(doc.generated_at)}` : "",
                            doc.is_watermarked ? "Watermarked" : "",
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                        {(doc.id || canDownloadWatermarkedBiodataDocuments) && (
                          <div style={S.documentActionRow}>
                            <button
                              type="button"
                              style={{
                                ...S.inlineLinkButton,
                                ...(documentActionBusyKey === `inline:${doc.id}` ? S.inlineLinkButtonDisabled : {}),
                              }}
                              onClick={() => handleDocumentFileAction(doc, "inline")}
                              disabled={documentActionBusyKey === `inline:${doc.id}`}
                            >
                              {documentActionBusyKey === `inline:${doc.id}` ? "Opening..." : "Open file"}
                            </button>
                            <button
                              type="button"
                              style={{
                                ...S.inlineLinkButton,
                                ...(documentActionBusyKey === `attachment:${doc.id}` ? S.inlineLinkButtonDisabled : {}),
                              }}
                              onClick={() => handleDocumentFileAction(doc, "attachment")}
                              disabled={documentActionBusyKey === `attachment:${doc.id}`}
                            >
                              {documentActionBusyKey === `attachment:${doc.id}`
                                ? "Preparing..."
                                : isPdfMedia(doc)
                                  ? "Download PDF"
                                  : "Download file"}
                            </button>
                            {canDownloadWatermarkedBiodataDocuments && isBiodataDocumentMedia(doc) && (
                              <button
                                type="button"
                                style={{
                                  ...S.inlineLinkButton,
                                  ...(watermarkedDocumentBusyId === doc.id ? S.inlineLinkButtonDisabled : {}),
                                }}
                                onClick={() => openWatermarkedDownloadOptions(doc)}
                                disabled={watermarkedDocumentBusyId === doc.id}
                              >
                                {watermarkedDocumentBusyId === doc.id ? "Preparing..." : getWatermarkedDownloadLabel(doc)}
                              </button>
                            )}
                          </div>
                        )}
                      </SubCard>
                    ))}
                  </div>
                ) : (
                  <Empty>No documents or generated media added.</Empty>
                )}
                  </>
                )}
              </div>
            )}

            {activeTab === "recommendations" && (
              <div>
                {canViewRecommendationsTab && (
                  <ProfileRecommendationsSection
                    profile={profile}
                    authHeaders={authHeaders}
                    user={user}
                    onViewProfile={onViewProfile}
                    recommendations={recommendations}
                    recommendationsLoading={recommendationsLoading}
                    recommendationMeta={recommendationMeta}
                    recommendationSearch={recommendationSearch}
                    recommendationSearchMode={recommendationSearchMode}
                    setRecommendationSearch={setRecommendationSearch}
                    setRecommendationSearchMode={setRecommendationSearchMode}
                    recommendationResults={recommendationResults}
                    recommendationSearchLoading={recommendationSearchLoading}
                    recommendationSearchError={recommendationSearchError}
                    recommendationSearchFeedback={recommendationSearchFeedback}
                    selectedRecommendationTargets={selectedRecommendationTargets}
                    recommendationNotesByTarget={recommendationNotesByTarget}
                    savingRecommendation={savingRecommendation}
                    canGiveRecommendations={canGiveRecommendations}
                    canViewRecommendations={canViewRecommendations}
                    canEditRecommendations={canEditRecommendations}
                    canViewTasks={canViewTasks}
                    canCreateTasks={canCreateTasks}
                    canDownloadRecommendationBiodataPermission={canDownloadRecommendationBiodataPermission}
                    recommendationOutcomeDrafts={recommendationOutcomeDrafts}
                    recommendationOutcomeBusyId={recommendationOutcomeBusyId}
                    recommendationDownloadBusyId={recommendationDownloadBusyId}
                    onSelectTarget={handleSelectRecommendationTarget}
                    onRemoveTarget={handleRemoveRecommendationTarget}
                    onTargetNoteChange={handleRecommendationTargetNoteChange}
                    onCreateRecommendation={handleCreateRecommendation}
                    onOutcomeDraftChange={handleRecommendationOutcomeDraftChange}
                    onSaveOutcome={handleSaveRecommendationOutcome}
                    onDownloadBiodata={handleDownloadRecommendationBiodata}
                  />
                )}
              </div>
            )}

            {activeTab === "crm" && (
              <div>
                {canViewAlerts && (
                  <>
                    <SectionHead>Profile Alerts</SectionHead>
                    {alerts.length > 0 ? (
                      <div style={S.familyOverviewGrid} className="profile-grid-2">
                        {alerts.map((alertItem, index) => (
                          <AlertCard key={alertItem.id || index} alert={alertItem} />
                        ))}
                      </div>
                    ) : (
                      <Empty>No alerts for this profile.</Empty>
                    )}

                    <div style={{ height: 24 }} />
                  </>
                )}

                <SectionHead>Persona</SectionHead>
                {safeArray(profile.persona).length > 0 ? (
                  <div style={S.familyOverviewGrid} className="profile-grid-2">
                    {profile.persona.map((persona, index) => (
                      <OverviewDetailCard
                        key={persona.id || index}
                        title={`${labelize(persona.tier) || "Basic"} Package`}
                        badges={[labelize(persona.status)].filter(Boolean)}
                        fields={[
                          {
                            label: "Completion",
                            value: persona.completeness_pct != null ? `${persona.completeness_pct}% complete` : "",
                          },
                          {
                            label: "Amount",
                            value: persona.payment_amount != null ? `₹${persona.payment_amount}` : "",
                          },
                          {
                            label: "Valid Until",
                            value: persona.valid_until ? formatDate(persona.valid_until) : "",
                          },
                        ]}
                      />
                    ))}
                  </div>
                ) : (
                  <Empty>No persona package assigned.</Empty>
                )}

                <div style={{ height: 24 }} />

                <SectionHead>Payment History</SectionHead>
                {safeArray(profile.payments).length > 0 ? (
                  <div style={S.familyOverviewGrid} className="profile-grid-2">
                    {profile.payments.map((payment, index) => (
                      <OverviewDetailCard
                        key={payment.id || index}
                        title={payment.amount != null ? `₹${payment.amount}` : "Payment"}
                        badges={[labelize(payment.payment_status)].filter(Boolean)}
                        fields={[
                          { label: "Method", value: payment.payment_method },
                          { label: "Reference", value: payment.transaction_ref },
                          { label: "Paid At", value: payment.paid_at ? formatDate(payment.paid_at) : "" },
                        ]}
                      />
                    ))}
                  </div>
                ) : (
                  <Empty>No payments added.</Empty>
                )}
              </div>
            )}
          </div>
        </div>

        {(canViewNotes || canAddNotes) && (
          <StructuredNotesSection
            notes={notes}
            recommendationNoteContextMap={recommendationNoteContextMap}
            newRemark={newRemark}
            setNewRemark={setNewRemark}
            addingRemark={addingRemark}
            notesLoading={notesLoading}
            canViewNotes={canViewNotes}
            canAddNotes={canAddNotes}
            noteMeta={noteMeta}
            noteDraft={noteDraft}
            noteFilters={noteFilters}
            followUpSourceNote={followUpSourceNote}
            resolveFollowUpSource={resolveFollowUpSource}
            rescheduleDraft={rescheduleDraft}
            noteActionBusyId={noteActionBusyId}
            dueFollowUpCount={dueFollowUpCount}
            scheduledFollowUpCount={scheduledFollowUpCount}
            communicationCount={communicationCount}
            feedbackCount={feedbackCount}
            handleRemarkChange={handleRemarkChange}
            handleSuggestionClick={handleSuggestionClick}
            handleAddRemark={handleAddRemark}
            handleStartFollowUp={handleStartFollowUp}
            handleStartReschedule={handleStartReschedule}
            handleSaveReschedule={handleSaveReschedule}
            handleCompleteFollowUp={handleCompleteFollowUp}
            handleCancelFollowUp={handleCancelFollowUp}
            handleDraftFieldChange={handleDraftFieldChange}
            handleFilterChange={handleFilterChange}
            setResolveFollowUpSource={setResolveFollowUpSource}
            setRescheduleDraft={setRescheduleDraft}
            resetDraft={resetDraft}
            showSuggestions={showSuggestions}
            setShowSuggestions={setShowSuggestions}
            filteredSuggestions={filteredSuggestions}
            hoveredSuggestion={hoveredSuggestion}
            setHoveredSuggestion={setHoveredSuggestion}
            remarkInputRef={remarkInputRef}
          />
        )}

        {documentHistoryOpen && typeof document !== "undefined" && createPortal(
          <div
            style={S.biodataModalBackdrop}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                closeDocumentHistory();
              }
            }}
          >
            <div style={S.documentHistoryModalCard}>
              <div style={S.biodataModalHeader}>
                <div>
                  <p style={S.overlayEyebrow}>Document History</p>
                  <h2 style={S.overlayTitle}>
                    {getDocumentDisplayName(documentHistoryMeta || documentHistoryDocument)}
                  </h2>
                  <p style={S.watermarkModalText}>
                    Download and open activity for this file, including who accessed it and when.
                  </p>
                </div>
                <button type="button" style={S.overlayClose} onClick={closeDocumentHistory}>
                  X
                </button>
              </div>

              <div style={S.documentHistoryBody}>
                {documentHistoryLoading ? (
                  <div style={S.watermarkEmptyState}>Loading history...</div>
                ) : documentHistoryError ? (
                  <div style={S.biodataOptionError}>{documentHistoryError}</div>
                ) : documentHistoryEntries.length > 0 ? (
                  <div style={S.documentHistoryList}>
                    {documentHistoryEntries.map((entry) => (
                      <div key={entry.id || `${entry.action}-${entry.created_at}`} style={S.documentHistoryItem}>
                        <div style={S.documentHistoryItemTop}>
                          <strong style={S.documentHistoryActor}>
                            {entry.user_name || entry.user_email || "System"}
                          </strong>
                          <span style={S.documentHistoryActionBadge}>
                            {formatDocumentHistoryAction(entry.action, entry)}
                          </span>
                        </div>
                        <div style={S.documentHistoryItemMeta}>
                          {formatDateTime(entry.created_at)}
                        </div>
                        <div style={S.documentHistoryItemMeta}>
                          {getDocumentHistoryEntryName(entry, documentHistoryMeta || documentHistoryDocument)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={S.watermarkEmptyState}>
                    No document activity has been recorded yet.
                  </div>
                )}
              </div>

              <div style={S.watermarkModalFooter}>
                <div style={S.watermarkActionRow}>
                  <button type="button" style={S.overlayCancel} onClick={closeDocumentHistory}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {biodataOptionModalOpen && typeof document !== "undefined" && createPortal(
          <div
            style={S.biodataModalBackdrop}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                closeBiodataOptionModal();
              }
            }}
          >
            <div style={S.biodataModalCard}>
              <div style={S.biodataModalHeader}>
                <div>
                  <p style={S.overlayEyebrow}>Biodata PDF</p>
                  <h2 style={S.overlayTitle}>Choose What To Include</h2>
                  <p style={S.watermarkModalText}>
                    Choose the sections and images to include. We&apos;ll generate the PDF using only the checked items.
                  </p>
                </div>
                <button type="button" style={S.overlayClose} onClick={closeBiodataOptionModal}>
                  x
                </button>
              </div>

              <div style={S.biodataOptionList}>
                {biodataOptionModalLoading ? (
                  <div style={S.watermarkEmptyState}>Preparing biodata options...</div>
                ) : biodataOptionList.length > 0 ? (
                  biodataOptionList.map((option) => (
                    <label key={option.id} style={S.watermarkOptionRow}>
                      <input
                        type="checkbox"
                        checked={biodataOptionSelections[option.id] === true}
                        onChange={(event) =>
                          handleBiodataOptionSelectionChange(option.id, event.target.checked)
                        }
                      />
                      <span>{option.label}</span>
                    </label>
                  ))
                ) : (
                  <div style={S.watermarkEmptyState}>
                    No biodata sections are available on this profile.
                  </div>
                )}
                {biodataOptionModalError && (
                  <div style={S.biodataOptionError}>{biodataOptionModalError}</div>
                )}
              </div>

              <div style={S.watermarkModalFooter}>
                <div style={S.watermarkFileHint}>
                  {pendingBiodataProfile ? buildProfileBiodataFileName(pendingBiodataProfile) : "JKM_PROFILE_BIO_DATA.pdf"}
                </div>
                <div style={S.watermarkActionRow}>
                  <button type="button" style={S.overlayCancel} onClick={closeBiodataOptionModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={{
                      ...S.overlaySubmit,
                      ...(recommendationDownloadBusyId === pendingBiodataRecommendation?.id || biodataOptionModalLoading
                        ? S.overlaySubmitDisabled
                        : {}),
                    }}
                    onClick={handleConfirmRecommendationBiodataDownload}
                    disabled={
                      recommendationDownloadBusyId === pendingBiodataRecommendation?.id ||
                      biodataOptionModalLoading ||
                      biodataOptionList.length === 0
                    }
                  >
                    {recommendationDownloadBusyId === pendingBiodataRecommendation?.id
                      ? "Preparing..."
                      : "Download PDF"}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {false && (
          <div
            style={S.watermarkModalBackdrop}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                closeWatermarkedDownloadOptions();
              }
            }}
          >
            <div style={S.watermarkModalCard}>
              <div style={S.watermarkModalHeader}>
                <div>
                  <p style={S.overlayEyebrow}>Watermarked Biodata</p>
                  <h2 style={S.overlayTitle}>Choose What To Remove</h2>
                  <p style={S.watermarkModalText}>
                    Select the sections to hide before we add the watermark and download the PDF.
                  </p>
                </div>
                <button type="button" style={S.overlayClose} onClick={closeWatermarkedDownloadOptions}>
                  ×
                </button>
              </div>

              <div style={S.watermarkOptionList}>
                {availableWatermarkRemovalOptions.length > 0 ? (
                  availableWatermarkRemovalOptions.map((option) => (
                    <label key={option.id} style={S.watermarkOptionRow}>
                      <input
                        type="checkbox"
                        checked={watermarkedDownloadSelections[option.id] === true}
                        onChange={(event) => handleWatermarkedSelectionChange(option.id, event.target.checked)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))
                ) : (
                  <div style={S.watermarkEmptyState}>
                    No removable biodata sections were found for this profile. The PDF will be downloaded with watermark only.
                  </div>
                )}
              </div>

              <div style={S.watermarkModalFooter}>
                <div style={S.watermarkFileHint}>
                  {buildWatermarkedBiodataFileName(profile, pendingWatermarkedDocument)}
                </div>
                <div style={S.watermarkActionRow}>
                  <button type="button" style={S.overlayCancel} onClick={closeWatermarkedDownloadOptions}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={{
                      ...S.overlaySubmit,
                      ...(watermarkedDocumentBusyId === pendingWatermarkedDocument?.id ? S.overlaySubmitDisabled : {}),
                    }}
                    onClick={() => handleDownloadWatermarkedDocument(pendingWatermarkedDocument)}
                    disabled={watermarkedDocumentBusyId === pendingWatermarkedDocument?.id}
                  >
                    {watermarkedDocumentBusyId === pendingWatermarkedDocument?.id
                      ? "Preparing..."
                      : (isImageMedia(pendingWatermarkedDocument) ? "Download Image" : "Download PDF")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {false && canAddPhotos && (
          <>
            {canViewPhotos && photos.length > 0 ? (
              <div style={S.gallery}>
                <div style={S.galleryHeader}>
                  <p style={S.galleryTitle}>Photos ({photos.length})</p>
                  <button style={S.galleryAddBtn} onClick={() => setShowUpload(true)}>＋ Add More</button>
                </div>
                <div style={S.galleryHelp}>
                  {canSetPrimaryPhoto ? "Click any photo to view it. You can set primary from the preview." : "Photo uploads are allowed for your role."}
                </div>
                <div style={S.galleryGrid}>
                  {photos.map((file, index) => (
                    <GalleryItem
                      key={file.id || index}
                      file={file}
                      onOpen={setSelectedGalleryPhoto}
                      onSetPrimary={handleSetPrimary}
                      isSettingPrimary={settingPrimaryId === file.id}
                      canSetPrimary={canSetPrimaryPhoto}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div style={S.noPhotoNudge}>
                <span style={S.noPhotoIcon}>◈</span>
                <p style={S.noPhotoText}>
                  {canViewPhotos ? "No photos uploaded yet" : "You can add photos, but photo viewing is restricted for your role."}
                </p>
                <button style={S.noPhotoBtn} onClick={() => setShowUpload(true)}>Upload Photos</button>
              </div>
            )}
          </>
        )}

        {selectedGalleryPhoto && (
          <PhotoPreviewModal
            file={selectedGalleryPhoto}
            canSetPrimary={canSetPrimaryPhoto}
            isSettingPrimary={settingPrimaryId === selectedGalleryPhoto.id}
            onClose={() => setSelectedGalleryPhoto(null)}
            onSetPrimary={async (fileId) => {
              await handleSetPrimary(fileId);
              setSelectedGalleryPhoto((current) => (
                current && current.id === fileId
                  ? { ...current, is_primary: true }
                  : current
              ));
            }}
          />
        )}

        {selectedDocumentImage && (
          <PhotoPreviewModal
            file={{ ...selectedDocumentImage, file_url: selectedDocumentImage.preview_url || selectedDocumentImage.file_url }}
            canSetPrimary={false}
            isSettingPrimary={false}
            eyebrow="Document Preview"
            title={getDocumentDisplayName(selectedDocumentImage)}
            onClose={() => {
              if (selectedDocumentImage.preview_url) {
                window.URL.revokeObjectURL(selectedDocumentImage.preview_url);
              }
              setSelectedDocumentImage(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

function UploadOverlay({
  profileNo,
  imageFiles,
  primaryIndex,
  uploading,
  preparingUploadFiles,
  uploadStatus,
  uploadPreparationStatus,
  fileInputRef,
  setPrimaryIndex,
  handleFileSelect,
  removeImage,
  closeUpload,
  handleUpload,
}) {
  if (typeof document === "undefined") return null;

  return createPortal(
    <div style={S.overlayBackdrop} onClick={(e) => { if (e.target === e.currentTarget) closeUpload(); }}>
      <div style={S.overlayCard}>
        <div style={S.overlayHeader}>
          <div>
            <p style={S.overlayEyebrow}>{profileNo ? `Profile #${profileNo}` : "Photo Upload"}</p>
            <h2 style={S.overlayTitle}>Add Photos</h2>
          </div>
          <button style={S.overlayClose} onClick={closeUpload} disabled={uploading || preparingUploadFiles}>x</button>
        </div>

        <div
          style={{
            ...S.dropZone,
            ...(preparingUploadFiles ? S.processingDropZone : {}),
          }}
          onClick={() => {
            if (!preparingUploadFiles) fileInputRef.current?.click();
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            style={{ display: "none" }}
            onChange={handleFileSelect}
            disabled={preparingUploadFiles}
          />
          <span style={S.dropIcon}>＋</span>
          <span style={S.dropText}>{preparingUploadFiles ? "Preparing photos..." : "Click to select photos"}</span>
          <span style={S.dropSub}>JPEG · PNG · WEBP · Max {MAX_UPLOAD_FILE_SIZE_LABEL} after compression · {imageFiles.length}/10 selected</span>
          {preparingUploadFiles ? (
            <span style={S.processingInlineNote}>
              <span style={S.processingSpinner} />
              {uploadPreparationStatus || "Compressing selected photos..."}
            </span>
          ) : null}
        </div>

        {imageFiles.length > 0 && (
          <>
            <p style={S.overlayHint}>Click a photo to set it as primary.</p>
            <div style={S.previewGrid}>
              {imageFiles.map((img, index) => (
                <div
                  key={index}
                  style={{
                    ...S.previewCard,
                    ...(index === primaryIndex ? S.previewCardPrimary : {}),
                  }}
                  onClick={() => setPrimaryIndex(index)}
                >
                  <img src={img.previewUrl} alt={img.name} style={S.previewImg} />
                  {index === primaryIndex && <div style={S.primaryBadge}>★ Primary</div>}
                  <button
                    type="button"
                    style={S.previewRemove}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeImage(index);
                    }}
                  >
                    x
                  </button>
                  <div style={S.previewName}>{img.name}</div>
                </div>
              ))}
            </div>
          </>
        )}

        <div style={S.overlayFooter}>
          {preparingUploadFiles ? (
            <span style={S.uploadStatus}>
              <span style={S.processingSpinner} />
              {uploadPreparationStatus || "Preparing files..."}
            </span>
          ) : uploadStatus ? (
            <span style={S.uploadStatus}>{uploadStatus}</span>
          ) : (
            <span style={S.uploadHint}>
              {imageFiles.length === 0
                ? "No photos selected yet"
                : `${imageFiles.length} photo${imageFiles.length > 1 ? "s" : ""} ready`}
            </span>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button style={S.overlayCancel} onClick={closeUpload} disabled={uploading || preparingUploadFiles}>Cancel</button>
            <button
              style={{
                ...S.overlaySubmit,
                ...(imageFiles.length === 0 || uploading || preparingUploadFiles ? S.overlaySubmitDisabled : {}),
              }}
              onClick={handleUpload}
              disabled={imageFiles.length === 0 || uploading || preparingUploadFiles}
            >
              {uploading ? "Uploading..." : imageFiles.length === 1 ? "Upload 1 Photo" : `Upload ${imageFiles.length || ""} Photos`}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// eslint-disable-next-line no-unused-vars
function NotesSection({
  notes,
  newRemark,
  addingRemark,
  canViewNotes,
  canAddNotes,
  handleRemarkChange,
  handleSuggestionClick,
  handleAddRemark,
  showSuggestions,
  setShowSuggestions,
  filteredSuggestions,
  hoveredSuggestion,
  setHoveredSuggestion,
  remarkInputRef,
}) {
  const visibleNotes = canViewNotes ? notes : [];

  return (
    <div style={S.remarkBox}>
      <div style={S.remarkHeader}>
        <div>
          <p style={S.remarkEyebrow}>Internal</p>
          <h3 style={S.remarkTitle}>Admin Notes</h3>
        </div>
        <span style={S.remarkCount}>{visibleNotes.length} note{visibleNotes.length !== 1 ? "s" : ""}</span>
      </div>

      {canAddNotes && <div style={S.remarkInputWrap}>
        <div style={{ position: "relative" }}>
          <textarea
            ref={remarkInputRef}
            placeholder="Write a note or pick from suggestions..."
            value={newRemark}
            onChange={handleRemarkChange}
            onFocus={() => {
              setShowSuggestions(true);
            }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            style={S.remarkInput}
          />

          {showSuggestions && (
            <div style={S.suggestionBox}>
              <div style={S.suggestionHeader}>Quick suggestions</div>
              {filteredSuggestions.map((suggestion, index) => (
                <div
                  key={suggestion}
                  className="sug-item"
                  style={{
                    ...S.suggestionItem,
                    ...(hoveredSuggestion === index ? S.suggestionItemHovered : {}),
                  }}
                  onMouseEnter={() => setHoveredSuggestion(index)}
                  onMouseLeave={() => setHoveredSuggestion(null)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <span style={S.suggestionDot}>›</span> {suggestion}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={S.remarkActions}>
          <span style={S.remarkCharCount}>{newRemark.length > 0 ? `${newRemark.length} chars` : ""}</span>
          <button
            onClick={handleAddRemark}
            style={{ ...S.remarkBtn, ...(addingRemark || !newRemark.trim() ? S.remarkBtnDisabled : {}) }}
            disabled={addingRemark || !newRemark.trim()}
          >
            {addingRemark ? "Saving..." : "Save Note"}
          </button>
        </div>
      </div>}

      <div style={S.remarkList}>
        {!canViewNotes ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>Note</span>
            <p style={S.remarkEmptyText}>Saved notes are hidden for your role.</p>
          </div>
        ) : visibleNotes.length === 0 ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>📝</span>
            <p style={S.remarkEmptyText}>No notes yet. Add the first one above.</p>
          </div>
        ) : (
          visibleNotes.map((note, index) => (
            <div key={note.id || index} style={{ ...S.remarkItem, ...(index === 0 ? S.remarkItemFirst : {}) }}>
              <div style={S.remarkItemLeft}>
                <div style={S.remarkAvatar}>
                  {formatActorName(note.created_by_name, note.created_by_email, "Admin")[0].toUpperCase()}
                </div>
              </div>
              <div style={S.remarkItemRight}>
                <div style={S.remarkItemTop}>
                  <span style={S.remarkAuthor}>
                    {formatActorName(note.created_by_name, note.created_by_email)}
                  </span>
                  <span style={S.remarkTime}>{formatDateTime(note.created_at)}</span>
                </div>
                <p style={S.remarkText}>{note.content || note.note}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function StructuredNotesSection({
  notes,
  recommendationNoteContextMap,
  newRemark,
  setNewRemark,
  addingRemark,
  notesLoading,
  canViewNotes,
  canAddNotes,
  noteMeta,
  noteDraft,
  noteFilters,
  followUpSourceNote,
  resolveFollowUpSource,
  rescheduleDraft,
  noteActionBusyId,
  dueFollowUpCount,
  scheduledFollowUpCount,
  communicationCount,
  feedbackCount,
  handleRemarkChange,
  handleSuggestionClick,
  handleAddRemark,
  handleStartFollowUp,
  handleStartReschedule,
  handleSaveReschedule,
  handleCompleteFollowUp,
  handleCancelFollowUp,
  handleDraftFieldChange,
  handleFilterChange,
  setResolveFollowUpSource,
  setRescheduleDraft,
  resetDraft,
  showSuggestions,
  setShowSuggestions,
  filteredSuggestions,
  hoveredSuggestion,
  setHoveredSuggestion,
  remarkInputRef,
}) {
  const visibleNotes = canViewNotes ? notes : [];
  const noteRecommendationMap =
    recommendationNoteContextMap instanceof Map ? recommendationNoteContextMap : new Map();
  const [currentNotesPage, setCurrentNotesPage] = useState(1);
  const totalNotePages = Math.max(1, Math.ceil(visibleNotes.length / NOTES_PAGE_SIZE));
  const paginatedNotes = visibleNotes.slice(
    (currentNotesPage - 1) * NOTES_PAGE_SIZE,
    currentNotesPage * NOTES_PAGE_SIZE
  );
  const pageButtonStart = Math.max(1, currentNotesPage - 2);
  const pageButtonEnd = Math.min(totalNotePages, pageButtonStart + 4);
  const notePageButtons = [];

  for (let page = Math.max(1, pageButtonEnd - 4); page <= pageButtonEnd; page += 1) {
    notePageButtons.push(page);
  }

  useEffect(() => {
    setCurrentNotesPage(1);
  }, [
    visibleNotes.length,
    noteFilters.note_type,
    noteFilters.outcome,
    noteFilters.follow_up,
    noteFilters.follow_up_status,
  ]);

  useEffect(() => {
    if (currentNotesPage > totalNotePages) {
      setCurrentNotesPage(totalNotePages);
    }
  }, [currentNotesPage, totalNotePages]);

  return (
    <div style={S.remarkBox}>
      <div style={S.remarkHeader}>
        <div>
          <p style={S.remarkEyebrow}>Internal</p>
          <h3 style={S.remarkTitle}>Admin Notes</h3>
          <p style={S.remarkSubhead}>Structured CRM logging for calls, meetings, match sharing, and feedback.</p>
        </div>
        <span style={S.remarkCount}>{visibleNotes.length} note{visibleNotes.length !== 1 ? "s" : ""}</span>
      </div>

      <div style={S.noteStatGrid} className="note-stat-grid">
        <MetricCard label="Logged" value={visibleNotes.length} hint="Total timeline rows" />
        <MetricCard label="Communication" value={communicationCount} hint="Calls, meetings, shares" />
        <MetricCard label="Due Follow-ups" value={dueFollowUpCount} hint="Needs attention now" accent="alert" />
        <MetricCard label="Feedback" value={feedbackCount} hint={`${scheduledFollowUpCount} scheduled follow-ups`} />
      </div>

      {canAddNotes && (
        <div style={S.remarkInputWrap}>
          {followUpSourceNote && (
            <div style={S.followUpContextCard}>
              <div style={{ minWidth: 0 }}>
                <div style={S.followUpContextTitle}>Following up on an existing note</div>
                <div style={S.followUpContextMeta}>
                  {[formatActorName(followUpSourceNote.created_by_name, followUpSourceNote.created_by_email), formatDateTime(followUpSourceNote.created_at)]
                    .filter(Boolean)
                    .join(" | ")}
                </div>
                <div style={S.followUpContextBody}>{followUpSourceNote.content || followUpSourceNote.note}</div>
              </div>
              <div style={S.followUpContextActions}>
                <label style={S.followUpResolveLabel}>
                  <input
                    type="checkbox"
                    checked={resolveFollowUpSource}
                    onChange={(event) => setResolveFollowUpSource(event.target.checked)}
                  />
                  Mark previous follow-up complete
                </label>
                <button
                  type="button"
                  style={S.noteGhostBtn}
                  onClick={() => {
                    setNewRemark("");
                    resetDraft();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div style={S.noteComposerGrid} className="note-composer-grid">
            <div style={S.noteField}>
              <label style={S.noteFieldLabel}>Interaction Type</label>
              <select style={S.noteSelect} value={noteDraft.note_type} onChange={handleDraftFieldChange("note_type")}>
                {noteMeta.note_types.map((item) => (
                  <option key={item} value={item}>{labelize(item)}</option>
                ))}
              </select>
            </div>
            <div style={S.noteField}>
              <label style={S.noteFieldLabel}>Result</label>
              <select style={S.noteSelect} value={noteDraft.outcome} onChange={handleDraftFieldChange("outcome")}>
                <option value="">No outcome yet</option>
                {noteMeta.outcomes.filter((item) => item !== "follow_up_required").map((item) => (
                  <option key={item} value={item}>{labelize(item)}</option>
                ))}
              </select>
            </div>
            <div style={S.noteField}>
              <label style={S.noteFieldLabel}>Next Follow-up</label>
              <input
                type="datetime-local"
                style={S.noteInput}
                value={noteDraft.follow_up_at}
                onChange={handleDraftFieldChange("follow_up_at")}
              />
            </div>
          </div>

          <div style={{ position: "relative" }}>
            <textarea
              ref={remarkInputRef}
              placeholder="Write a structured CRM note or pick a suggestion..."
              value={newRemark}
              onChange={handleRemarkChange}
              onFocus={() => {
                setShowSuggestions(filteredSuggestions.length > 0);
              }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              style={S.remarkInput}
            />

            {showSuggestions && filteredSuggestions.length > 0 && (
              <div style={S.suggestionBox}>
                <div style={S.suggestionHeader}>Suggested note starters</div>
                {filteredSuggestions.slice(0, 10).map((suggestion, index) => (
                  <div
                    key={suggestion}
                    className="sug-item"
                    style={{
                      ...S.suggestionItem,
                      ...(hoveredSuggestion === index ? S.suggestionItemHovered : {}),
                    }}
                    onMouseEnter={() => setHoveredSuggestion(index)}
                    onMouseLeave={() => setHoveredSuggestion(null)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <span style={S.suggestionDot}></span> {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={S.remarkActions}>
            <span style={S.remarkCharCount}>{newRemark.length > 0 ? `${newRemark.length} chars` : ""}</span>
            <div style={S.noteActionRow}>
              <button
                type="button"
                style={S.noteGhostBtn}
                onClick={() => {
                  setNewRemark("");
                  resetDraft();
                }}
              >
                Reset
              </button>
              <button
                onClick={handleAddRemark}
                style={{ ...S.remarkBtn, ...(addingRemark || !newRemark.trim() ? S.remarkBtnDisabled : {}) }}
                disabled={addingRemark || !newRemark.trim()}
              >
                {addingRemark ? "Saving..." : "Save Note"}
              </button>
            </div>
          </div>
        </div>
      )}

      {canViewNotes && (
        <div style={S.noteFiltersBar}>
          <select style={S.noteFilterSelect} value={noteFilters.note_type} onChange={handleFilterChange("note_type")}>
            <option value="all">All types</option>
            {noteMeta.note_types.map((item) => (
              <option key={item} value={item}>{labelize(item)}</option>
            ))}
          </select>
          <select style={S.noteFilterSelect} value={noteFilters.outcome} onChange={handleFilterChange("outcome")}>
            <option value="all">All outcomes</option>
            <option value="none">No outcome</option>
            {noteMeta.outcomes.map((item) => (
              <option key={item} value={item}>{labelize(item)}</option>
            ))}
          </select>
          <select style={S.noteFilterSelect} value={noteFilters.follow_up} onChange={handleFilterChange("follow_up")}>
            <option value="all">All follow-ups</option>
            <option value="scheduled">Scheduled only</option>
            <option value="due">Due now</option>
          </select>
          <select style={S.noteFilterSelect} value={noteFilters.follow_up_status} onChange={handleFilterChange("follow_up_status")}>
            <option value="all">All statuses</option>
            {noteMeta.follow_up_statuses.map((item) => (
              <option key={item} value={item}>{labelize(item)}</option>
            ))}
          </select>
        </div>
      )}

      <div style={S.remarkList}>
        {!canViewNotes ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>Note</span>
            <p style={S.remarkEmptyText}>Saved notes are hidden for your role.</p>
          </div>
        ) : notesLoading ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>...</span>
            <p style={S.remarkEmptyText}>Refreshing CRM timeline...</p>
          </div>
        ) : visibleNotes.length === 0 ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>Note</span>
            <p style={S.remarkEmptyText}>No notes yet. Add the first one above.</p>
          </div>
        ) : (
          paginatedNotes.map((note, index) => {
            const recommendationContext =
              noteRecommendationMap.get(String(note.id || ""))
              || noteRecommendationMap.get(String(note.parent_note_id || ""));
            return (
              <div key={note.id || index} style={{ ...S.remarkItem, ...(index === 0 ? S.remarkItemFirst : {}) }}>
                <div style={S.remarkItemLeft}>
                  <div style={S.remarkAvatar}>
                    {formatActorName(note.created_by_name, note.created_by_email, "Admin")[0].toUpperCase()}
                  </div>
                </div>
                <div style={S.remarkItemRight}>
                  <div style={S.remarkItemTop}>
                    <div>
                      <span style={S.remarkAuthor}>
                        {formatActorName(note.created_by_name, note.created_by_email)}
                      </span>
                      <div style={S.noteMetaRow}>
                        <span style={S.noteTypeBadge}>{labelize(note.note_type || "general")}</span>
                        {note.outcome && note.outcome !== "follow_up_required" && <span style={S.noteOutcomeBadge}>{labelize(note.outcome)}</span>}
                        {String(note.follow_up_status_effective || "").trim().toLowerCase() !== "none" && (
                          <span
                            style={{
                              ...S.noteStatusBadge,
                              ...(String(note.follow_up_status_effective || "").trim().toLowerCase() === "completed" ? S.noteStatusBadgeCompleted : {}),
                              ...(String(note.follow_up_status_effective || "").trim().toLowerCase() === "cancelled" ? S.noteStatusBadgeCancelled : {}),
                            }}
                          >
                            {labelize(note.follow_up_status_effective)}
                          </span>
                        )}
                        {note.follow_up_at && String(note.follow_up_status_effective || "").trim().toLowerCase() !== "completed" && String(note.follow_up_status_effective || "").trim().toLowerCase() !== "cancelled" && (
                          <span
                            style={{
                              ...S.noteFollowUpBadge,
                              ...(isFollowUpDue(note.follow_up_at) ? S.noteFollowUpBadgeDue : {}),
                            }}
                          >
                            {isFollowUpDue(note.follow_up_at) ? "Due " : "Follow-up "}
                            {formatDateTime(note.follow_up_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    <span style={S.remarkTime}>{formatDateTime(note.created_at)}</span>
                  </div>
                  {recommendationContext && (
                    <div style={S.noteRecommendationContext}>
                      <span style={S.noteRecommendationBadge}>{recommendationContext.badge}</span>
                      <span style={S.noteRecommendationText}>{recommendationContext.label}</span>
                      {recommendationContext.meta && (
                        <span style={S.noteRecommendationMeta}>{recommendationContext.meta}</span>
                      )}
                    </div>
                  )}
                  <p style={S.remarkText}>{note.content || note.note}</p>
                  {canAddNotes && noteNeedsFollowUp(note) && (
                    <div style={S.noteCardActions}>
                      {rescheduleDraft.noteId === note.id ? (
                        <div style={S.noteInlineActionGroup}>
                          <input
                            type="datetime-local"
                            style={S.noteInlineInput}
                            value={rescheduleDraft.value}
                            onChange={(event) =>
                              setRescheduleDraft((current) => ({ ...current, value: event.target.value }))
                            }
                          />
                          <button
                            type="button"
                            style={S.noteFollowUpBtn}
                            onClick={() => handleSaveReschedule(note)}
                            disabled={!rescheduleDraft.value || noteActionBusyId === note.id}
                          >
                            {noteActionBusyId === note.id ? "Saving..." : "Save"}
                          </button>
                          <button
                            type="button"
                            style={S.noteGhostBtn}
                            onClick={() => setRescheduleDraft({ noteId: null, value: "" })}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={S.noteInlineActionGroup}>
                          <button
                            type="button"
                            style={S.noteFollowUpBtn}
                            onClick={() => handleStartFollowUp(note)}
                          >
                            Follow Up
                          </button>
                          <button
                            type="button"
                            style={S.noteSecondaryBtn}
                            onClick={() => handleStartReschedule(note)}
                          >
                            Reschedule
                          </button>
                          <button
                            type="button"
                            style={S.noteSecondaryBtn}
                            onClick={() => handleCompleteFollowUp(note)}
                            disabled={noteActionBusyId === note.id}
                          >
                            {noteActionBusyId === note.id ? "Working..." : "Complete"}
                          </button>
                          <button
                            type="button"
                            style={S.noteSecondaryBtn}
                            onClick={() => handleCancelFollowUp(note)}
                            disabled={noteActionBusyId === note.id}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {canViewNotes && visibleNotes.length > NOTES_PAGE_SIZE && (
        <div style={S.notePagination}>
          <div style={S.notePaginationInfo}>
            Showing {(currentNotesPage - 1) * NOTES_PAGE_SIZE + 1}
            {" "}to {Math.min(currentNotesPage * NOTES_PAGE_SIZE, visibleNotes.length)}
            {" "}of {visibleNotes.length} notes
          </div>
          <div style={S.notePaginationControls}>
            <button
              type="button"
              style={{
                ...S.notePaginationButton,
                ...(currentNotesPage === 1 ? S.notePaginationButtonDisabled : {}),
              }}
              onClick={() => setCurrentNotesPage((current) => Math.max(1, current - 1))}
              disabled={currentNotesPage === 1}
            >
              Prev
            </button>
            {notePageButtons.map((page) => (
              <button
                key={page}
                type="button"
                style={{
                  ...S.notePaginationButton,
                  ...(page === currentNotesPage ? S.notePaginationButtonActive : {}),
                }}
                onClick={() => setCurrentNotesPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              style={{
                ...S.notePaginationButton,
                ...(currentNotesPage === totalNotePages ? S.notePaginationButtonDisabled : {}),
              }}
              onClick={() => setCurrentNotesPage((current) => Math.min(totalNotePages, current + 1))}
              disabled={currentNotesPage === totalNotePages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileRecommendationsSection({
  profile,
  authHeaders,
  user,
  onViewProfile,
  recommendations,
  recommendationsLoading,
  recommendationMeta,
  recommendationSearch,
  recommendationSearchMode,
  setRecommendationSearch,
  setRecommendationSearchMode,
  recommendationResults,
  recommendationSearchLoading,
  recommendationSearchError,
  recommendationSearchFeedback,
  selectedRecommendationTargets,
  recommendationNotesByTarget,
  savingRecommendation,
  canGiveRecommendations,
  canViewRecommendations,
  canEditRecommendations,
  canViewTasks,
  canCreateTasks,
  canDownloadRecommendationBiodataPermission,
  recommendationOutcomeDrafts,
  recommendationOutcomeBusyId,
  recommendationDownloadBusyId,
  onSelectTarget,
  onRemoveTarget,
  onTargetNoteChange,
  onCreateRecommendation,
  onOutcomeDraftChange,
  onSaveOutcome,
  onDownloadBiodata,
}) {
  const visibleRecommendations = useMemo(
    () => (canViewRecommendations ? recommendations : []),
    [canViewRecommendations, recommendations]
  );
  const [recommendationTaskUsers, setRecommendationTaskUsers] = useState([]);
  const [recommendationTaskUsersLoading, setRecommendationTaskUsersLoading] = useState(false);
  const [recommendationTasks, setRecommendationTasks] = useState([]);
  const [recommendationTasksLoading, setRecommendationTasksLoading] = useState(false);
  const [recommendationTaskModalOpen, setRecommendationTaskModalOpen] = useState(false);
  const [recommendationTaskModalTab, setRecommendationTaskModalTab] = useState("view-tasks");
  const [recommendationTaskModalError, setRecommendationTaskModalError] = useState("");
  const [recommendationTaskBusy, setRecommendationTaskBusy] = useState(false);
  const [recommendationTaskTarget, setRecommendationTaskTarget] = useState(null);
  const [expandedRecommendationTaskIds, setExpandedRecommendationTaskIds] = useState([]);
  const [recommendationTaskHistoryById, setRecommendationTaskHistoryById] = useState({});
  const [recommendationTaskHistoryLoadingById, setRecommendationTaskHistoryLoadingById] = useState({});
  const [recommendationTaskHistoryErrorById, setRecommendationTaskHistoryErrorById] = useState({});
  const recommendationTaskLoadSeqRef = useRef(0);
  const [recommendationSectionTab, setRecommendationSectionTab] = useState(
    canGiveRecommendations ? "add" : "view"
  );
  const [recommendationListSearch, setRecommendationListSearch] = useState("");
  const [recommendationListSearchMode, setRecommendationListSearchMode] = useState(PROFILE_NO_DISPLAY_SEARCH_KEY);
  const [selectedRecommendationListActor, setSelectedRecommendationListActor] = useState("all");
  const [selectedRecommendationListId, setSelectedRecommendationListId] = useState("");
  const [recommendationListPickerOpen, setRecommendationListPickerOpen] = useState(false);
  const [copiedRecommendationProfileNumberId, setCopiedRecommendationProfileNumberId] = useState("");
  const [recommendationTaskForm, setRecommendationTaskForm] = useState({
    title: "",
    description: "",
    due_date: "",
    assigned_to_user_id: "",
  });
  const selectedTargets = Array.isArray(selectedRecommendationTargets) ? selectedRecommendationTargets : [];
  const selectedTargetCount = selectedTargets.length;
  const activeCount = visibleRecommendations.filter((item) => {
    const status = String(item?.status || "").trim().toLowerCase();
    return !["matched", "rejected", "cancelled"].includes(status);
  }).length;
  const pendingCount = visibleRecommendations.filter((item) => {
    const status = String(item?.status || "").trim().toLowerCase();
    return ["recommended", "shared", "viewed"].includes(status);
  }).length;
  const rejectedCount = visibleRecommendations.filter((item) => {
    const status = String(item?.status || "").trim().toLowerCase();
    return status === "rejected";
  }).length;
  const cancelledCount = visibleRecommendations.filter((item) => {
    const status = String(item?.status || "").trim().toLowerCase();
    return status === "cancelled";
  }).length;
  const recommendationActorOptions = useMemo(() => {
    const seen = new Set();
    return visibleRecommendations
      .map((item) => {
        const actorValue = String(
          item?.recommended_by ||
          item?.recommended_by_email ||
          item?.recommended_by_name ||
          ""
        ).trim();
        if (!actorValue || seen.has(actorValue)) return null;
        seen.add(actorValue);
        return {
          value: actorValue,
          label: formatActorName(item?.recommended_by_name, item?.recommended_by_email),
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [visibleRecommendations]);
  const filteredRecommendations = useMemo(() => {
    if (selectedRecommendationListActor === "all") return visibleRecommendations;
    return visibleRecommendations.filter((item) => (
      String(
        item?.recommended_by ||
        item?.recommended_by_email ||
        item?.recommended_by_name ||
        ""
      ).trim() === selectedRecommendationListActor
    ));
  }, [selectedRecommendationListActor, visibleRecommendations]);
  const selectedRecommendationListItem = useMemo(
    () => filteredRecommendations.find((item) => String(item?.id || "") === String(selectedRecommendationListId || "")) || null,
    [filteredRecommendations, selectedRecommendationListId]
  );
  const recommendationListSearchResults = useMemo(() => {
    const query = String(recommendationListSearch || "").trim().toLowerCase();
    if (!query) return filteredRecommendations;

    return filteredRecommendations.filter((item) => {
      const profileNumberDisplay = String(
        formatProfileNumberValue(item?.to_profile_no, item?.to_profile_gender) || item?.to_profile_no || ""
      ).trim().toLowerCase();
      const profileName = String(item?.to_profile_name || "").trim().toLowerCase();
      return isProfileNoDisplaySearchMode(recommendationListSearchMode)
        ? profileNumberDisplay.includes(query)
        : profileName.includes(query);
    });
  }, [filteredRecommendations, recommendationListSearch, recommendationListSearchMode]);
  const renderedRecommendations = selectedRecommendationListItem ? [selectedRecommendationListItem] : [];
  const recommendationTasksByRecommendationId = useMemo(() => {
    const map = new Map();
    safeArray(recommendationTasks).forEach((task) => {
      const recommendationKey = String(task?.recommendation_id || "").trim();
      if (!recommendationKey) return;
      if (!map.has(recommendationKey)) map.set(recommendationKey, []);
      map.get(recommendationKey).push(task);
    });
    return map;
  }, [recommendationTasks]);
  const recommendationOpenTaskCountByRecommendationId = useMemo(() => {
    const map = new Map();
    safeArray(recommendationTasks).forEach((task) => {
      const recommendationKey = String(task?.recommendation_id || "").trim();
      if (!recommendationKey) return;
      const status = String(task?.status || "").trim().toLowerCase();
      if (status === "completed") return;
      map.set(recommendationKey, (map.get(recommendationKey) || 0) + 1);
    });
    return map;
  }, [recommendationTasks]);
  const defaultRecommendationTaskAssigneeId = useMemo(
    () => (recommendationTaskUsers[0]?.id ? String(recommendationTaskUsers[0].id) : ""),
    [recommendationTaskUsers]
  );
  const visibleRecommendationTasks = useMemo(
    () => recommendationTasksByRecommendationId.get(String(recommendationTaskTarget?.id || "")) || [],
    [recommendationTaskTarget?.id, recommendationTasksByRecommendationId]
  );
  const visibleRecommendationOpenTaskCount = useMemo(
    () => visibleRecommendationTasks.filter((task) => String(task?.status || "").trim().toLowerCase() !== "completed").length,
    [visibleRecommendationTasks]
  );

  const refreshRecommendationTasks = useCallback(async () => {
    const loadSeq = recommendationTaskLoadSeqRef.current + 1;
    recommendationTaskLoadSeqRef.current = loadSeq;

    if (!canViewTasks) {
      setRecommendationTasks([]);
      setExpandedRecommendationTaskIds([]);
      setRecommendationTaskHistoryById({});
      setRecommendationTaskHistoryLoadingById({});
      setRecommendationTaskHistoryErrorById({});
      setRecommendationTasksLoading(false);
      return;
    }

    try {
      setRecommendationTasksLoading(true);
      setRecommendationTasks([]);
      setExpandedRecommendationTaskIds([]);
      setRecommendationTaskHistoryById({});
      setRecommendationTaskHistoryLoadingById({});
      setRecommendationTaskHistoryErrorById({});
      const [myRes, assignedRes] = await Promise.all([
        API.get("/tasks/my", authHeaders),
        API.get("/tasks/assigned-by-me", authHeaders),
      ]);
      const nextTasks = dedupeTasksById([
        ...(Array.isArray(myRes.data) ? myRes.data : []),
        ...(Array.isArray(assignedRes.data) ? assignedRes.data : []),
      ]).filter((task) => isTaskVisibleToCurrentUser(task, user));
      if (recommendationTaskLoadSeqRef.current !== loadSeq) return;
      setRecommendationTasks(nextTasks);
    } catch (err) {
      if (recommendationTaskLoadSeqRef.current !== loadSeq) return;
      console.error("Recommendation task load failed:", err);
      setRecommendationTasks([]);
    } finally {
      if (recommendationTaskLoadSeqRef.current === loadSeq) {
        setRecommendationTasksLoading(false);
      }
    }
  }, [authHeaders, canViewTasks, user]);

  useEffect(() => {
    if (!canCreateTasks) {
      setRecommendationTaskUsers([]);
      return undefined;
    }

    let cancelled = false;
    const loadTaskUsers = async () => {
      try {
        setRecommendationTaskUsersLoading(true);
        const res = await API.get("/tasks/users", authHeaders);
        if (cancelled) return;
        setRecommendationTaskUsers(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (cancelled) return;
        console.error("Recommendation task users load failed:", err);
        setRecommendationTaskUsers([]);
      } finally {
        if (!cancelled) setRecommendationTaskUsersLoading(false);
      }
    };

    loadTaskUsers();

    return () => {
      cancelled = true;
    };
  }, [authHeaders, canCreateTasks]);

  useEffect(() => {
    refreshRecommendationTasks();
  }, [refreshRecommendationTasks]);

  useEffect(() => {
    if (!selectedRecommendationListId) return;
    const stillExists = filteredRecommendations.some((item) => String(item?.id || "") === String(selectedRecommendationListId));
    if (!stillExists) {
      setSelectedRecommendationListId("");
    }
  }, [filteredRecommendations, selectedRecommendationListId]);

  useEffect(() => {
    if (selectedRecommendationListActor === "all") return;
    const stillExists = recommendationActorOptions.some((item) => item.value === selectedRecommendationListActor);
    if (!stillExists) {
      setSelectedRecommendationListActor("all");
    }
  }, [recommendationActorOptions, selectedRecommendationListActor]);

  useEffect(() => {
    if (recommendationSectionTab === "add" && !canGiveRecommendations) {
      setRecommendationSectionTab("view");
      return;
    }
    if (recommendationSectionTab === "view" && !canViewRecommendations && canGiveRecommendations) {
      setRecommendationSectionTab("add");
    }
  }, [canGiveRecommendations, canViewRecommendations, recommendationSectionTab]);

  useEffect(() => {
    setRecommendationListPickerOpen(recommendationSectionTab === "view");
  }, [recommendationSectionTab]);

  useEffect(() => {
    if (recommendationSectionTab !== "view") return;
    const nextVisibleIds = new Set(
      recommendationListSearchResults.map((item) => String(item?.id || "")).filter(Boolean)
    );
    if (!selectedRecommendationListId) return;
    if (nextVisibleIds.has(String(selectedRecommendationListId))) return;
    setSelectedRecommendationListId("");
  }, [recommendationListSearchResults, recommendationSectionTab, selectedRecommendationListId]);

  useEffect(() => {
    if (!recommendationTaskModalOpen || recommendationTaskModalTab !== "view-tasks") return;
    if (visibleRecommendationTasks.length === 1 && visibleRecommendationTasks[0]?.id) {
      setExpandedRecommendationTaskIds([String(visibleRecommendationTasks[0].id)]);
      return;
    }
    setExpandedRecommendationTaskIds([]);
  }, [recommendationTaskModalOpen, recommendationTaskModalTab, visibleRecommendationTasks]);

  const closeRecommendationTaskModal = useCallback(() => {
    setRecommendationTaskModalOpen(false);
    setRecommendationTaskModalTab(canViewTasks ? "view-tasks" : "assign-task");
    setRecommendationTaskModalError("");
    setRecommendationTaskBusy(false);
    setRecommendationTaskTarget(null);
    setExpandedRecommendationTaskIds([]);
    setRecommendationTaskHistoryById({});
    setRecommendationTaskHistoryLoadingById({});
    setRecommendationTaskHistoryErrorById({});
    setRecommendationTaskForm({
      title: "",
      description: "",
      due_date: "",
      assigned_to_user_id: defaultRecommendationTaskAssigneeId,
    });
  }, [canViewTasks, defaultRecommendationTaskAssigneeId]);

  const openRecommendationTaskModal = useCallback((recommendation) => {
    setRecommendationTaskTarget(recommendation);
    setRecommendationTaskModalError("");
    setRecommendationTaskModalTab(canViewTasks ? "view-tasks" : "assign-task");
    setExpandedRecommendationTaskIds([]);
    setRecommendationTaskForm({
      title: buildRecommendationTaskTitle(recommendation),
      description: "",
      due_date: "",
      assigned_to_user_id: defaultRecommendationTaskAssigneeId,
    });
    setRecommendationTaskModalOpen(true);
  }, [canViewTasks, defaultRecommendationTaskAssigneeId]);

  const toggleRecommendationTaskExpansion = useCallback((taskId) => {
    const normalizedTaskId = String(taskId || "").trim();
    if (!normalizedTaskId) return;
    setExpandedRecommendationTaskIds((current) => (
      current.includes(normalizedTaskId)
        ? current.filter((id) => id !== normalizedTaskId)
        : current.concat(normalizedTaskId)
    ));
  }, []);

  const loadRecommendationTaskHistory = useCallback(async (taskId) => {
    const normalizedTaskId = String(taskId || "").trim();
    if (!normalizedTaskId) return;

    setRecommendationTaskHistoryLoadingById((current) => ({
      ...current,
      [normalizedTaskId]: true,
    }));
    setRecommendationTaskHistoryErrorById((current) => ({
      ...current,
      [normalizedTaskId]: "",
    }));

    try {
      const res = await API.get(`/tasks/${normalizedTaskId}/history`, authHeaders);
      setRecommendationTaskHistoryById((current) => ({
        ...current,
        [normalizedTaskId]: Array.isArray(res.data?.items) ? res.data.items : [],
      }));
    } catch (err) {
      setRecommendationTaskHistoryErrorById((current) => ({
        ...current,
        [normalizedTaskId]: err?.response?.data?.error || "Failed to load task updates.",
      }));
    } finally {
      setRecommendationTaskHistoryLoadingById((current) => ({
        ...current,
        [normalizedTaskId]: false,
      }));
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!recommendationTaskModalOpen || recommendationTaskModalTab !== "view-tasks") return;
    expandedRecommendationTaskIds.forEach((taskId) => {
      if (recommendationTaskHistoryById[taskId] || recommendationTaskHistoryLoadingById[taskId]) return;
      loadRecommendationTaskHistory(taskId);
    });
  }, [
    expandedRecommendationTaskIds,
    loadRecommendationTaskHistory,
    recommendationTaskHistoryById,
    recommendationTaskHistoryLoadingById,
    recommendationTaskModalOpen,
    recommendationTaskModalTab,
  ]);

  const submitRecommendationTask = useCallback(async () => {
    const title = String(recommendationTaskForm.title || "").trim();
    const recommendationId = recommendationTaskTarget?.id;
    if (!title) {
      setRecommendationTaskModalError("Task title is required.");
      return;
    }
    if (!recommendationTaskForm.assigned_to_user_id) {
      setRecommendationTaskModalError("Select a task owner.");
      return;
    }
    if (!recommendationId) {
      setRecommendationTaskModalError("This recommendation is missing its record id.");
      return;
    }

    try {
      setRecommendationTaskBusy(true);
      setRecommendationTaskModalError("");
      await API.post(
        "/tasks",
        {
          title,
          description: String(recommendationTaskForm.description || "").trim(),
          due_date: recommendationTaskForm.due_date || null,
          assigned_to_user_id: Number(recommendationTaskForm.assigned_to_user_id),
          recommendation_id: Number(recommendationId),
        },
        authHeaders
      );
      closeRecommendationTaskModal();
      await refreshRecommendationTasks();
    } catch (err) {
      setRecommendationTaskModalError(err?.response?.data?.error || "Failed to create task.");
    } finally {
      setRecommendationTaskBusy(false);
    }
  }, [authHeaders, closeRecommendationTaskModal, recommendationTaskForm, recommendationTaskTarget, refreshRecommendationTasks]);

  const handleCopyRecommendationProfileNumber = useCallback(async (event, recommendation) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const profileNumber = formatProfileNumberValue(
      recommendation?.to_profile_no,
      recommendation?.to_profile_gender
    );
    const recommendationId = String(recommendation?.id || "").trim();
    if (!profileNumber || !recommendationId) return;
    const copied = await copyToClipboard(profileNumber);
    if (!copied) return;
    setCopiedRecommendationProfileNumberId(recommendationId);
    window.setTimeout(() => {
      setCopiedRecommendationProfileNumberId((current) => (
        current === recommendationId ? "" : current
      ));
    }, 1400);
  }, []);

  return (
    <div style={S.remarkBox}>
      <div style={S.remarkHeader}>
        <div>
          <h3 style={S.remarkTitle}>Recommendations</h3>
        </div>
        <span style={S.remarkCount}>{visibleRecommendations.length} recommendation{visibleRecommendations.length !== 1 ? "s" : ""}</span>
      </div>

      <div style={S.recommendationMetricGrid} className="note-stat-grid recommendation-stat-grid">
        <MetricCard label="Recommended" value={visibleRecommendations.length} compact />
        <MetricCard label="Active" value={activeCount} compact />
        <MetricCard label="Pending" value={pendingCount} accent="alert" compact />
        <MetricCard label="Rejected" value={rejectedCount} compact />
        <MetricCard label="Cancelled" value={cancelledCount} compact />
      </div>

      {canGiveRecommendations && canViewRecommendations && (
        <div style={S.recommendationSectionTabRow}>
          <button
            type="button"
            style={{
              ...S.recommendationSectionTabBtn,
              ...(recommendationSectionTab === "add" ? S.recommendationSectionTabBtnActive : {}),
            }}
            onClick={() => setRecommendationSectionTab("add")}
          >
            Add Recommendations
          </button>
          <button
            type="button"
            style={{
              ...S.recommendationSectionTabBtn,
              ...(recommendationSectionTab === "view" ? S.recommendationSectionTabBtnActive : {}),
            }}
            onClick={() => setRecommendationSectionTab("view")}
          >
            View Recommendations
          </button>
        </div>
      )}

      {canGiveRecommendations && recommendationSectionTab === "add" && (
        <div style={S.remarkInputWrap}>
          <div style={S.recommendationSearchModeRow}>
            <label style={S.recommendationRadioLabel}>
              <input
                type="radio"
                name="recommendation-search-mode"
                value={PROFILE_NO_DISPLAY_SEARCH_KEY}
                checked={isProfileNoDisplaySearchMode(recommendationSearchMode)}
                onChange={(event) => setRecommendationSearchMode(event.target.value)}
              />
              <span>Profile Number</span>
            </label>
            <label style={S.recommendationRadioLabel}>
              <input
                type="radio"
                name="recommendation-search-mode"
                value="name"
                checked={recommendationSearchMode === "name"}
                onChange={(event) => setRecommendationSearchMode(event.target.value)}
              />
              <span>Name</span>
            </label>
          </div>

          <div style={S.noteField}>
            <label style={S.noteFieldLabel}>Recommend Profile</label>
            <input
              type="text"
              style={S.noteInput}
              value={recommendationSearch}
              onChange={(event) => setRecommendationSearch(event.target.value)}
              placeholder={
                canGiveRecommendations
                  ? isProfileNoDisplaySearchMode(recommendationSearchMode)
                    ? "Enter profile number e.g. M123"
                    : "Enter profile name..."
                  : "Recommendation give permission is required"
              }
              disabled={!canGiveRecommendations}
            />
          </div>

          {canGiveRecommendations && (
            <div style={S.recommendationSearchPanel}>
              {recommendationSearchLoading ? (
                <div style={S.recommendationSearchHint}>Searching profiles...</div>
              ) : recommendationSearch.trim().length < (isProfileNoDisplaySearchMode(recommendationSearchMode) ? 1 : 2) ? (
                null
              ) : recommendationSearchError ? (
                <div style={S.recommendationSearchError}>{recommendationSearchError}</div>
              ) : recommendationResults.length === 0 ? (
                <div style={S.recommendationSearchHint}>
                  {recommendationSearchFeedback || "No matching profiles available for recommendation."}
                </div>
              ) : (
                <div style={S.recommendationSearchList} className="recommendation-search-list">
                  {recommendationResults.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      style={{
                        ...S.recommendationSearchItem,
                        ...(item.recommendationAlreadyGiven ? S.recommendationSearchItemDisabled : {}),
                      }}
                      onClick={() => {
                        if (item.recommendationAlreadyGiven) return;
                        onSelectTarget(item);
                      }}
                      disabled={item.recommendationAlreadyGiven}
                    >
                      <div style={S.recommendationSearchItemInner}>
                        <div style={S.recommendationSearchPhotoWrap}>
                          <ProfilePhoto
                            src={item.photo || item.primary_photo_url_cache || null}
                            initials={getProfileInitials(item)}
                            avatarColor={pickColor(item?.first_name || getProfileNumberDisplay(item) || "R")}
                          />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={S.recommendationSearchName}>{getProfileDisplayName(item) || `Profile #${getProfileNumberDisplay(item) || item.id}`}</div>
                          <div style={S.recommendationSearchMeta}>{buildRecommendationResultMeta(item)}</div>
                        </div>
                        <span
                          style={{
                            ...S.recommendationAddPill,
                            ...(item.recommendationAlreadyGiven ? S.recommendationAddPillDisabled : {}),
                          }}
                        >
                          {item.recommendationAlreadyGiven ? "Already Given" : "Add"}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {selectedTargetCount > 0 && (
            <div style={S.recommendationSelectedWrap}>
              <div style={S.recommendationSelectedHeader}>
                <div>
                  <div style={S.recommendationSelectedTitle}>Added profiles ({selectedTargetCount})</div>
                  <div style={S.recommendationSelectedHint}>Add notes if needed, or remove a profile before creating.</div>
                </div>
                <button type="button" style={S.noteGhostBtn} onClick={() => onSelectTarget(null)}>
                  Clear all
                </button>
              </div>
              <div style={S.recommendationSelectedGrid}>
                {selectedTargets.map((target) => (
                  <div key={target.id} style={S.recommendationSelectedCard}>
                    <div style={S.recommendationSelectedMain}>
                      <div style={S.recommendationTargetPhotoWrap}>
                        <ProfilePhoto
                          src={target?.photo || target?.primary_photo_url_cache || null}
                          initials={getProfileInitials(target)}
                          avatarColor={pickColor(target?.first_name || getProfileNumberDisplay(target) || "R")}
                        />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={S.recommendationTargetTitle}>{getProfileDisplayName(target) || "Selected profile"}</div>
                        <div style={S.recommendationTargetMeta}>{buildRecommendationResultMeta(target)}</div>
                      </div>
                    </div>
                    <div style={S.recommendationSelectedControls}>
                      <button
                        type="button"
                        style={S.recommendationSelectedRemove}
                        onClick={() => onRemoveTarget(target.id)}
                      >
                        Remove
                      </button>
                    </div>
                    <textarea
                      style={S.recommendationSelectedNote}
                      value={recommendationNotesByTarget?.[String(target.id)] || ""}
                      onChange={(event) => onTargetNoteChange(target.id, event.target.value)}
                      placeholder="Optional note for this recommendation"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={S.remarkActions}>
            <span style={S.remarkCharCount}>
              {selectedTargetCount ? `Notes are optional for each selected profile.` : ""}
            </span>
            <button
              type="button"
              onClick={onCreateRecommendation}
              style={{ ...S.remarkBtn, ...((savingRecommendation || selectedTargetCount === 0) ? S.remarkBtnDisabled : {}) }}
              disabled={savingRecommendation || selectedTargetCount === 0}
            >
              {savingRecommendation
                ? "Saving..."
                : selectedTargetCount > 1
                  ? `Create ${selectedTargetCount} Recommendations`
                  : "Create Recommendation"}
            </button>
          </div>
        </div>
      )}

      {canViewRecommendations && recommendationSectionTab === "view" && visibleRecommendations.length > 0 && (
        <div style={S.remarkInputWrap}>
          <div style={S.recommendationSearchModeRow}>
            <label style={S.recommendationRadioLabel}>
              <input
                type="radio"
                name="recommendation-list-search-mode"
                value={PROFILE_NO_DISPLAY_SEARCH_KEY}
                checked={isProfileNoDisplaySearchMode(recommendationListSearchMode)}
                onChange={(event) => setRecommendationListSearchMode(event.target.value)}
              />
              <span>Profile Number</span>
            </label>
            <label style={S.recommendationRadioLabel}>
              <input
                type="radio"
                name="recommendation-list-search-mode"
                value="name"
                checked={recommendationListSearchMode === "name"}
                onChange={(event) => setRecommendationListSearchMode(event.target.value)}
              />
              <span>Name</span>
            </label>
          </div>

          <div style={S.recommendationFilterRow} className="profile-grid-2">
            <div style={S.noteField}>
              <label style={S.noteFieldLabel}>Find Recommended Profile</label>
              <input
                type="text"
                style={S.noteInput}
                value={recommendationListSearch}
                onChange={(event) => setRecommendationListSearch(event.target.value)}
                onFocus={() => setRecommendationListPickerOpen(true)}
                onClick={() => setRecommendationListPickerOpen(true)}
                placeholder={
                  isProfileNoDisplaySearchMode(recommendationListSearchMode)
                    ? "Enter profile number e.g. M123"
                    : "Enter profile name..."
                }
              />
            </div>

            <div style={S.noteField}>
              <label style={S.noteFieldLabel}>Added By</label>
              <select
                style={S.noteSelect}
                value={selectedRecommendationListActor}
                onChange={(event) => {
                  setSelectedRecommendationListActor(event.target.value);
                  setSelectedRecommendationListId("");
                  setRecommendationListPickerOpen(true);
                }}
              >
                <option value="all">All People</option>
                {recommendationActorOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {recommendationListPickerOpen ? (
            <div style={S.recommendationSearchPanelFlat}>
              {recommendationListSearchResults.length === 0 ? (
                <div style={S.recommendationSearchHint}>No matching recommended profiles found.</div>
              ) : (
                <div style={S.recommendationSearchListFlat} className="recommendation-search-list">
                  {recommendationListSearchResults.map((item) => {
                    const profileNumberDisplay = formatProfileNumberValue(item?.to_profile_no, item?.to_profile_gender);
                    const isCopied = copiedRecommendationProfileNumberId === String(item?.id || "");
                    const isSelected = String(selectedRecommendationListId || "") === String(item?.id || "");
                    return (
                      <div
                        key={item.id}
                        style={{
                          ...S.recommendationSearchItemFlat,
                          ...(isSelected ? S.recommendationSearchItemFlatActive : {}),
                        }}
                      >
                        <button
                          type="button"
                          style={S.recommendationSearchItemMain}
                          onClick={() => {
                            setSelectedRecommendationListId(String(item.id));
                          }}
                        >
                          <div style={S.recommendationSearchItemInner}>
                            <div style={S.recommendationSearchPhotoWrap}>
                              <ProfilePhoto
                                src={item.to_profile_photo || null}
                                initials={getProfileInitials({
                                  full_name: item.to_profile_name,
                                  [RAW_PROFILE_NUMBER_FIELD]: item.to_profile_no || item.to_profile_id,
                                })}
                                avatarColor={pickColor(item.to_profile_name || formatProfileNumberValue(item.to_profile_no, item.to_profile_gender) || "R")}
                              />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={S.recommendationSearchTitleRow}>
                                <div style={S.recommendationSearchName}>
                                  {item.to_profile_name || `Profile #${formatProfileNumberValue(item.to_profile_no, item.to_profile_gender) || item.to_profile_id}`}
                                </div>
                                {profileNumberDisplay && (
                                  <span style={S.recommendationSearchNumber}>#{profileNumberDisplay}</span>
                                )}
                              </div>
                              <div style={S.recommendationSearchMeta}>
                                {[formatRecommendationStatusLabel(item.status || "recommended")].filter(Boolean).join(" | ")}
                              </div>
                            </div>
                          </div>
                        </button>
                        <div style={S.recommendationSearchActionRow}>
                          {profileNumberDisplay ? (
                            <button
                              type="button"
                              style={S.recommendationCopyPill}
                              onClick={(event) => handleCopyRecommendationProfileNumber(event, item)}
                            >
                              {isCopied ? "Copied" : "Copy Profile No"}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            style={S.recommendationAddPillButton}
                            onClick={() => {
                              setSelectedRecommendationListId(String(item.id));
                            }}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {recommendationSectionTab === "view" && (
      <div style={S.remarkList}>
        {!canViewRecommendations ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>Link</span>
            <p style={S.remarkEmptyText}>Recommendations are hidden for your role.</p>
          </div>
        ) : recommendationsLoading ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>...</span>
            <p style={S.remarkEmptyText}>Loading recommendations...</p>
          </div>
        ) : visibleRecommendations.length === 0 ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>Recommend</span>
            <p style={S.remarkEmptyText}>No profile recommendations recorded yet.</p>
          </div>
        ) : renderedRecommendations.length === 0 ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>Search</span>
            <p style={S.remarkEmptyText}>Select a recommended profile above to view its card.</p>
          </div>
        ) : (
          renderedRecommendations.map((item, index) => {
            const draft = recommendationOutcomeDrafts[item.id] || { status: "", notes: "", contactSide: recommendationMeta.defaults?.contact_side || "both" };
            const busy = recommendationOutcomeBusyId === item.id;
            const downloadBusy = recommendationDownloadBusyId === item.id;
            const canDownloadBiodata =
              canDownloadRecommendationBiodataPermission &&
              canDownloadRecommendationBiodata(item) &&
              item?.to_profile_id;
            const contactSideOptions = buildRecommendationContactSideOptions(
              profile,
              {
                id: item.to_profile_id,
                [RAW_PROFILE_NUMBER_FIELD]: item.to_profile_no,
                full_name: item.to_profile_name,
              }
            );
            return (
              <div key={item.id || index} style={{ ...S.remarkItem, ...(index === 0 ? S.remarkItemFirst : {}) }}>
                <div style={S.remarkItemRight}>
                  <div style={S.remarkItemTop}>
                    <button
                      type="button"
                      style={S.recommendationProfileLink}
                      onClick={() => {
                        if (item?.to_profile_id) onViewProfile?.(item.to_profile_id);
                      }}
                      disabled={!item?.to_profile_id || typeof onViewProfile !== "function"}
                    >
                      <div style={S.remarkAvatar}>
                        <ProfilePhoto
                          src={item.to_profile_photo || null}
                          initials={getProfileInitials({
                            full_name: item.to_profile_name,
                            [RAW_PROFILE_NUMBER_FIELD]: item.to_profile_no || item.to_profile_id,
                          })}
                          avatarColor={pickColor(item.to_profile_name || formatProfileNumberValue(item.to_profile_no, item.to_profile_gender) || "R")}
                        />
                      </div>
                      <div style={S.recommendationProfileLinkText}>
                        <span style={S.remarkAuthor}>
                          {item.to_profile_name || `Profile #${formatProfileNumberValue(item.to_profile_no, item.to_profile_gender) || item.to_profile_id}`}
                        </span>
                        <div style={S.noteMetaRow}>
                          {item.to_profile_no && <span style={S.noteTypeBadge}>#{formatProfileNumberValue(item.to_profile_no, item.to_profile_gender)}</span>}
                          <span style={S.noteOutcomeBadge}>{formatRecommendationStatusLabel(item.status || "recommended")}</span>
                        </div>
                      </div>
                    </button>
                    <span style={S.remarkTime}>{formatRelativeTime(item.created_at)}</span>
                  </div>
                  <div style={S.recommendationNoteTitle}>Recommendation Note</div>
                  <p style={S.remarkText}>{item.note_content || "Recommendation linked without note preview."}</p>
                  <div style={S.recommendationCardMeta}>
                    Added by: {formatActorName(item.recommended_by_name, item.recommended_by_email)}
                  </div>

                  {(canCreateTasks || canViewTasks) && (
                    <div style={S.recommendationTaskSummaryRow}>
                      {(() => {
                        const openTaskCount = recommendationOpenTaskCountByRecommendationId.get(String(item?.id || "")) || 0;
                        return (
                        <button
                          type="button"
                          style={{
                            ...S.noteFollowUpBtn,
                            ...((!canViewTasks && (recommendationTaskUsersLoading || recommendationTaskBusy || recommendationTaskUsers.length === 0)) ? S.remarkBtnDisabled : {}),
                          }}
                          onClick={() => openRecommendationTaskModal(item)}
                          disabled={!canViewTasks && (recommendationTaskUsersLoading || recommendationTaskBusy || recommendationTaskUsers.length === 0)}
                        >
                          {recommendationTaskUsersLoading && !canViewTasks
                            ? "Loading..."
                            : canViewTasks
                              ? `View Tasks (${openTaskCount})`
                              : "Assign Task"}
                        </button>
                        );
                      })()}
                    </div>
                  )}

                  {Array.isArray(item.outcomes) && item.outcomes.length > 0 && (
                    <div style={S.recommendationTimeline}>
                      {item.outcomes.map((outcome) => (
                        <div key={outcome.id} style={S.recommendationTimelineItem}>
                          <div style={S.recommendationTimelineTop}>
                            <div style={S.noteMetaRow}>
                              <span style={S.noteOutcomeBadge}>{formatRecommendationStatusLabel(outcome.match_status)}</span>
                              {outcome.contact_side ? (
                                <span style={S.noteStatusBadge}>
                                  {getRecommendationContactSideLabel(
                                    outcome.contact_side,
                                    profile,
                                    {
                                      id: item.to_profile_id,
                                      [RAW_PROFILE_NUMBER_FIELD]: item.to_profile_no,
                                      full_name: item.to_profile_name,
                                    }
                                  )}
                                </span>
                              ) : null}
                              <span style={S.recommendationTimelineActor}>
                                {formatActorName(outcome.matched_by_name, outcome.matched_by_email)}
                              </span>
                            </div>
                            <span style={S.remarkTime}>{formatDateTime(outcome.matched_at || outcome.created_at)}</span>
                          </div>
                          {(outcome.outcome_notes || outcome.note_content) && (
                            <div style={S.recommendationTimelineBody}>{outcome.outcome_notes || outcome.note_content}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {canDownloadBiodata && (
                    <div style={S.recommendationDownloadRow}>
                      <span style={S.remarkCharCount}>Biodata is ready for download on this recommendation entry.</span>
                      <button
                        type="button"
                        style={{ ...S.noteFollowUpBtn, ...(downloadBusy ? S.remarkBtnDisabled : {}) }}
                        onClick={() => onDownloadBiodata(item)}
                        disabled={downloadBusy}
                      >
                        {downloadBusy ? "Preparing PDF..." : "Download Biodata PDF"}
                      </button>
                    </div>
                  )}

                  {canEditRecommendations && (
                    <div style={S.recommendationOutcomeComposer}>
                      <div style={S.noteComposerGrid} className="note-composer-grid recommendation-outcome-grid">
                        <div style={S.noteField}>
                          <label style={S.noteFieldLabel}>Update Status</label>
                          <select
                            style={S.noteSelect}
                            value={draft.status}
                            onChange={(event) => onOutcomeDraftChange(item.id, "status", event.target.value)}
                          >
                            <option value="">Choose next stage</option>
                            {recommendationMeta.match_statuses.map((status) => (
                              <option key={status} value={status}>{formatRecommendationStatusLabel(status)}</option>
                            ))}
                          </select>
                        </div>
                        <div style={S.noteField}>
                          <label style={S.noteFieldLabel}>Applies To</label>
                          <select
                            style={S.noteSelect}
                            value={draft.contactSide || recommendationMeta.defaults?.contact_side || "both"}
                            onChange={(event) => onOutcomeDraftChange(item.id, "contactSide", event.target.value)}
                          >
                            {contactSideOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ ...S.noteField, gridColumn: "span 3" }}>
                          <label style={S.noteFieldLabel}>Outcome Note</label>
                          <input
                            type="text"
                            style={S.noteInput}
                            value={draft.notes}
                            onChange={(event) => onOutcomeDraftChange(item.id, "notes", event.target.value)}
                            placeholder="What happened after recommending this profile?"
                          />
                        </div>
                      </div>
                      <div style={S.recommendationOutcomeActions}>
                        <span style={S.remarkCharCount}>Current status: {formatRecommendationStatusLabel(item.status || "recommended")}</span>
                        <button
                          type="button"
                          style={{ ...S.noteFollowUpBtn, ...(busy || !draft.status ? S.remarkBtnDisabled : {}) }}
                          onClick={() => onSaveOutcome(item)}
                          disabled={busy || !draft.status}
                        >
                          {busy ? "Saving..." : "Save Outcome"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      )}

      {recommendationTaskModalOpen && typeof document !== "undefined" && createPortal(
        <div
          style={S.overlayBackdrop}
          onClick={(event) => {
            if (event.target === event.currentTarget && !recommendationTaskBusy) {
              closeRecommendationTaskModal();
            }
          }}
        >
          <div style={S.overlayCard}>
            <div style={S.overlayHeader}>
              <div>
                <p style={S.overlayEyebrow}>Recommendation</p>
                <h2 style={S.overlayTitle}>{buildRecommendationTargetLabel(recommendationTaskTarget)}</h2>
                <p style={S.recommendationTaskModalText}>
                  View task progress for this recommendation or assign a new follow-up task.
                </p>
              </div>
              <button
                type="button"
                style={S.overlayClose}
                onClick={closeRecommendationTaskModal}
                disabled={recommendationTaskBusy}
              >
                x
              </button>
            </div>

            <div style={S.recommendationTaskModalBody}>
              <div style={S.recommendationTaskTabRow}>
                {canViewTasks && (
                  <button
                    type="button"
                    style={{
                      ...S.recommendationTaskTabBtn,
                      ...(recommendationTaskModalTab === "view-tasks" ? S.recommendationTaskTabBtnActive : {}),
                    }}
                    onClick={() => setRecommendationTaskModalTab("view-tasks")}
                  >
                    View Tasks ({visibleRecommendationOpenTaskCount})
                  </button>
                )}
                {canCreateTasks && (
                  <button
                    type="button"
                    style={{
                      ...S.recommendationTaskTabBtn,
                      ...(recommendationTaskModalTab === "assign-task" ? S.recommendationTaskTabBtnActive : {}),
                    }}
                    onClick={() => setRecommendationTaskModalTab("assign-task")}
                  >
                    Assign Task
                  </button>
                )}
              </div>

              {recommendationTaskModalTab === "view-tasks" && canViewTasks && (
                <>
                  {recommendationTasksLoading ? (
                    <div style={S.recommendationSearchHint}>Loading linked tasks...</div>
                  ) : (visibleRecommendationTasks.length === 0) ? (
                    <div style={S.recommendationSearchHint}>
                      No linked tasks found for this recommendation yet.
                    </div>
                  ) : (
                    <div style={S.recommendationTaskList}>
                      {visibleRecommendationTasks.map((task) => {
                        const isExpanded = expandedRecommendationTaskIds.includes(String(task.id));
                        return (
                          <div key={task.id} style={S.recommendationTaskItem}>
                            <button
                              type="button"
                              style={S.recommendationTaskToggle}
                              onClick={() => toggleRecommendationTaskExpansion(task.id)}
                            >
                              <div style={S.recommendationTaskTop}>
                                <div style={S.recommendationTaskTitle}>{task.title || "Untitled task"}</div>
                                <div style={S.recommendationTaskBadgeRow}>
                                  <span style={getProfileRecommendationTaskStatusStyle(task.status)}>
                                    {getProfileRecommendationTaskStatusLabel(task.status)}
                                  </span>
                                  <span style={S.recommendationTaskMeta}>
                                    {task.due_date ? `Due ${formatDate(task.due_date)}` : "No due date"}
                                  </span>
                                  <span style={S.recommendationTaskChevron}>{isExpanded ? "^" : "v"}</span>
                                </div>
                              </div>
                            </button>
                            {isExpanded && (
                              <>
                                {task.description ? (
                                  <div style={S.recommendationTaskBody}>{task.description}</div>
                                ) : (
                                  <div style={S.recommendationTaskBodyMuted}>No description added yet.</div>
                                )}
                                <div style={S.recommendationTaskMetaRow}>
                                  <span>Owner: {formatActorName(task.assigned_to_name, task.assigned_to_email, "User")}</span>
                                  <span>Assigned by: {formatActorName(task.assigned_by_name, task.assigned_by_email, "User")}</span>
                                  {task.completed_at ? <span>Completed: {formatDateTime(task.completed_at)}</span> : null}
                                </div>
                                {Boolean(recommendationTaskHistoryErrorById[String(task.id)]) && (
                                  <div style={S.recommendationSearchError}>
                                    {recommendationTaskHistoryErrorById[String(task.id)]}
                                  </div>
                                )}
                                {recommendationTaskHistoryLoadingById[String(task.id)] ? (
                                  <div style={S.recommendationTaskHistoryEmpty}>Loading task updates...</div>
                                ) : (
                                  <div style={S.recommendationTaskHistoryList}>
                                    {(recommendationTaskHistoryById[String(task.id)] || []).length === 0 ? (
                                      <div style={S.recommendationTaskHistoryEmpty}>No task updates recorded yet.</div>
                                    ) : (
                                      (recommendationTaskHistoryById[String(task.id)] || []).map((historyItem) => (
                                        <div key={historyItem.id} style={S.recommendationTaskHistoryRow}>
                                          <div style={S.recommendationTaskHistoryDot} />
                                          <div style={S.recommendationTaskHistoryCard}>
                                            <div style={S.recommendationTaskHistoryTitle}>
                                              {formatRecommendationTaskActivity(historyItem)}
                                            </div>
                                            <div style={S.recommendationTaskHistoryMeta}>
                                              {formatActorName("", historyItem.changed_by_email, "System")} | {formatDateTime(historyItem.created_at)}
                                            </div>
                                            {historyItem.note ? (
                                              <div style={S.recommendationTaskHistoryNote}>{historyItem.note}</div>
                                            ) : null}
                                          </div>
                                        </div>
                                      ))
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {recommendationTaskModalTab === "assign-task" && canCreateTasks && (
                <>
                  <div style={S.noteComposerGrid} className="note-composer-grid recommendation-task-grid">
                    <div style={S.noteField}>
                      <label style={S.noteFieldLabel}>Title</label>
                      <input
                        type="text"
                        style={S.noteInput}
                        value={recommendationTaskForm.title}
                        onChange={(event) =>
                          setRecommendationTaskForm((current) => ({ ...current, title: event.target.value }))
                        }
                        placeholder="Follow up recommendation"
                      />
                    </div>
                    <div style={S.noteField}>
                      <label style={S.noteFieldLabel}>Due Date</label>
                      <input
                        type="date"
                        style={S.noteInput}
                        value={recommendationTaskForm.due_date}
                        onChange={(event) =>
                          setRecommendationTaskForm((current) => ({ ...current, due_date: event.target.value }))
                        }
                      />
                    </div>
                    <div style={S.noteField}>
                      <label style={S.noteFieldLabel}>Assign To</label>
                      <select
                        style={S.noteSelect}
                        value={recommendationTaskForm.assigned_to_user_id}
                        onChange={(event) =>
                          setRecommendationTaskForm((current) => ({ ...current, assigned_to_user_id: event.target.value }))
                        }
                        disabled={recommendationTaskUsersLoading}
                      >
                        <option value="">{recommendationTaskUsersLoading ? "Loading users..." : "Select user"}</option>
                        {recommendationTaskUsers.map((taskUser) => (
                          <option key={taskUser.id} value={taskUser.id}>
                            {formatRecommendationTaskUserLabel(taskUser)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ ...S.noteField, gridColumn: "span 3" }}>
                      <label style={S.noteFieldLabel}>Description</label>
                      <textarea
                        style={S.recommendationTaskTextarea}
                        value={recommendationTaskForm.description}
                        onChange={(event) =>
                          setRecommendationTaskForm((current) => ({ ...current, description: event.target.value }))
                        }
                        placeholder="Add the exact follow-up or review work needed"
                      />
                    </div>
                  </div>

                  {recommendationTaskModalError ? (
                    <div style={S.recommendationSearchError}>{recommendationTaskModalError}</div>
                  ) : recommendationTaskUsers.length === 0 ? (
                    <div style={S.recommendationSearchHint}>
                      No active task owners are available for assignment right now.
                    </div>
                  ) : null}
                </>
              )}

              {!canViewTasks && !canCreateTasks && (
                <div style={S.recommendationSearchHint}>
                  Task access is not available for your role on this recommendation.
                </div>
              )}
            </div>

            <div style={S.overlayFooter}>
              <span style={S.uploadHint}>
                {recommendationTaskTarget?.id
                  ? `Linked to recommendation #${recommendationTaskTarget.id}`
                  : "Task will be linked to this recommendation"}
              </span>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  style={S.overlayCancel}
                  onClick={closeRecommendationTaskModal}
                  disabled={recommendationTaskBusy}
                >
                  Cancel
                </button>
                {recommendationTaskModalTab === "assign-task" && canCreateTasks ? (
                  <button
                    type="button"
                    style={{
                      ...S.overlaySubmit,
                      ...((recommendationTaskBusy || recommendationTaskUsersLoading || recommendationTaskUsers.length === 0) ? S.overlaySubmitDisabled : {}),
                    }}
                    onClick={submitRecommendationTask}
                    disabled={recommendationTaskBusy || recommendationTaskUsersLoading || recommendationTaskUsers.length === 0}
                  >
                    {recommendationTaskBusy ? "Creating..." : "Create Task"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function MetricCard({ label, value, hint, accent = "default", compact = false }) {
  return (
    <div
      style={{
        ...S.noteMetricCard,
        ...(compact ? S.noteMetricCardCompact : {}),
        ...(accent === "alert" ? S.noteMetricCardAlert : {}),
      }}
    >
      <span style={{ ...S.noteMetricLabel, ...(compact ? S.noteMetricLabelCompact : {}) }}>{label}</span>
      <strong style={{ ...S.noteMetricValue, ...(compact ? S.noteMetricValueCompact : {}) }}>{value}</strong>
      {hint ? <span style={S.noteMetricHint}>{hint}</span> : null}
    </div>
  );
}


// eslint-disable-next-line no-unused-vars
function ContactCard({ contact, profileId, authHeaders, canCopyPhone = false, canCopyEmail = false }) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const type = String(contact.contact_type || contact.type || "contact").toLowerCase();
  const isEmail = type === "email";
  const displayValue = getContactDisplayValue(contact);
  const canCopy = isEmail
    ? canCopyEmail
    : canCopyPhone && ["mobile", "phone", "whatsapp", "parent_mobile", "landline"].includes(type);

  const copyValue = async () => {
    if (!displayValue) return;

    try {
      await API.post(
        `/profiles/${profileId}/contact-access`,
        {
          kind: isEmail ? "email" : "phone",
          action: "copy",
          contact_type: type,
        },
        authHeaders
      );
    } catch (err) {
      alert(err?.response?.data?.error || "Copy is restricted for this contact.");
      return;
    }

    const ok = await copyToClipboard(displayValue);

    if (ok) {
      setCopied(true);
      setCopyFailed(false);
      setTimeout(() => setCopied(false), 1300);
    } else {
      setCopyFailed(true);
      setTimeout(() => setCopyFailed(false), 2200);
    }
  };

  return (
    <SubCard>
      <div style={S.contactCardTop}>
        <div style={{ minWidth: 0 }}>
          <div style={S.subCardTitle}>
            {labelize(contact.contact_type || contact.type || "Contact")}
            {contact.is_primary && <span style={S.highestBadge}>Primary</span>}
            {contact.is_verified && <span style={S.verifiedBadge}>Verified</span>}
          </div>
          <div style={S.subCardSub}>
            {[displayValue, contact.contact_person_name, labelize(contact.contact_person_relation)]
              .filter(Boolean)
              .join(" · ")}
          </div>
          {copyFailed && (
            <div style={S.copyHint}>
              Copy blocked by browser. Select and copy manually: {displayValue}
            </div>
          )}
        </div>

        {canCopy && displayValue && (
          <button style={S.copyBtn} onClick={copyValue} type="button">
            {copied ? "Copied" : isEmail ? "Copy Email" : "Copy Phone"}
          </button>
        )}
      </div>
    </SubCard>
  );
}

// eslint-disable-next-line no-unused-vars
function RelativeCard({ relative, showFamilySide = false }) {
  const occupationRows = getRelativeOccupations(relative);
  const primaryOccupation = occupationRows.find((occupation) => occupation.is_primary) || occupationRows[0] || null;

  return (
    <SubCard>
      <div style={S.relativeTop}>
        <div>
          <div style={S.subCardTitle}>
            {relative.full_name ||
              [relative.first_name, relative.middle_name, relative.last_name].filter(Boolean).join(" ") ||
              "Unnamed"}
          </div>
          <div style={S.subCardSub}>
            {[
              labelize(relative.relation_type),
              showFamilySide ? getRelativeFamilySideLabel(relative.family_side) : "",
              relative.sakha_name || "",
              primaryOccupation?.occupation_type || "",
              primaryOccupation?.occupation_details || "",
              primaryOccupation?.company_name || "",
              primaryOccupation?.work_profile || "",
              shouldShowRelativeMaritalStatus(relative) && relative.marital_status ? labelize(relative.marital_status) : "",
              shouldShowRelativeMaritalStatus(relative) && isRelativeMarriedStatus(relative.marital_status) && relative.partner_name ? `${getRelativePartnerNameLabel(relative)}: ${relative.partner_name}` : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
          {(relative.phone || relative.email || relative.place) && (
            <div style={S.relativeContact}>
              {[
                relative.phone ? `Phone: ${relative.phone}` : "",
                relative.email ? `Email: ${relative.email}` : "",
                relative.place ? `Place: ${relative.place}` : "",
              ].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
        <div style={S.relativeBadgeColumn}>
          {shouldShowRelativeMaritalStatus(relative) && relative.is_future_prospect && isRelativeUnmarriedStatus(relative.marital_status) && <span style={S.futureBadge}>Future Prospect</span>}
          {relative.is_deceased && <span style={S.dangerBadge}>Deceased</span>}
        </div>
      </div>

      {occupationRows.length > 1 && (
        <div style={S.relativeEduList}>
          {occupationRows.map((occupation, occupationIndex) => (
            <span key={occupation.id || occupationIndex} style={S.relativeEduChip}>
              {formatRelativeOccupationSummary(occupation)}
            </span>
          ))}
        </div>
      )}

      {safeArray(relative.education).length > 0 && (
        <div style={S.relativeEduList}>
          {relative.education.map((edu, eduIndex) => (
            <span key={edu.id || eduIndex} style={S.relativeEduChip}>
              {[edu.degree_name, edu.education_description].filter(Boolean).join(" · ")}
            </span>
          ))}
        </div>
      )}
    </SubCard>
  );
}

function RelativeOverviewCard({ relative, showFamilySide = false }) {
  const occupationRows = getRelativeOccupations(relative);
  const primaryOccupation = occupationRows.find((occupation) => occupation.is_primary) || occupationRows[0] || null;
  const showMaritalStatus = shouldShowRelativeMaritalStatus(relative);
  const isUnmarriedRelative = showMaritalStatus && isRelativeUnmarriedStatus(relative.marital_status);
  const isMarriedRelative = showMaritalStatus && isRelativeMarriedStatus(relative.marital_status);
  const relativeBadges = [
    isUnmarriedRelative && relative.is_future_prospect ? "Future Prospect" : "",
    relative.is_deceased ? "Deceased" : "",
  ].filter(Boolean);
  const additionalOccupations = occupationRows
    .map((occupation) => formatRelativeOccupationSummary(occupation))
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(" | ");
  const educationSummary = safeArray(relative.education)
    .map((edu) => [edu.degree_name, edu.education_description].filter(Boolean).join(" · "))
    .filter(Boolean)
    .join(" | ");

  const fields = [
    { label: "Relation", value: labelize(relative.relation_type) },
    { label: "Family Side", value: showFamilySide ? getRelativeFamilySideLabel(relative.family_side) : "" },
    { label: "Sakha", value: relative.sakha_name || "" },
    { label: "Occupation Type", value: primaryOccupation?.occupation_type || "" },
    { label: "Occupation Details", value: primaryOccupation?.occupation_details || "" },
    { label: "Company", value: primaryOccupation?.company_name || "" },
    { label: "Work Profile", value: primaryOccupation?.work_profile || "" },
    {
      label: "Marital Status",
      value: showMaritalStatus && relative.marital_status
        ? labelize(relative.marital_status)
        : "",
    },
    {
      label: getRelativePartnerNameLabel(relative),
      value:
        isMarriedRelative &&
        relative.partner_name
          ? relative.partner_name
          : "",
    },
    {
      label: "DOB",
      value:
        isUnmarriedRelative && relative.is_future_prospect && relative.future_prospect_birth_date
          ? formatDate(relative.future_prospect_birth_date)
          : "",
    },
    {
      label: "Eligible Year",
      value:
        isUnmarriedRelative && relative.is_future_prospect && relative.future_prospect_eligible_year
          ? String(relative.future_prospect_eligible_year)
          : "",
    },
    { label: "Phone", value: relative.phone || "" },
    { label: "Email", value: relative.email || "" },
    { label: "Place", value: relative.place || "" },
    { label: "Education", value: educationSummary },
    { label: "Occupations", value: occupationRows.length > 1 ? additionalOccupations : "" },
  ].filter((item) => hasOverviewValue(item?.value));

  return (
    <div style={S.familyOverviewCard}>
      <div style={S.familyOverviewHeader}>
        <div style={S.familyOverviewName}>
          {relative.full_name ||
            [relative.first_name, relative.middle_name, relative.last_name].filter(Boolean).join(" ") ||
            "Unnamed"}
        </div>
        {relativeBadges.length > 0 ? (
          <div style={S.familyOverviewBadgeRow}>
            {relativeBadges.map((badge) => (
              <span key={`${relative.id || relative.relation_type}-${badge}`} style={S.familyOverviewBadge}>
                {badge}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <div style={S.familyOverviewFields}>
        {fields.map((field) => (
          <div key={`${relative.id || relative.relation_type}-${field.label}`} style={S.familyOverviewField}>
            <span style={S.familyOverviewFieldLabel}>{field.label}</span>
            <span style={S.familyOverviewFieldValue}>{field.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AddressOverviewCard({ address, profile, lookupMaps }) {
  const fields = buildAddressOverviewFields(address, profile, lookupMaps);

  return (
    <div style={S.familyOverviewCard}>
      <div style={S.familyOverviewHeader}>
        <div style={S.familyOverviewName}>
          {labelize(address?.address_type || "Address")}
        </div>
      </div>
      <div style={S.familyOverviewFields}>
        {fields.map((field) => (
          <div key={`${address?.id || address?.address_type}-${field.label}`} style={S.familyOverviewField}>
            <span style={S.familyOverviewFieldLabel}>{field.label}</span>
            <span style={S.familyOverviewFieldValue}>{field.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryItem({ file, onOpen, isSettingPrimary, canSetPrimary = false }) {
  const [imgError, setImgError] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    if (isSettingPrimary) return;
    onOpen?.(file);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...S.galleryItem,
        ...(file.is_primary ? S.galleryItemPrimary : {}),
        ...S.galleryItemClickable,
        ...(isSettingPrimary ? S.galleryItemLoading : {}),
        ...(hovered && !isSettingPrimary
          ? { transform: "scale(1.03)", boxShadow: "0 12px 28px rgba(37,41,53,0.14)" }
          : {}),
      }}
      title={file.is_primary ? "View primary photo" : canSetPrimary ? "View photo and actions" : "View photo"}
      disabled={isSettingPrimary}
    >
      {!imgError ? (
        <img src={file.file_url} alt="" style={S.galleryImg} onError={() => setImgError(true)} />
      ) : (
        <div style={S.galleryImgFallback}>?</div>
      )}
      {file.is_primary && <span style={S.galleryBadge}>★ Primary</span>}
      {!file.is_primary && !isSettingPrimary && <span style={S.gallerySetBadge}>{canSetPrimary ? "View Photo" : "Preview"}</span>}
      {isSettingPrimary && <span style={S.galleryUpdatingBadge}>Updating...</span>}
    </button>
  );
}

function PhotoPreviewModal({
  file,
  canSetPrimary,
  isSettingPrimary,
  onClose,
  onSetPrimary,
  eyebrow = "Photo Preview",
  title = null,
  footerText = "",
}) {
  const [imgError, setImgError] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    setImgError(false);
    setZoomLevel(1);
  }, [file?.id]);

  if (typeof document === "undefined") return null;

  const zoomOutDisabled = zoomLevel <= 1;
  const zoomInDisabled = zoomLevel >= 3;

  return createPortal(
    <div
      style={S.photoPreviewBackdrop}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div style={S.photoPreviewModal}>
        <div style={S.photoPreviewHeader}>
          <div>
            <p style={S.overlayEyebrow}>{eyebrow}</p>
            <h2 style={S.photoPreviewTitle}>{title || (file.is_primary ? "Primary Photo" : "Profile Photo")}</h2>
          </div>
          <div style={S.photoPreviewToolbar}>
            <button
              type="button"
              style={{
                ...S.photoZoomButton,
                ...(zoomOutDisabled ? S.photoZoomButtonDisabled : {}),
              }}
              onClick={() => setZoomLevel((current) => Math.max(1, Number((current - 0.25).toFixed(2))))}
              disabled={zoomOutDisabled}
            >
              -
            </button>
            <button
              type="button"
              style={S.photoZoomResetButton}
              onClick={() => setZoomLevel(1)}
            >
              Reset
            </button>
            <button
              type="button"
              style={{
                ...S.photoZoomButton,
                ...(zoomInDisabled ? S.photoZoomButtonDisabled : {}),
              }}
              onClick={() => setZoomLevel((current) => Math.min(3, Number((current + 0.25).toFixed(2))))}
              disabled={zoomInDisabled}
            >
              +
            </button>
            <button type="button" style={S.photoPreviewCloseButton} onClick={onClose}>x</button>
          </div>
        </div>

        <div style={S.photoPreviewBody}>
          {!imgError ? (
            <div style={S.photoPreviewViewport}>
              <img
                src={file.file_url}
                alt="Profile"
                style={{
                  ...S.photoPreviewImage,
                  transform: `scale(${zoomLevel})`,
                }}
                onError={() => setImgError(true)}
              />
            </div>
          ) : (
            <div style={S.photoPreviewFallback}>Photo preview unavailable.</div>
          )}
        </div>

        <div style={S.photoPreviewFooter}>
          <div style={S.photoPreviewMeta}>
            {footerText || (
              file.is_primary
                ? `This photo is currently primary. Zoom ${Math.round(zoomLevel * 100)}%.`
                : canSetPrimary
                  ? `You can make this the primary profile photo. Zoom ${Math.round(zoomLevel * 100)}%.`
                  : `Zoom ${Math.round(zoomLevel * 100)}%.`
            )}
          </div>
          <div style={S.photoPreviewActions}>
            <button type="button" style={S.overlayCancel} onClick={onClose}>Close</button>
            {canSetPrimary && !file.is_primary && (
              <button
                type="button"
                style={{
                  ...S.overlaySubmit,
                  ...(isSettingPrimary ? S.overlaySubmitDisabled : {}),
                }}
                onClick={() => onSetPrimary?.(file.id)}
                disabled={isSettingPrimary}
              >
                {isSettingPrimary ? "Setting..." : "Set as Primary"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ProfilePhoto({ src, initials, avatarColor }) {
  const [imgError, setImgError] = useState(false);
  const showImage = src && !imgError;

  return showImage ? (
    <img src={src} alt="" style={S.photo} onError={() => setImgError(true)} />
  ) : (
    <div style={{ ...S.avatar, background: avatarColor }}>
      <span style={S.avatarInitials}>{initials || "?"}</span>
    </div>
  );
}

function Pill({ children }) {
  return <span style={S.pill}>{children}</span>;
}

function AlertBellIcon() {
  return (
    <svg viewBox="0 0 64 64" aria-hidden="true" style={S.heroAlertBellIcon}>
      <path
        d="M32 12c-9.4 0-17 7.6-17 17v8.5c0 2.2-.8 4.4-2.3 6l-3.7 4.1h46l-3.7-4.1c-1.5-1.6-2.3-3.8-2.3-6V29c0-9.4-7.6-17-17-17Z"
        fill="currentColor"
      />
      <path d="M25 52c1.2 3.8 3.8 6 7 6s5.8-2.2 7-6H25Z" fill="currentColor" />
      <path
        d="M46 18c3 2.2 5 5.5 5.7 9.3"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        opacity="0.45"
      />
      <path
        d="M50.5 12.5c4.1 3.1 6.8 7.8 7.5 13"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        opacity="0.25"
      />
    </svg>
  );
}

function AlertsOverlay({ alerts = [], onClose }) {
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      style={S.overlayBackdrop}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div style={S.alertsOverlayCard}>
        <div style={S.overlayHeader}>
          <div>
            <p style={S.overlayEyebrow}>Profile Alerts</p>
            <h2 style={S.overlayTitle}>Open Alerts</h2>
            <p style={S.alertsOverlaySub}>
              {alerts.length > 0
                ? `${alerts.length} active alert${alerts.length !== 1 ? "s" : ""} need attention.`
                : "There are no open alerts for this profile."}
            </p>
          </div>
          <button type="button" style={S.overlayClose} onClick={onClose}>x</button>
        </div>

        <div style={S.alertsOverlayBody}>
          {alerts.length > 0 ? (
            <div style={S.alertsOverlayList}>
              {alerts.map((alert, index) => (
                <AlertCard key={alert.id || index} alert={alert} />
              ))}
            </div>
          ) : (
            <p style={S.completeText}>No open alerts right now.</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// eslint-disable-next-line no-unused-vars
function SignalCard({ label, title, detail, accent }) {
  return (
    <div style={{ ...S.signalCard, ...(S[`signal_${accent}`] || {}) }}>
      <span style={S.signalLabel}>{label}</span>
      <b style={S.signalTitle}>{title || "Not added"}</b>
      {detail && <p style={S.signalDetail}>{detail}</p>}
    </div>
  );
}

/* eslint-disable no-unreachable */
function CompletenessOverlay({ pct, sections, onClose }) {
  if (typeof document === "undefined") return null;

  const normalizedPct = Math.max(0, Math.min(Number(pct) || 0, 100));
  const status = getCompletenessStatusLabel(normalizedPct);

  return createPortal(
    <div
      style={S.overlayBackdrop}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div style={S.overlayCard}>
        <div style={S.overlayHeader}>
          <div>
            <p style={S.overlayEyebrow}>Profile Strength</p>
            <h2 style={S.overlayTitle}>{normalizedPct}% complete</h2>
            <p style={S.completenessOverlaySub}>
              {status}. Add the missing fields below to improve the profile score.
            </p>
          </div>
          <button type="button" style={S.overlayClose} onClick={onClose}>x</button>
        </div>

        <div style={S.completenessOverlayBody}>
          <div style={S.progressTrack}>
            <div style={{ ...S.progressFill, width: `${normalizedPct}%` }} />
          </div>

          {sections.length > 0 ? (
            <div style={S.completenessSectionList}>
              {sections.map((section) => (
                <div key={section.section} style={S.completenessSectionCard}>
                  <div style={S.completenessSectionTop}>
                    <div>
                      <h3 style={S.completenessSectionTitle}>{section.section}</h3>
                      {section.note ? <p style={S.completenessSectionNote}>{section.note}</p> : null}
                    </div>
                    {section.action ? (
                      <span style={S.completenessSectionAction}>{section.action}</span>
                    ) : null}
                  </div>

                  <div style={S.completenessFieldList}>
                    {section.fields.map((field) => (
                      <span key={`${section.section}-${field}`} style={S.completenessFieldChip}>
                        {field}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={S.completeText}>Profile looks complete and ready for matching conversations.</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <div style={S.completenessPanel}>
      <div style={S.completenessPanelTop}>
        <div>
          <p style={S.panelEyebrow}>Profile Strength</p>
          <h3 style={S.panelTitle}>{normalizedPct}% complete · {status}</h3>
          <p style={S.panelSub}>
            This score is based on biodata completeness only. Payment/package status is shown separately.
          </p>
        </div>
      </div>

      <div style={S.progressTrack}>
        <div style={{ ...S.progressFill, width: `${normalizedPct}%` }} />
      </div>

      {sections.length > 0 ? (
        <div style={S.missingGrid}>
          {sections.slice(0, 4).map((item, index) => (
            <div key={index} style={S.missingItem}>
              <span style={S.missingNumber}>{index + 1}</span>
              <div>
                <b>{item.section || "Missing detail"}</b>
                <p>{item.note || "Add the required detail to improve profile quality."}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p style={S.completeText}>Profile looks complete and ready for matching conversations.</p>
      )}
    </div>
  );
}

function SectionHead({ children }) {
  return <p style={S.sectionHead}>{children}</p>;
}
/* eslint-enable no-unreachable */

function ProfileInfoSection({ title, items = [] }) {
  const visibleItems = safeArray(items).filter((item) => hasOverviewValue(item?.value));
  if (!visibleItems.length) return null;

  return (
    <section style={S.overviewSection}>
      <div style={S.overviewSectionHeader}>
        <h3 style={S.overviewSectionTitle}>{title}</h3>
      </div>
      <div style={S.overviewGrid} className="profile-overview-grid">
        {visibleItems.map((item) => (
          <ProfileInfoCard
            key={`${title}-${item.label}`}
            label={item.label}
            value={item.value}
            span={item.span}
          />
        ))}
      </div>
    </section>
  );
}

// eslint-disable-next-line no-unused-vars
function buildContactOverviewItems(contacts = []) {
  return safeArray(contacts)
    .map((contact, index) => {
      const displayValue = getContactDisplayValue(contact);
      const relationLabel = labelize(contact?.contact_person_relation || "");
      const personName = String(contact?.contact_person_name || "").trim();
      const labelParts = [
        labelize(contact?.contact_type || contact?.type || `Contact ${index + 1}`),
        contact?.is_primary ? "Primary" : "",
        contact?.is_verified ? "Verified" : "",
      ].filter(Boolean);
      const valueParts = [
        displayValue,
        personName,
        relationLabel,
      ].filter(Boolean);

      return {
        label: labelParts.join(" · "),
        value: valueParts.join(" · "),
      };
    })
    .filter((item) => hasOverviewValue(item?.value));
}

// eslint-disable-next-line no-unused-vars
function buildEducationDetailItems(educations = []) {
  return safeArray(educations)
    .map((edu, index) => {
      const statusParts = [
        edu.is_highest_degree ? "Highest" : "",
        String(edu.education_status || "").trim(),
      ].filter(Boolean);

      const fields = [
        { label: "Degree", value: edu.degree_name || edu.education_description || `Education ${index + 1}` },
        { label: "Category", value: edu.education_category },
        { label: "Status", value: statusParts.join(" · ") },
        { label: "Institution", value: edu.institution_name },
        { label: "Location", value: edu.education_location },
        { label: "Year", value: edu.passing_year },
      ].filter((item) => hasOverviewValue(item?.value));

      return fields.length ? { key: edu.id || `education-${index}`, fields } : null;
    })
    .filter(Boolean);
}

// eslint-disable-next-line no-unused-vars
function buildCareerDetailItems(occupations = []) {
  return safeArray(occupations)
    .map((occupation, index) => {
      const fields = [
        { label: "Career", value: occupation.occupation_details || occupation.occupation_type || `Occupation ${index + 1}` },
        { label: "Type", value: occupation.occupation_type },
        { label: "Status", value: occupation.is_primary ? "Primary" : "" },
        { label: "Company", value: occupation.company_name },
        { label: "Work Profile", value: occupation.work_profile },
        { label: "Income / Turnover", value: formatOccupationAmount(occupation) },
      ].filter((item) => hasOverviewValue(item?.value));

      return fields.length ? { key: occupation.id || `occupation-${index}`, fields } : null;
    })
    .filter(Boolean);
}

// eslint-disable-next-line no-unused-vars
function DetailColumnSection({ title, items = [], emptyText }) {
  const visibleItems = safeArray(items);

  return (
    <section style={S.educationCareerColumn}>
      <div style={S.overviewSectionHeader}>
        <h3 style={S.overviewSectionTitle}>{title}</h3>
      </div>
      {visibleItems.length > 0 ? (
        <div style={S.educationCareerColumnStack}>
          {visibleItems.map((item) => (
            <div key={item.key} style={S.educationCareerCard}>
              <div style={S.educationCareerFieldList}>
                {safeArray(item.fields).map((field) => (
                  <div key={`${item.key}-${field.label}`} style={S.educationCareerField}>
                    <span style={S.educationCareerFieldLabel}>{field.label}</span>
                    <span style={S.educationCareerFieldValue}>{field.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Empty>{emptyText}</Empty>
      )}
    </section>
  );
}

function buildEducationParameterItems(educations = [], lookupMaps = {}) {
  return safeArray(educations)
    .map((edu, index) => {
      const educationLocation = formatEducationLocation(edu, lookupMaps);
      const educationStatus = String(edu.education_status || "").trim().toLowerCase();
      const fields = [
        { label: "Category", value: edu.education_category },
        { label: "Institution", value: edu.institution_name },
        { label: "Location", value: educationLocation },
        { label: educationStatus === "pursuing" ? "Expected Year" : "Passing Year", value: edu.passing_year },
      ].filter((item) => hasOverviewValue(item?.value));

      return fields.length || hasOverviewValue(edu.degree_name) || hasOverviewValue(edu.education_description)
        ? {
            key: edu.id || `education-parameter-${index}`,
            title: edu.degree_name || edu.education_description || `Education ${index + 1}`,
            summary:
              edu.degree_name && edu.education_description && edu.degree_name !== edu.education_description
                ? edu.education_description
                : "",
            badges: [
              String(edu.education_status || "").trim(),
              edu.is_highest_degree ? "Highest Degree" : "",
            ].filter(Boolean),
            fields,
          }
        : null;
    })
    .filter(Boolean);
}

function buildCareerParameterItems(occupations = []) {
  return safeArray(occupations)
    .map((occupation, index) => {
      const fields = [
        { label: "Company", value: occupation.company_name },
        { label: "Work Profile", value: occupation.work_profile },
        { label: "Income / Turnover", value: formatOccupationAmount(occupation) },
      ].filter((item) => hasOverviewValue(item?.value));

      return fields.length || hasOverviewValue(occupation.occupation_details) || hasOverviewValue(occupation.occupation_type)
        ? {
            key: occupation.id || `career-parameter-${index}`,
            title: occupation.occupation_type || occupation.occupation_details || `Occupation ${index + 1}`,
            summary:
              occupation.occupation_details &&
              occupation.occupation_details !== occupation.occupation_type
                ? occupation.occupation_details
                : "",
            summaryInline: true,
            badges: [occupation.is_primary ? "Primary" : ""].filter(Boolean),
            fields,
          }
        : null;
    })
    .filter(Boolean);
}

function EducationCareerColumnSection({ title, items = [], emptyText }) {
  const visibleItems = safeArray(items);

  return (
    <section style={S.educationCareerColumn}>
      <div style={S.overviewSectionHeader}>
        <h3 style={S.overviewSectionTitle}>{title}</h3>
      </div>
      {visibleItems.length > 0 ? (
        <div style={S.educationCareerColumnStack}>
          {visibleItems.map((item) => (
            <div key={item.key} style={S.educationCareerCard}>
              {(hasOverviewValue(item.title) || safeArray(item.badges).length > 0) && (
                <div style={S.educationCareerCardHeader}>
                  {hasOverviewValue(item.title) ? (
                    <div style={S.educationCareerCardTitle}>{item.title}</div>
                  ) : null}
                  {safeArray(item.badges).length > 0 ? (
                    <div style={S.educationCareerBadgeRow}>
                      {item.badges.map((badge) => (
                        <span key={`${item.key}-${badge}`} style={S.educationCareerBadge}>
                          {badge}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
              {hasOverviewValue(item.summary) ? (
                <div style={item.summaryInline ? S.educationCareerInlineSummary : S.educationCareerSummary}>
                  {item.summary}
                </div>
              ) : null}
              <div style={S.educationCareerFieldList}>
                {safeArray(item.fields).map((field) => (
                  <div key={`${item.key}-${field.label}`} style={S.educationCareerField}>
                    <span style={S.educationCareerFieldLabel}>{field.label}</span>
                    <span style={S.educationCareerFieldValue}>{field.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Empty>{emptyText}</Empty>
      )}
    </section>
  );
}

function formatEducationLocation(education, lookupMaps = {}) {
  if (!education) return "";

  const cityLookup = lookupLocationDetails(
    lookupMaps.locations,
    education.education_city_id || education.city_id
  );

  return [
    education.education_city,
    cityLookup.city,
    education.education_location,
  ]
    .filter(Boolean)
    .filter((value, index, values) => values.findIndex((item) => String(item).trim().toLowerCase() === String(value).trim().toLowerCase()) === index)
    .join(" · ");
}

function ContactOverviewSection({
  contacts = [],
  profileId,
  authHeaders,
  canCopyPhone = false,
  canCopyEmail = false,
}) {
  const visibleContacts = safeArray(contacts)
    .map((contact, index) => {
      const displayValue = getContactDisplayValue(contact);
      const type = String(contact?.contact_type || contact?.type || "contact").toLowerCase();
      const isEmail = type === "email";
      const canCopy = isEmail
        ? canCopyEmail && displayValue
        : canCopyPhone && displayValue && ["mobile", "phone", "whatsapp", "parent_mobile", "landline"].includes(type);
      const personName = String(contact?.contact_person_name || "").trim();
      const relationLabel = labelize(contact?.contact_person_relation || "");
      const statusParts = [
        contact?.is_primary ? "Primary" : "",
        contact?.is_verified ? "Verified" : "",
      ].filter(Boolean);

      const fields = [
        { label: isEmail ? "Mail ID" : "Number", value: displayValue },
        { label: "Name", value: personName },
        { label: "Relation", value: relationLabel },
      ].filter((item) => hasOverviewValue(item?.value));

      if (fields.length === 0) return null;

      return {
        key: contact?.id || `${contact?.contact_type || contact?.type || "contact"}-${index}`,
        title: labelize(contact?.contact_type || contact?.type || `Contact ${index + 1}`),
        status: statusParts.join(" · "),
        fields,
        canCopy,
        copyLabel: isEmail ? "Copy Email" : "Copy Phone",
        contact,
      };
    })
    .filter(Boolean);

  if (!visibleContacts.length) return null;

  return (
    <section style={S.overviewSection}>
      <div style={S.overviewSectionHeader}>
        <h3 style={S.overviewSectionTitle}>Contact Details</h3>
      </div>
      <div style={S.contactOverviewGrid} className="profile-overview-grid">
        {visibleContacts.map((contact) => (
          <div key={contact.key} style={S.contactOverviewCard}>
            <div style={S.contactOverviewHeader}>
              <div style={S.contactOverviewTitle}>{contact.title}</div>
              {contact.status ? <div style={S.contactOverviewStatus}>{contact.status}</div> : null}
            </div>
            <div style={S.contactOverviewFieldList}>
              {contact.fields.map((field) => (
                <div key={`${contact.key}-${field.label}`} style={S.contactOverviewField}>
                  <span style={S.contactOverviewFieldLabel}>{field.label}</span>
                  <span style={S.contactOverviewFieldValue}>{field.value}</span>
                </div>
              ))}
            </div>
            {contact.canCopy ? (
              <ContactOverviewCopyButton
                contact={contact.contact}
                profileId={profileId}
                authHeaders={authHeaders}
                label={contact.copyLabel}
              />
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function ContactOverviewCopyButton({ contact, profileId, authHeaders, label }) {
  const [copied, setCopied] = useState(false);
  const type = String(contact?.contact_type || contact?.type || "contact").toLowerCase();
  const isEmail = type === "email";
  const displayValue = getContactDisplayValue(contact);

  const handleCopy = async () => {
    if (!displayValue) return;

    try {
      await API.post(
        `/profiles/${profileId}/contact-access`,
        {
          kind: isEmail ? "email" : "phone",
          action: "copy",
          contact_type: type,
        },
        authHeaders
      );
    } catch (err) {
      alert(err?.response?.data?.error || "Copy is restricted for this contact.");
      return;
    }

    const ok = await copyToClipboard(displayValue);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1300);
  };

  return (
    <button type="button" style={S.contactOverviewCopyBtn} onClick={handleCopy}>
      {copied ? "Copied" : label}
    </button>
  );
}

function ProfileInfoCard({ label, value, span = "default" }) {
  if (!hasOverviewValue(value)) return null;

  return (
    <div
      style={{
        ...S.overviewItem,
        ...(span === "wide" ? S.overviewItemWide : {}),
      }}
      className={span === "wide" ? "profile-overview-item-wide" : ""}
    >
      <span style={S.overviewItemLabel}>{label}</span>
      <span style={S.overviewItemValue}>{value}</span>
    </div>
  );
}

function hasOverviewValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function SubCard({ children }) {
  return <div style={S.subCard}>{children}</div>;
}

function Empty({ children }) {
  return <p style={S.emptyText}>{children}</p>;
}

function OverviewDetailCard({ title, badges = [], fields = [], wide = false }) {
  const visibleFields = safeArray(fields).filter((field) => hasOverviewValue(field?.value));
  if (!hasOverviewValue(title) && visibleFields.length === 0) return null;

  return (
    <div
      style={{
        ...S.familyOverviewCard,
        ...(wide ? { gridColumn: "1 / -1" } : {}),
      }}
    >
      <div style={S.familyOverviewHeader}>
        <div style={S.familyOverviewName}>{title}</div>
        {badges.length > 0 ? (
          <div style={S.familyOverviewBadgeRow}>
            {badges.map((badge) => (
              <span key={`${title}-${badge}`} style={S.familyOverviewBadge}>
                {badge}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      {visibleFields.length > 0 ? (
        <div style={S.familyOverviewFields}>
          {visibleFields.map((field) => (
            <div key={`${title}-${field.label}`} style={S.familyOverviewField}>
              <span style={S.familyOverviewFieldLabel}>{field.label}</span>
              <span style={S.familyOverviewFieldValue}>{field.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function AlertCard({ alert }) {
  const status = normalizeAlertStatus(alert?.status);
  const severity = normalizeAlertSeverity(alert?.severity);
  const notePreview = summarizeText(alert?.note_content, 144);

  return (
    <OverviewDetailCard
      title={alert?.message || labelize(alert?.alert_type) || "Alert"}
      badges={[
        labelize(status || "open"),
        severity ? labelize(severity) : "",
      ].filter(Boolean)}
      fields={[
        { label: "Alert Type", value: labelize(alert?.alert_type || "alert") },
        { label: "Created", value: alert?.created_at ? formatDateTime(alert.created_at) : "" },
        { label: "Resolved", value: alert?.resolved_at ? formatDateTime(alert.resolved_at) : "" },
        { label: "Due", value: alert?.follow_up_at ? formatDateTime(alert.follow_up_at) : "" },
        { label: "Linked To", value: alert?.related_entity_type ? labelize(alert.related_entity_type) : "" },
        { label: "Note", value: notePreview },
      ]}
      wide
    />
  );
}

function InfoItem({ label, value }) {
  if (!value && value !== 0) return null;

  return (
    <div style={S.infoItem}>
      <span style={S.infoLabel}>{label}</span>
      <span style={S.infoValue}>{value}</span>
    </div>
  );
}

function PreferenceInfoItem({ label, value }) {
  if (!value && value !== 0) return null;

  return (
    <div style={S.familyOverviewCard}>
      <div style={S.familyOverviewHeader}>
        <div style={S.familyOverviewName}>{label}</div>
      </div>
      <div style={S.familyOverviewFields}>
        <div style={S.familyOverviewField}>
          <span style={S.familyOverviewFieldValue}>{value}</span>
        </div>
      </div>
    </div>
  );
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeAlertStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized || "open";
}

function normalizeAlertSeverity(value) {
  return String(value || "").trim().toLowerCase();
}

function isServiceOccupationTypeLabel(value) {
  return /\bservice\b/i.test(String(value || "").trim());
}

function occupationHasContent(occupation) {
  return Boolean(
    occupation?.occupation_type_id ||
    occupation?.occupation_type ||
    occupation?.company_name ||
    occupation?.occupation_details ||
    occupation?.work_profile ||
    occupation?.annual_income_raw ||
    occupation?.annual_income_amount ||
    occupation?.annual_turnover_amount
  );
}

function getProfileOccupations(profile) {
  const occupationRows = safeArray(profile?.occupations);
  if (occupationRows.length) {
    const profileAnnualIncomeRaw = String(profile?.annual_income_raw || "").trim();
    const hasPrimaryOccupation = occupationRows.some((occupation) => occupation?.is_primary);
    return occupationRows.map((occupation, index) => {
      const isPrimaryOccupation = occupation?.is_primary || (!hasPrimaryOccupation && index === 0);
      return {
        ...occupation,
        annual_income_raw:
          String(occupation?.annual_income_raw || "").trim() ||
          (isPrimaryOccupation ? profileAnnualIncomeRaw : ""),
      };
    });
  }

  const fallbackOccupation = {
    occupation_type: profile?.occupation_type || "",
    company_name: profile?.company_name || "",
    occupation_details: profile?.current_occupation_details || "",
    work_profile: profile?.work_profile || "",
    annual_income_raw: profile?.annual_income_raw || "",
    annual_income_amount: profile?.annual_income_amount ?? null,
    annual_income_currency_code: normalizeOccupationCurrencyCode(
      profile?.annual_income_currency_code,
      profile?.annual_income_amount
    ),
    annual_turnover_amount: profile?.annual_turnover_amount ?? null,
    annual_turnover_currency_code: normalizeOccupationCurrencyCode(
      profile?.annual_turnover_currency_code,
      profile?.annual_turnover_amount
    ),
    is_primary: true,
  };

  return [fallbackOccupation].filter((occupation) =>
    [
      occupation.occupation_type,
      occupation.company_name,
      occupation.occupation_details,
      occupation.work_profile,
      occupation.annual_income_raw,
      occupation.annual_income_amount,
      occupation.annual_turnover_amount,
    ].some((value) => value !== null && value !== undefined && String(value).trim() !== "")
  );
}

// eslint-disable-next-line no-unused-vars
function formatOccupationAnnualIncomeRaw(occupation) {
  const value = String(occupation?.annual_income_raw || "").trim();
  return value || "";
}

function formatOccupationAmount(occupation) {
  if (!occupation) return "";
  const amount = isServiceOccupationTypeLabel(occupation.occupation_type)
    ? occupation.annual_income_amount
    : occupation.annual_turnover_amount;
  if (amount === null || amount === undefined || amount === "") return "";

  const label = isServiceOccupationTypeLabel(occupation.occupation_type)
    ? "Annual income"
    : "Annual turnover";
  const currencyCode = isServiceOccupationTypeLabel(occupation.occupation_type)
    ? normalizeOccupationCurrencyCode(occupation.annual_income_currency_code, amount)
    : normalizeOccupationCurrencyCode(occupation.annual_turnover_currency_code, amount);
  const formattedAmount = formatCurrencyAmount(amount, currencyCode || "INR");
  const currencyName = getCurrencyName(currencyCode || "INR");
  return `${label}: ${formattedAmount}${currencyName ? ` (${currencyName})` : ""}`;
}

function getRelativeOccupations(relative) {
  const occupationRows = safeArray(relative?.occupations).filter(occupationHasContent);
  if (occupationRows.length) return occupationRows;

  const fallbackOccupation = {
    occupation_type: relative?.occupation_type || "",
    company_name: relative?.company_name || "",
    occupation_details: relative?.occupation_details || "",
    work_profile: relative?.work_profile || "",
    annual_income_amount: relative?.annual_income_amount ?? null,
    annual_income_currency_code: normalizeOccupationCurrencyCode(
      relative?.annual_income_currency_code,
      relative?.annual_income_amount
    ),
    annual_turnover_amount: relative?.annual_turnover_amount ?? null,
    annual_turnover_currency_code: normalizeOccupationCurrencyCode(
      relative?.annual_turnover_currency_code,
      relative?.annual_turnover_amount
    ),
    is_primary: true,
  };

  return occupationHasContent(fallbackOccupation) ? [fallbackOccupation] : [];
}

function formatRelativeOccupationSummary(occupation) {
  return [
    occupation?.is_primary ? "Primary" : "",
    occupation?.occupation_type || "",
    occupation?.occupation_details || "",
    occupation?.company_name || "",
    occupation?.work_profile || "",
    formatOccupationAmount(occupation),
  ]
    .filter(Boolean)
    .join(" · ");
}

function getAge(dateValue) {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor((new Date() - date) / (365.25 * 86400000));
}

function formatDate(dateValue) {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function toTime(value) {
  return formatTime12Hour(value);
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return "—";

  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (Number.isNaN(diff)) return "—";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function labelize(value) {
  if (!value) return "";
  return String(value)
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatRecommendationStatusLabel(value) {
  return labelize(value);
}

function formatActorName(name, email, fallback = "Admin") {
  const normalizedName = String(name || "").trim();
  if (normalizedName) return normalizedName;

  const localPart = String(email || "").split("@")[0].trim();
  if (!localPart) return fallback;

  return localPart
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function isRelativeUnmarriedStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "unmarried" || normalized === "single";
}

function isRelativeMarriedStatus(value) {
  return String(value || "").trim().toLowerCase() === "married";
}

function shouldShowRelativeMaritalStatus(relative) {
  const normalizedRelationType = normalizeRelationType(relative?.relation_type);
  return normalizedRelationType === "sister" || normalizedRelationType === "brother" || normalizedRelationType === "sibling";
}

function getRelativePartnerNameLabel(relative) {
  const normalizedRelationType = normalizeRelationType(relative?.relation_type);
  if (normalizedRelationType === "sister") return "Husband Name";
  if (normalizedRelationType === "brother") return "Wife Name";
  if (normalizedRelationType === "sibling") {
    const normalizedGender = String(relative?.gender || "").trim().toLowerCase();
    if (normalizedGender === "female") return "Husband Name";
    if (normalizedGender === "male") return "Wife Name";
  }
  return "Spouse Name";
}

function getRelativeFamilySideLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  if (normalized === "none" || normalized === "direct") return "Direct";
  return labelize(value);
}

function getProfileDisplayName(profile) {
  if (!profile || typeof profile !== "object") return "";
  const parts = [
    profile.full_name,
    [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(" "),
  ].filter(Boolean);
  return parts[0] || "";
}

function buildProfileReferenceLabel(profile) {
  if (!profile || typeof profile !== "object") return "Unknown profile";
  const name = getProfileDisplayName(profile);
  const profileNo = getProfileNumberDisplay(profile) || profile.id || profile.profile_id;
  if (name && profileNo) return `${name} (#${profileNo})`;
  if (name) return name;
  if (profileNo) return `Profile #${profileNo}`;
  return "Unknown profile";
}

function buildRecommendationContactSideOptions(sourceProfile, targetProfile) {
  const options = [];
  const sourceProfileId = sourceProfile?.id || sourceProfile?.profile_id;
  const targetProfileId = targetProfile?.id || targetProfile?.profile_id;

  if (sourceProfileId) {
    options.push({
      value: String(sourceProfileId),
      label: buildProfileReferenceLabel(sourceProfile),
    });
  }

  if (targetProfileId) {
    options.push({
      value: String(targetProfileId),
      label: buildProfileReferenceLabel(targetProfile),
    });
  }

  options.push({
    value: "both",
    label: `${buildProfileReferenceLabel(sourceProfile)} + ${buildProfileReferenceLabel(targetProfile)}`,
  });

  return options;
}

function getRecommendationContactSideLabel(side, sourceProfile, targetProfile) {
  const rawSide = String(side || "").trim();
  const normalized = rawSide.toLowerCase();
  const sourceProfileId = String(sourceProfile?.id || sourceProfile?.profile_id || "").trim();
  const targetProfileId = String(targetProfile?.id || targetProfile?.profile_id || "").trim();
  if (!normalized) return "";
  if (normalized === "profile_1" || (sourceProfileId && rawSide === sourceProfileId)) {
    return buildProfileReferenceLabel(sourceProfile);
  }
  if (normalized === "profile_2" || (targetProfileId && rawSide === targetProfileId)) {
    return buildProfileReferenceLabel(targetProfile);
  }
  if (normalized === "both") {
    return `${buildProfileReferenceLabel(sourceProfile)} + ${buildProfileReferenceLabel(targetProfile)}`;
  }
  if (normalized === "internal") return "Admin Note (Internal)";
  return labelize(side);
}

function getProfileInitials(profile) {
  if (!profile || typeof profile !== "object") return "?";
  const first = String(profile.first_name || profile.full_name || profile.profile_no_display || profile[RAW_PROFILE_NUMBER_FIELD] || "").trim();
  const last = String(profile.last_name || "").trim();
  const initials = `${first[0] || ""}${last[0] || ""}`.trim().toUpperCase();
  return initials || "?";
}

function buildRecommendationResultMeta(profile) {
  if (!profile || typeof profile !== "object") return "";
  return [
    getProfileNumberDisplay(profile) ? `#${getProfileNumberDisplay(profile)}` : "",
    profile.gender ? labelize(profile.gender) : "",
    profile.location_display_cache || profile.location_display || [profile.city, profile.state, profile.country].filter(Boolean).join(", "),
  ]
    .filter(Boolean)
    .join(" | ");
}

function showChildrenFields(maritalStatus) {
  const normalized = String(maritalStatus || "").trim().toLowerCase();
  if (!normalized) return false;
  return normalized !== "single" && normalized !== "never married";
}

function normalizeRelationType(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function pickColor(str) {
  const palette = ["#D73C2E", "#B9343A", "#C7433F", "#A93338", "#D1483A", "#252935"];
  let hash = 0;
  for (const ch of str) hash = ch.charCodeAt(0) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
}

function getCompletenessPct(profile) {
  return profile?.completeness?.completeness_pct ?? profile?.completeness_pct_cache ?? 0;
}

function isCompletenessValueFilled(value) {
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function hasCompletenessLocationSelection(address = {}) {
  return [
    address?.country_id,
    address?.state_id,
    address?.district_id,
    address?.tehsil_id,
    address?.city_id,
    address?.village_id,
    address?.country,
    address?.state,
    address?.district,
    address?.tehsil,
    address?.city,
    address?.village,
  ].some(isCompletenessValueFilled);
}

function pickCompletenessLocationAddress(addresses = []) {
  const rows = safeArray(addresses);
  return (
    rows.find((address) => String(address?.address_type || "").trim().toLowerCase() === "current") ||
    rows.find((address) => String(address?.address_type || "").trim().toLowerCase() === "residential") ||
    rows.find(hasCompletenessLocationSelection) ||
    rows[0] ||
    null
  );
}

function hasAnyCompletenessPreferences(preferences) {
  if (!preferences) return false;

  return [
    preferences.preferred_age_gap_months,
    preferences.preferred_age_min,
    preferences.preferred_age_max,
    preferences.preferred_city_ids,
    preferences.non_preferred_city_ids,
    preferences.preferred_city_tier_ids,
    preferences.non_preferred_sakha_ids,
    preferences.preferred_education_category_ids,
    preferences.non_preferred_education_category_ids,
    preferences.preferred_degrees,
    preferences.non_preferred_degrees,
    preferences.preferred_occupation_types,
    preferences.non_preferred_occupation_types,
  ].some(isCompletenessValueFilled);
}

function getCompletenessStatusLabel(pct) {
  if (pct >= 90) return "Excellent";
  if (pct >= 75) return "Strong";
  if (pct >= 55) return "Needs attention";
  return "Incomplete";
}

function getCompletenessSectionFields(profile, section) {
  const normalizedSection = String(section || "").trim().toLowerCase();
  const educations = safeArray(profile?.educations);
  const contacts = safeArray(profile?.contacts);
  const relatives = safeArray(profile?.relatives);
  const media = safeArray(profile?.media || profile?.files);
  const preferences = profile?.preferences || null;
  const occupations = getProfileOccupations(profile);
  const primaryOccupation = occupations.find((item) => item?.is_primary) || occupations[0] || null;
  const currentAddress = pickCompletenessLocationAddress(profile?.addresses);
  const hasCurrentLocation =
    hasCompletenessLocationSelection(currentAddress) ||
    isCompletenessValueFilled(profile?.location_display_cache) ||
    isCompletenessValueFilled(profile?.city_id) ||
    isCompletenessValueFilled(profile?.tehsil_id) ||
    isCompletenessValueFilled(profile?.district_id) ||
    isCompletenessValueFilled(profile?.state_id) ||
    isCompletenessValueFilled(profile?.village_id);

  if (normalizedSection === "basic identity") {
    return [
      !isCompletenessValueFilled(profile?.first_name) ? "First Name" : "",
      !isCompletenessValueFilled(profile?.last_name) ? "Last Name" : "",
      !isCompletenessValueFilled(profile?.gender) ? "Gender" : "",
      !isCompletenessValueFilled(profile?.birth_date || profile?.dob) ? "Date of Birth" : "",
    ].filter(Boolean);
  }

  if (normalizedSection === "personal details") {
    return [
      !(isCompletenessValueFilled(profile?.height_cm) || isCompletenessValueFilled(profile?.height_display)) ? "Height" : "",
      !isCompletenessValueFilled(profile?.marital_status) ? "Marital Status" : "",
      !isCompletenessValueFilled(profile?.complexion) ? "Complexion" : "",
      !isCompletenessValueFilled(profile?.about_me) ? "About Me" : "",
    ].filter(Boolean);
  }

  if (normalizedSection === "community") {
    return [
      !(isCompletenessValueFilled(profile?.caste_id) || isCompletenessValueFilled(profile?.caste) || isCompletenessValueFilled(profile?.caste_name_cache)) ? "Caste" : "",
      !(isCompletenessValueFilled(profile?.sakha_id) || isCompletenessValueFilled(profile?.sakha) || isCompletenessValueFilled(profile?.sakha_name_cache)) ? "Sakha" : "",
      !(isCompletenessValueFilled(profile?.sampraday_id) || isCompletenessValueFilled(profile?.sampraday_name)) ? "Sampraday" : "",
    ].filter(Boolean);
  }

  if (normalizedSection === "location") {
    return [
      !hasCurrentLocation ? "Current Location" : "",
      !(isCompletenessValueFilled(profile?.birth_place_city_id) || isCompletenessValueFilled(profile?.birth_place)) ? "Birth Place" : "",
    ].filter(Boolean);
  }

  if (normalizedSection === "education") {
    return [
      educations.length === 0 ? "Education Record" : "",
      !educations.some((item) => item?.is_highest_degree) ? "Highest Education" : "",
      !educations.some((item) =>
        isCompletenessValueFilled(
          item?.degree_name ||
          item?.education_description ||
          item?.education_category ||
          item?.degree
        )
      )
        ? "Degree / Qualification"
        : "",
    ].filter(Boolean);
  }

  if (normalizedSection === "career") {
    return [
      !(isCompletenessValueFilled(profile?.occupation_type) || isCompletenessValueFilled(primaryOccupation?.occupation_type)) ? "Occupation Type" : "",
      !(isCompletenessValueFilled(profile?.company_name) || isCompletenessValueFilled(primaryOccupation?.company_name)) ? "Company Name" : "",
      !(isCompletenessValueFilled(profile?.current_occupation_details) || isCompletenessValueFilled(primaryOccupation?.occupation_details)) ? "Occupation Details" : "",
      !(isCompletenessValueFilled(profile?.work_profile) || isCompletenessValueFilled(primaryOccupation?.work_profile)) ? "Work Profile" : "",
    ].filter(Boolean);
  }

  if (normalizedSection === "contact") {
    const hasReachableContact = contacts.some((item) =>
      ["mobile", "whatsapp", "parent_mobile"].includes(String(item?.contact_type || "").trim().toLowerCase())
    );
    return hasReachableContact ? [] : ["Mobile Number", "WhatsApp Number"];
  }

  if (normalizedSection === "family") {
    const hasFather = relatives.some((item) => normalizeRelationType(item?.relation_type) === "father");
    const hasMother = relatives.some((item) => normalizeRelationType(item?.relation_type) === "mother");
    return [
      !hasFather ? "Father Details" : "",
      !hasMother ? "Mother Details" : "",
    ].filter(Boolean);
  }

  if (normalizedSection === "partner preferences") {
    return [
      !(
        isCompletenessValueFilled(preferences?.preferred_age_gap_months) ||
        isCompletenessValueFilled(preferences?.preferred_age_min) ||
        isCompletenessValueFilled(preferences?.preferred_age_max)
      )
        ? "Age Preference"
        : "",
      !(
        isCompletenessValueFilled(preferences?.preferred_city_ids) ||
        isCompletenessValueFilled(preferences?.non_preferred_city_ids) ||
        isCompletenessValueFilled(preferences?.preferred_city_tier_ids)
      )
        ? "City Preference"
        : "",
      !isCompletenessValueFilled(preferences?.non_preferred_sakha_ids) ? "Sakha Preference" : "",
      !(
        isCompletenessValueFilled(preferences?.preferred_education_category_ids) ||
        isCompletenessValueFilled(preferences?.non_preferred_education_category_ids) ||
        isCompletenessValueFilled(preferences?.preferred_degrees) ||
        isCompletenessValueFilled(preferences?.non_preferred_degrees)
      )
        ? "Education Preference"
        : "",
      !(
        isCompletenessValueFilled(preferences?.preferred_occupation_types) ||
        isCompletenessValueFilled(preferences?.non_preferred_occupation_types)
      )
        ? "Occupation Preference"
        : "",
    ].filter(Boolean);
  }

  if (normalizedSection === "photos") {
    const hasPrimaryPhoto =
      media.some((item) => String(item?.media_type || "").trim().toLowerCase() === "photo" && item?.is_primary) ||
      isCompletenessValueFilled(profile?.primary_photo_url_cache);
    return hasPrimaryPhoto ? [] : ["Primary Profile Photo"];
  }

  if (!hasAnyCompletenessPreferences(preferences) && normalizedSection.includes("preference")) {
    return ["Age Preference", "City Preference", "Education Preference", "Occupation Preference"];
  }

  return [];
}

function buildCompletenessOverlaySections(profile, missingItems = []) {
  if (!profile) return [];

  const seen = new Set();

  return safeArray(missingItems)
    .map((item) => {
      const section = String(item?.section || "").trim() || "Missing detail";
      const key = section.toLowerCase();
      if (seen.has(key)) return null;
      seen.add(key);

      const fields = getCompletenessSectionFields(profile, section);
      return {
        section,
        note: item?.message || "",
        action: item?.action || "",
        fields: fields.length ? fields : [item?.action || item?.message || "Add remaining details"],
      };
    })
    .filter(Boolean);
}

function formatDateTime(dateValue) {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function summarizeText(value, maxLength = 120) {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

function toDateTimeLocalValue(dateValue) {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function isFollowUpDue(dateValue) {
  if (!dateValue) return false;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() <= Date.now();
}

function deriveDraftFollowUpStatus(noteDraft) {
  if (!noteDraft) return "none";
  if (noteDraft.follow_up_at) return "scheduled";

  const outcome = String(noteDraft.outcome || "").trim().toLowerCase();
  if (["follow_up_required", "callback"].includes(outcome)) return "pending";

  return "none";
}

function noteNeedsFollowUp(note) {
  const status = String(note?.follow_up_status_effective || "").trim().toLowerCase();
  if (["completed", "cancelled", "none"].includes(status)) return false;
  if (status) return true;

  const outcome = String(note?.outcome || "").trim().toLowerCase();
  if (outcome === "follow_up_required" || outcome === "callback") return true;
  return Boolean(note?.follow_up_at);
}

function formatFileSize(bytes) {
  const size = Number(bytes || 0);
  if (!size) return "";
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
}

// eslint-disable-next-line no-unused-vars
function countContactTypes(contacts) {
  return contacts.reduce(
    (acc, c) => {
      const type = String(c.contact_type || "").toLowerCase();
      if (type.includes("mobile") || type.includes("phone")) acc.mobile += 1;
      if (type.includes("email")) acc.email += 1;
      if (type.includes("whatsapp")) acc.whatsapp += 1;
      return acc;
    },
    { mobile: 0, email: 0, whatsapp: 0 }
  );
}

// eslint-disable-next-line no-unused-vars
function highestEducationText(educations) {
  const edu = safeArray(educations).find((e) => e.is_highest_degree) || safeArray(educations)[0];
  if (!edu) return "";
  return edu.degree_name || edu.education_description || "";
}

// eslint-disable-next-line no-unused-vars
function educationDetail(educations) {
  const edu = safeArray(educations).find((e) => e.is_highest_degree) || safeArray(educations)[0];
  if (!edu) return "";
  return [edu.institution_name, edu.education_location, edu.passing_year].filter(Boolean).join(" · ");
}

function buildOverviewEducationItems(educations, fallbackSummary = "") {
  const rows = safeArray(educations)
    .map((edu, index) => {
      const title = edu.degree_name || edu.education_description || edu.education_category || "";
      const details = [
        edu.education_category,
        edu.institution_name,
        edu.education_location,
        edu.passing_year,
        edu.is_highest_degree ? "Highest" : "",
        String(edu.education_status || "").toLowerCase() === "pursuing" ? "Pursuing" : "",
      ]
        .filter(Boolean)
        .join(" · ");

      return {
        label: "Education",
        value: [title, details].filter(Boolean).join(" · "),
        span: "wide",
      };
    })
    .filter((item) => hasOverviewValue(item.value));

  if (rows.length > 0) {
    const total = rows.length;
    return rows.map((item, index) => ({
      ...item,
      label: total === 1 ? "Education" : `Education ${index + 1}`,
    }));
  }

  return hasOverviewValue(fallbackSummary)
    ? [{ label: "Education", value: fallbackSummary, span: "wide" }]
    : [];
}

function buildLookupMaps(lookups) {
  return {
    locations: safeArray(lookups.locations),
    cityTiers: safeArray(lookups.cityTiers),
    castes: safeArray(lookups.castes),
    sakhas: safeArray(lookups.sakhas),
    educationCategories: safeArray(lookups.educationCategories),
    visaStatuses: safeArray(lookups.visaStatuses),
    visaNames: safeArray(lookups.visaNames),
  };
}

function getCountryName(row) {
  return String(row?.country || row?.country_name || row?.countryName || "India").trim();
}

function getCountryId(row) {
  return row?.country_id || row?.countryId || row?.countryID || null;
}

function lookupName(items, id, type = "default") {
  if (!id) return "";
  const found = safeArray(items).find((item) => Number(item.id) === Number(id));
  if (!found) return "";

  if (type === "city") {
    return [
      found.city || found.name,
      found.district || found.district_name,
      found.state || found.state_name,
      getCountryName(found),
    ]
      .filter(Boolean)
      .join(", ");
  }

  return found.name || found.display_label || "";
}

function lookupLocationDetails(items, cityId) {
  if (!cityId) return {};
  const found = safeArray(items).find((item) => Number(item.id) === Number(cityId));
  if (!found) return {};

  return {
    city_id: found.id || null,
    city: found.city || found.name || "",
    tehsil_id: found.tehsil_id || found.tehsilId || found.tehsilID || null,
    tehsil: found.tehsil || found.tehsil_name || found.tehsilName || "",
    district_id: found.district_id || found.districtId || found.districtID || null,
    district: found.district || found.district_name || found.districtName || "",
    state_id: found.state_id || found.stateId || found.stateID || null,
    state: found.state || found.state_name || found.stateName || "",
    country_id: getCountryId(found),
    country: getCountryName(found),
  };
}

function lookupHierarchyName(items, id, idKeys, nameKeys) {
  if (!id) return "";
  const found = safeArray(items).find((item) =>
    idKeys.some((key) => Number(item?.[key]) === Number(id))
  );
  if (!found) return "";
  return nameKeys.map((key) => found?.[key]).find(Boolean) || "";
}

function formatLocationParts(parts) {
  const out = [];
  parts.forEach((value) => pushUniqueAddressPart(out, value));
  return out.join(", ");
}

function formatProfileLocation(profile, lookupMaps) {
  if (!profile) return "";

  const lookup = lookupLocationDetails(lookupMaps.locations, profile.city_id || profile.current_location_id);

  return formatLocationParts([
    profile.city || lookup.city,
    profile.district || lookup.district,
    profile.state || lookup.state,
    profile.country || lookup.country,
  ]);
}

function formatProfileBirthPlace(profile, lookupMaps) {
  if (!profile) return "";

  const lookup = lookupLocationDetails(lookupMaps.locations, profile.birth_place_city_id || profile.birth_place_id);

  return formatLocationParts([
    profile.birth_city || profile.birth_place_city || lookup.city,
    profile.birth_district || lookup.district,
    profile.birth_state || lookup.state,
    profile.birth_country || lookup.country,
  ]);
}

function formatPreferenceIds(ids, items, type = "default") {
  const names = safeArray(ids)
    .map((id) => lookupName(items, id, type))
    .filter(Boolean);

  return names.length ? names.join(", ") : "";
}

function formatPreferenceCityIds(ids, items) {
  const names = safeArray(ids)
    .map((id) => {
      const location = safeArray(items).find((item) => Number(item.id) === Number(id));
      if (!location) return "";
      return [
        location.city || location.name,
        location.state || location.state_name,
        getCountryName(location),
      ]
        .filter(Boolean)
        .join(", ");
    })
    .filter(Boolean);

  return names.length ? names.join(", ") : "";
}

function formatAgeGapMonths(value) {
  const totalMonths = Number(value);
  if (!Number.isFinite(totalMonths)) return "";
  if (totalMonths <= 0) return "0 months";
  const years = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;
  const yearText = years ? `${years} year${years === 1 ? "" : "s"}` : "";
  const monthText = remainingMonths ? `${formatPreferredAgeNumber(remainingMonths)} month${remainingMonths === 1 ? "" : "s"}` : "";
  return [yearText, monthText].filter(Boolean).join(" ");
}

function formatPreferredAgeNumber(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  return Number.isInteger(numericValue) ? String(numericValue) : String(Number(numericValue.toFixed(2)));
}

function getStoredPreferredAgeUnit(preferences, side) {
  const unitValue =
    side === "min"
      ? preferences?.preferred_age_min_unit
      : preferences?.preferred_age_max_unit;
  const normalized = String(unitValue || "").trim().toLowerCase();
  return normalized === "years" || normalized === "months" ? normalized : "";
}

function formatPreferredAgeValue(value, unit = "") {
  const totalMonths = Number(value);
  if (!Number.isFinite(totalMonths)) return "";
  if (unit === "years") {
    const yearValue = totalMonths / 12;
    const normalizedValue = formatPreferredAgeNumber(yearValue);
    return normalizedValue ? `${normalizedValue} year${Number(yearValue) === 1 ? "" : "s"}` : "";
  }
  if (unit === "months") {
    const normalizedValue = formatPreferredAgeNumber(totalMonths);
    return normalizedValue ? `${normalizedValue} month${Number(totalMonths) === 1 ? "" : "s"}` : "";
  }
  return formatAgeGapMonths(totalMonths);
}

function formatPreferredAgeDifference(preferences) {
  const minRaw = preferences?.preferred_age_min ?? preferences?.min_age;
  const maxRaw = preferences?.preferred_age_max ?? preferences?.max_age;
  const minMonths = Number(minRaw);
  const maxMonths = Number(maxRaw);
  const minUnit = getStoredPreferredAgeUnit(preferences, "min");
  const maxUnit = getStoredPreferredAgeUnit(preferences, "max");
  const hasMin = Number.isFinite(minMonths) && minRaw !== "" && minRaw !== null && minRaw !== undefined;
  const hasMax = Number.isFinite(maxMonths) && maxRaw !== "" && maxRaw !== null && maxRaw !== undefined;

  if (hasMin || hasMax) {
    const fromLabel = `- ${formatPreferredAgeValue(hasMin ? minMonths : 0, minUnit)}`;
    const toLabel = `+ ${formatPreferredAgeValue(hasMax ? maxMonths : 0, maxUnit)}`;
    return `${fromLabel}, ${toLabel}`;
  }

  const legacyGap = preferences?.preferred_age_gap_months;
  const gapMonths = Number(legacyGap);
  if (Number.isFinite(gapMonths) && legacyGap !== "" && legacyGap !== null && legacyGap !== undefined) {
    return `- ${formatAgeGapMonths(gapMonths)}, + ${formatAgeGapMonths(gapMonths)}`;
  }

  return "";
}

function cleanAddressLine(value) {
  const text = String(value || "").trim();
  if (!text) return "";

  const normalized = text.toLowerCase();

  // Hide demo/test helper text from address display.
  if (
    normalized.includes("same city as source") ||
    normalized.includes("same district as source") ||
    normalized.includes("same state as source")
  ) {
    return "";
  }

  if (normalized.includes("intentional test native house")) {
    return text.replace(/intentional test native house/ig, "Native house").trim();
  }

  return text;
}

function pushUniqueAddressPart(parts, value) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return;

  const key = text.toLowerCase();
  const alreadyExists = parts.some((part) => {
    const partKey = String(part).toLowerCase();
    return partKey === key || partKey.includes(key) || key.includes(partKey);
  });

  if (!alreadyExists) parts.push(text);
}

function formatAddress(address, profile, lookupMaps) {
  const lookup = lookupLocationDetails(lookupMaps.locations, address.city_id);
  const tehsilName =
    address.tehsil_name ||
    address.tehsil ||
    lookupHierarchyName(lookupMaps.locations, address.tehsil_id, ["tehsil_id", "tehsilId", "tehsilID"], ["tehsil", "tehsil_name", "tehsilName"]) ||
    (Number(address.tehsil_id) === Number(profile.tehsil_id) ? profile.tehsil : "");

  const districtName =
    address.district_name ||
    address.district ||
    lookup.district ||
    lookupHierarchyName(lookupMaps.locations, address.district_id, ["district_id", "districtId", "districtID"], ["district", "district_name", "districtName"]) ||
    (Number(address.district_id) === Number(profile.district_id) ? profile.district : "");

  const stateName =
    address.state_name ||
    address.state ||
    lookup.state ||
    lookupHierarchyName(lookupMaps.locations, address.state_id, ["state_id", "stateId", "stateID"], ["state", "state_name", "stateName"]) ||
    (Number(address.state_id) === Number(profile.state_id) ? profile.state : "");

  const countryName =
    address.country_name ||
    address.country ||
    lookup.country ||
    lookupHierarchyName(lookupMaps.locations, address.country_id, ["country_id", "countryId", "countryID"], ["country", "country_name", "countryName"]) ||
    profile.country ||
    "India";

  const villageName =
    address.village_name ||
    address.village ||
    (Number(address.village_id) === Number(profile.village_id) ? profile.village : "");

  const parts = [];

  pushUniqueAddressPart(parts, cleanAddressLine(address.address_line1));
  pushUniqueAddressPart(parts, cleanAddressLine(address.address_line2));
  pushUniqueAddressPart(parts, villageName);
  pushUniqueAddressPart(parts, tehsilName);
  pushUniqueAddressPart(parts, districtName);
  pushUniqueAddressPart(parts, stateName);
  pushUniqueAddressPart(parts, countryName);
  pushUniqueAddressPart(parts, address.pincode);

  return parts.join(" · ");
}

function getAddressVisaInfo(address, lookupMaps) {
  const visaStatus = lookupName(lookupMaps.visaStatuses, address?.visa_status_id);
  const visaName = lookupName(lookupMaps.visaNames, address?.visa_name_id);
  if (!visaStatus && !visaName) return null;
  return { visaStatus, visaName };
}

function buildAddressOverviewFields(address, profile, lookupMaps) {
  const formattedAddress = formatAddress(address, profile, lookupMaps);
  const visaInfo = getAddressVisaInfo(address, lookupMaps);

  return [
    { label: "Address", value: formattedAddress },
    { label: "Visa Status", value: visaInfo?.visaStatus || "" },
    { label: "Visa Name", value: visaInfo?.visaName || "" },
  ].filter((item) => hasOverviewValue(item?.value));
}

async function copyToClipboard(value) {
  const text = String(value || "");
  if (!text) return false;

  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    // fallback below
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch (err) {
    return false;
  }
}

function familyMembers(relatives) {
  return safeArray(relatives).filter((relative) =>
    FAMILY_RELATION_TYPES.has(normalizeRelationType(relative.relation_type))
  );
}

function sideRelatives(relatives) {
  return safeArray(relatives).filter(
    (relative) => !FAMILY_RELATION_TYPES.has(normalizeRelationType(relative.relation_type))
  );
}

const C = {
  bg: "#fff7f5",
  card: "#ffffff",
  border: "rgba(185,52,58,0.14)",
  borderStrong: "rgba(185,52,58,0.18)",
  borderBold: "rgba(185,52,58,0.22)",
  accent: "#D73C2E",
  accentDark: "#B9343A",
  accentLight: "#fff2ef",
  blue: "#B9343A",
  blueLight: "#fff7f5",
  text: "#252935",
  muted: "#6b6f7d",
  inputBg: "#fff7f5",
  soft: "#fff7f5",
  green: "#B9343A",
  purple: "#D73C2E",
  amber: "#252935",
  successBg: "#f4fbf6",
  successText: "#1f7a46",
  neutralBg: "#f7f8fb",
  shadow: "rgba(37,41,53,0.07)",
  shadowSoft: "rgba(37,41,53,0.06)",
  shadowLight: "rgba(37,41,53,0.05)",
  shadowStrong: "rgba(37,41,53,0.14)",
};

const S = {
  page: {
    minHeight: "calc(100vh - 60px)",
    background: `linear-gradient(135deg, #ffffff 0%, ${C.bg} 48%, ${C.soft} 100%)`,
    padding: "28px 18px 60px",
    fontFamily: "var(--jm-font-body)",
  },
  watermarkPage: {
    minHeight: "calc(100dvh - 112px)",
    height: "calc(100dvh - 112px)",
    background: `linear-gradient(135deg, #ffffff 0%, ${C.bg} 48%, ${C.soft} 100%)`,
    width: "100vw",
    marginLeft: "calc(50% - 50vw)",
    padding: "0",
    fontFamily: "var(--jm-font-body)",
    boxSizing: "border-box",
    overflow: "hidden",
  },

  centerWrap: {
    minHeight: "62vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
  },

  spinner: {
    width: 38,
    height: 38,
    border: `3px solid ${C.border}`,
    borderTopColor: C.accent,
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
  },

  loadingText: { color: C.muted, fontSize: 14, fontWeight: 800 },
  errorText: { color: C.accent, fontSize: 14, fontWeight: 800 },

  backBtn: {
    background: C.accent,
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "10px 22px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
  },

  topBar: { maxWidth: 1180, margin: "0 auto 18px", position: "relative" },
  topBarHidden: {
    display: "none",
  },
  assignmentReturnBar: {
    position: "relative",
    display: "flex",
    alignItems: "flex-start",
    gap: 18,
    padding: "18px 22px",
    paddingRight: 180,
    borderRadius: 24,
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${C.border}`,
    boxShadow: `0 18px 40px ${C.shadowLight}`,
  },
  assignmentReturnEyebrow: {
    margin: "0 0 10px",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.24em",
    textTransform: "uppercase",
    color: C.accent,
  },
  assignmentReturnTabs: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: 6,
    borderRadius: 999,
    background: "#fff3ef",
    border: `1px solid ${C.border}`,
  },
  assignmentReturnTab: {
    border: "none",
    background: "transparent",
    color: C.muted,
    borderRadius: 999,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  assignmentReturnTabActive: {
    background: C.accent,
    color: "#ffffff",
    boxShadow: "0 10px 24px rgba(215,60,46,0.18)",
  },
  backLink: {
    position: "absolute",
    top: 20,
    right: 22,
    background: "none",
    border: "none",
    color: C.accent,
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    padding: 0,
  },
  searchBackButton: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    color: C.accent,
    borderRadius: 12,
    padding: "10px 18px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
    boxShadow: `0 12px 30px ${C.shadowLight}`,
  },

  wrap: {
    maxWidth: 1180,
    margin: "0 auto",
    animation: "fadeUp 0.35s ease both",
  },
  watermarkWorkspaceWrap: {
    width: "100%",
    margin: 0,
    minHeight: "calc(100dvh - 112px)",
    height: "calc(100dvh - 112px)",
    animation: "fadeUp 0.35s ease both",
    overflow: "hidden",
  },

  hero: {
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${C.border}`,
    borderRadius: 32,
    padding: 28,
    display: "grid",
    gridTemplateColumns: "180px 1fr auto",
    gap: 28,
    alignItems: "flex-start",
    marginBottom: 18,
    boxShadow: `0 24px 70px ${C.shadow}`,
  },

  photoWrap: { flexShrink: 0 },
  photoShell: { position: "relative", width: 190, height: 190 },

  photo: {
    width: "100%",
    height: "100%",
    borderRadius: "inherit",
    objectFit: "cover",
    border: `3px solid ${C.border}`,
    display: "block",
    boxSizing: "border-box",
  },

  avatar: {
    width: "100%",
    height: "100%",
    borderRadius: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `3px solid ${C.border}`,
    boxSizing: "border-box",
  },

  avatarInitials: {
    fontSize: 58,
    color: "rgba(255,255,255,0.92)",
    fontWeight: 950,
    letterSpacing: -2,
    userSelect: "none",
  },

  completenessBadge: {
    position: "absolute",
    right: -10,
    bottom: -10,
    width: 74,
    height: 74,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    background: "#fff",
    border: `7px solid ${C.accent}`,
    boxShadow: `0 16px 34px ${C.borderBold}`,
    color: C.accentDark,
    textAlign: "center",
    fontWeight: 950,
  },

  heroInfo: { minWidth: 0 },

  heroTopLine: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 6,
  },

  eyebrow: {
    fontSize: 11,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    color: C.accent,
    fontWeight: 950,
  },

  statusChip: {
    padding: "4px 9px",
    borderRadius: 10,
    background: C.accentLight,
    color: C.accent,
    fontSize: 11,
    fontWeight: 950,
    textTransform: "uppercase",
  },

  heroName: {
    fontSize: 36,
    fontWeight: 700,
    color: C.text,
    margin: "0 0 14px",
    letterSpacing: -0.95,
    lineHeight: 1.04,
  },

  pillRow: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 },

  pill: {
    fontSize: 12,
    background: C.accentLight,
    color: C.accentDark,
    padding: "6px 12px",
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    fontWeight: 850,
    display: "inline-flex",
    alignItems: "center",
  },

  heroAbout: {
    fontSize: 14,
    color: C.muted,
    lineHeight: 1.75,
    margin: "0 0 16px",
    maxWidth: 700,
  },

  tagRow: { display: "flex", flexWrap: "wrap", gap: 7 },

  tag: {
    fontSize: 11,
    background: C.text,
    color: "#fff",
    padding: "5px 11px",
    borderRadius: 999,
    fontWeight: 850,
  },

  onlineRegRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 10,
  },

  onlineRegLabel: {
    fontSize: 12,
    color: C.muted,
    fontWeight: 800,
  },

  onlineRegValue: {
    fontSize: 13,
    color: C.text,
    fontWeight: 900,
  },

  actionRow: {
    marginTop: 18,
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },

  phoneBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "10px 18px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 950,
    boxShadow: `0 14px 30px ${C.borderBold}`,
  },

  phoneRevealed: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    background: C.accentLight,
    border: `1.5px solid ${C.accent}`,
    borderRadius: 12,
    padding: "9px 16px",
    fontSize: 14,
    color: C.accentDark,
    fontWeight: 950,
  },

  phoneHideBtn: {
    marginLeft: 6,
    background: "none",
    border: "none",
    color: C.muted,
    fontSize: 11,
    cursor: "pointer",
    textDecoration: "underline",
    padding: 0,
  },

  emailPill: {
    padding: "9px 14px",
    borderRadius: 12,
    background: C.blueLight,
    border: `1px solid ${C.border}`,
    color: C.muted,
    fontSize: 12,
    fontWeight: 850,
  },

  policyNotice: {
    marginTop: 10,
    padding: "12px 14px",
    borderRadius: 18,
    background: C.accentLight,
    border: `1px solid ${C.borderStrong}`,
    color: C.muted,
    maxWidth: 640,
  },

  policyNoticeTitle: {
    display: "block",
    color: C.text,
    fontSize: 13,
    fontWeight: 950,
    marginBottom: 4,
  },

  policyNoticeBody: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    fontSize: 12,
    lineHeight: 1.5,
  },

  heroAlertStack: {
    marginTop: 14,
    display: "grid",
    gap: 10,
    maxWidth: 720,
  },

  heroAlertBanner: {
    padding: "12px 14px",
    borderRadius: 18,
    background: C.blueLight,
    border: `1px solid ${C.border}`,
  },

  heroAlertBannerHigh: {
    background: C.accentLight,
    border: `1px solid ${C.borderBold}`,
  },

  heroAlertTitle: {
    color: C.text,
    fontSize: 13,
    fontWeight: 900,
    lineHeight: 1.45,
  },

  heroAlertMeta: {
    marginTop: 4,
    color: C.muted,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.45,
  },

  heroActions: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    alignItems: "flex-end",
  },

  strengthTrigger: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 4,
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    textAlign: "right",
    fontSize: 0,
  },

  strengthTriggerValue: {
    fontSize: 18,
    lineHeight: 1.1,
    fontWeight: 950,
    color: C.accentDark,
  },

  strengthTriggerHint: {
    fontSize: 11,
    lineHeight: 1.4,
    fontWeight: 850,
    color: C.accent,
  },

  heroAlertBellButton: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 8,
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    textAlign: "right",
  },

  heroAlertBellIconWrap: {
    width: 58,
    height: 58,
    borderRadius: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, rgba(255,244,241,1), rgba(255,255,255,1))",
    color: C.accent,
    border: `1px solid ${C.border}`,
    boxShadow: `0 12px 30px ${C.shadowLight}`,
    transformOrigin: "top center",
    animation: "bellPulse 2.2s ease-in-out infinite",
  },

  heroAlertBellIcon: {
    width: 30,
    height: 30,
    display: "block",
    transformOrigin: "top center",
    animation: "bellSwing 2.2s ease-in-out infinite",
  },

  heroAlertBellText: {
    display: "grid",
    gap: 2,
  },

  heroAlertBellTitle: {
    fontSize: 14,
    lineHeight: 1.2,
    fontWeight: 950,
    color: C.text,
  },

  heroAlertBellHint: {
    fontSize: 11,
    lineHeight: 1.4,
    fontWeight: 800,
    color: C.accent,
  },

  addPhotoBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "#fff",
    border: `1.5px solid ${C.accent}`,
    color: C.accent,
    borderRadius: 12,
    padding: "10px 16px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 950,
  },

  editBtn: {
    background: C.text,
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "11px 17px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 950,
  },

  quickGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 18,
  },

  quickCard: {
    background: "rgba(255,255,255,0.86)",
    border: `1px solid ${C.border}`,
    borderRadius: 22,
    padding: 16,
    boxShadow: `0 12px 34px ${C.shadowLight}`,
  },

  completenessPanel: {
    background: "rgba(255,255,255,0.94)",
    border: `1px solid ${C.border}`,
    borderRadius: 28,
    padding: 22,
    marginBottom: 18,
    boxShadow: `0 18px 54px ${C.shadowSoft}`,
  },

  completenessPanelTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    alignItems: "flex-start",
  },

  panelEyebrow: {
    margin: 0,
    color: C.accent,
    fontSize: 10,
    letterSpacing: 2,
    textTransform: "uppercase",
    fontWeight: 950,
  },

  panelTitle: {
    margin: "5px 0 0",
    color: C.text,
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.1,
  },

  panelSub: {
    margin: "6px 0 0",
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.6,
  },

  completenessOverlaySub: {
    margin: "8px 0 0",
    color: C.muted,
    fontSize: 14,
    lineHeight: 1.6,
    fontWeight: 700,
  },

  completenessOverlayBody: {
    display: "grid",
    gap: 16,
    padding: "20px 32px 28px",
  },

  completenessSectionList: {
    display: "grid",
    gap: 12,
  },

  completenessSectionCard: {
    display: "grid",
    gap: 12,
    padding: "16px 18px",
    borderRadius: 20,
    background: C.soft,
    border: `1px solid ${C.border}`,
  },

  completenessSectionTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },

  completenessSectionTitle: {
    margin: 0,
    color: C.text,
    fontSize: 16,
    fontWeight: 900,
  },

  completenessSectionNote: {
    margin: "6px 0 0",
    color: C.muted,
    fontSize: 12,
    lineHeight: 1.55,
    fontWeight: 700,
  },

  completenessSectionAction: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "6px 10px",
    background: C.accentLight,
    color: C.accentDark,
    fontSize: 10,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },

  completenessFieldList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },

  completenessFieldChip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 34,
    padding: "7px 12px",
    borderRadius: 999,
    background: "#fff",
    border: `1px solid ${C.border}`,
    color: C.text,
    fontSize: 12,
    fontWeight: 850,
  },

  panelAction: {
    background: C.text,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "10px 16px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },

  progressTrack: {
    height: 10,
    borderRadius: 999,
    background: "#f4d9d6",
    overflow: "hidden",
    marginTop: 16,
  },

  progressFill: {
    height: "100%",
    borderRadius: 999,
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
  },

  missingGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    marginTop: 16,
  },

  missingItem: {
    display: "flex",
    gap: 10,
    padding: 12,
    borderRadius: 18,
    background: C.blueLight,
    border: `1px solid ${C.border}`,
  },

  missingNumber: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    background: C.accentLight,
    color: C.accentDark,
    fontSize: 11,
    fontWeight: 950,
    flexShrink: 0,
  },

  completeText: {
    margin: "14px 0 0",
    color: C.accent,
    fontSize: 13,
    fontWeight: 900,
  },

  profileSignalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
    marginBottom: 18,
  },

  signalCard: {
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${C.border}`,
    borderRadius: 24,
    padding: 18,
    boxShadow: `0 14px 40px ${C.shadowLight}`,
    minHeight: 104,
  },

  signal_blue: { borderTop: `4px solid ${C.accent}` },
  signal_green: { borderTop: `4px solid ${C.accentDark}` },
  signal_orange: { borderTop: `4px solid ${C.amber}` },
  signal_purple: { borderTop: `4px solid ${C.purple}` },

  signalLabel: {
    display: "block",
    color: C.muted,
    fontSize: 10,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    fontWeight: 950,
    marginBottom: 7,
  },

  signalTitle: {
    display: "block",
    color: C.text,
    fontSize: 16,
    lineHeight: 1.3,
  },

  signalDetail: {
    margin: "6px 0 0",
    color: C.muted,
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 700,
  },

  detailBox: {
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${C.border}`,
    borderRadius: 28,
    overflow: "visible",
    marginBottom: 18,
    boxShadow: `0 18px 54px ${C.shadowSoft}`,
  },

  tabBar: {
    display: "flex",
    position: "sticky",
    top: PROFILE_TAB_STICKY_TOP,
    zIndex: 70,
    borderBottom: `1px solid ${C.border}`,
    background: C.card,
    padding: "8px 10px",
    gap: 8,
    overflowX: "auto",
    boxShadow: `0 12px 24px ${C.shadowSoft}`,
  },

  tab: {
    padding: "12px 16px",
    background: "transparent",
    border: "none",
    borderRadius: 12,
    color: C.muted,
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
    textDecoration: "none",
    outline: "none",
    boxShadow: "none",
  },

  tabActive: {
    color: C.accent,
    background: C.accentLight,
    border: "none",
    outline: "none",
    boxShadow: "none",
  },

  tabContent: { padding: "28px 32px", animation: "fadeUp 0.25s ease both" },

  overviewStack: {
    display: "grid",
    gap: 22,
  },

  overviewSection: {
    display: "grid",
    gap: 16,
  },

  overviewSectionHeader: {
    marginBottom: 0,
  },

  overviewSectionTitle: {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.2,
    color: C.accent,
    fontWeight: 900,
    letterSpacing: -0.05,
  },

  overviewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
  },

  contactOverviewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
  },

  contactOverviewCard: {
    minWidth: 0,
    display: "grid",
    gridTemplateRows: "auto minmax(0, 1fr) auto",
    gap: 12,
    alignContent: "start",
    height: "100%",
    padding: "14px 16px",
    borderRadius: 16,
    background: "#ffffff",
    border: `1px solid ${C.border}`,
    boxShadow: "0 8px 20px rgba(37,41,53,0.05)",
  },

  contactOverviewHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },

  contactOverviewTitle: {
    fontSize: 10,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: C.muted,
    fontWeight: 900,
    lineHeight: 1.3,
  },

  contactOverviewStatus: {
    fontSize: 11,
    color: C.accent,
    fontWeight: 800,
    lineHeight: 1.4,
    textAlign: "right",
  },

  contactOverviewFieldList: {
    display: "grid",
    gap: 8,
    alignContent: "start",
    minHeight: 0,
  },

  contactOverviewField: {
    display: "grid",
    gap: 3,
  },

  contactOverviewFieldLabel: {
    fontSize: 10,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: C.muted,
    fontWeight: 900,
    lineHeight: 1.25,
  },

  contactOverviewFieldValue: {
    fontSize: 12,
    color: C.text,
    fontWeight: 780,
    lineHeight: 1.45,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  contactOverviewCopyBtn: {
    marginTop: 12,
    alignSelf: "start",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 38,
    padding: "0 14px",
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    background: "#fff",
    color: C.accent,
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 900,
  },

  educationCareerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 24,
    alignItems: "start",
  },

  educationCareerColumn: {
    display: "grid",
    gap: 16,
    minWidth: 0,
  },

  educationCareerColumnStack: {
    display: "grid",
    gap: 12,
  },

  educationCareerCard: {
    minWidth: 0,
    display: "grid",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 16,
    background: "#ffffff",
    border: `1px solid ${C.border}`,
    boxShadow: "0 8px 20px rgba(37,41,53,0.05)",
  },

  educationCareerCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },

  educationCareerCardTitle: {
    fontSize: 22,
    lineHeight: 1.1,
    color: C.text,
    fontWeight: 900,
    letterSpacing: -0.04,
  },

  educationCareerBadgeRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: 8,
  },

  educationCareerBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    padding: "0 12px",
    borderRadius: 999,
    background: C.accentSoft,
    color: C.accent,
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  educationCareerSummary: {
    padding: "12px 14px",
    borderRadius: 14,
    background: "#ffffff",
    border: `1px solid ${C.border}`,
    color: C.text,
    fontSize: 14,
    lineHeight: 1.45,
    fontWeight: 780,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  educationCareerInlineSummary: {
    color: C.text,
    fontSize: 14,
    lineHeight: 1.45,
    fontWeight: 780,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  educationCareerFieldList: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
  },

  educationCareerField: {
    display: "grid",
    gap: 3,
  },

  educationCareerFieldLabel: {
    fontSize: 10,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: C.muted,
    fontWeight: 900,
    lineHeight: 1.25,
  },

  educationCareerFieldValue: {
    fontSize: 12,
    color: C.text,
    fontWeight: 780,
    lineHeight: 1.45,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  familyOverviewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
  },

  familyOverviewCard: {
    minWidth: 0,
    display: "grid",
    gap: 14,
    padding: "14px 16px",
    borderRadius: 16,
    background: "#ffffff",
    border: `1px solid ${C.border}`,
    boxShadow: "0 8px 20px rgba(37,41,53,0.05)",
  },

  familyOverviewHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  familyOverviewName: {
    fontSize: 18,
    lineHeight: 1.2,
    color: C.text,
    fontWeight: 900,
    letterSpacing: -0.04,
  },

  familyOverviewBadgeRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: 8,
  },

  familyOverviewBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    padding: "0 12px",
    borderRadius: 999,
    background: C.accentSoft,
    color: C.accent,
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  familyOverviewFields: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },

  familyOverviewField: {
    display: "grid",
    gap: 4,
    minWidth: 0,
  },

  familyOverviewFieldLabel: {
    fontSize: 10,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: C.muted,
    fontWeight: 900,
    lineHeight: 1.25,
  },

  familyOverviewFieldValue: {
    fontSize: 12,
    color: C.text,
    fontWeight: 780,
    lineHeight: 1.45,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  overviewItem: {
    minWidth: 0,
    display: "grid",
    gap: 8,
    alignContent: "start",
    padding: "12px 14px",
    borderRadius: 16,
    background: "#ffffff",
    border: `1px solid ${C.border}`,
    boxShadow: "0 8px 20px rgba(37,41,53,0.05)",
  },

  overviewItemWide: {
    gridColumn: "span 2",
  },

  overviewItemLabel: {
    fontSize: 10,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: C.muted,
    fontWeight: 900,
    lineHeight: 1.3,
  },

  overviewItemValue: {
    fontSize: 12,
    color: C.text,
    fontWeight: 780,
    lineHeight: 1.4,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 32px" },
  cardGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 },

  infoItem: {
    padding: "14px 0",
    borderBottom: `1px solid ${C.border}`,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  infoLabel: {
    fontSize: 10,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    color: C.muted,
    fontWeight: 900,
  },

  infoValue: { fontSize: 15, color: C.text, fontWeight: 800 },

  sectionHead: {
    fontSize: 11,
    letterSpacing: 1.8,
    textTransform: "uppercase",
    color: C.accentDark,
    margin: "0 0 14px",
    fontWeight: 950,
  },

  subCard: {
    background: C.accentLight,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    padding: "15px 18px",
    marginBottom: 10,
  },

  subCardTitle: {
    fontSize: 15,
    color: C.text,
    marginBottom: 5,
    fontWeight: 950,
  },

  subCardSub: {
    fontSize: 12,
    color: C.muted,
    lineHeight: 1.5,
    fontWeight: 700,
  },

  metaLine: {
    marginTop: 8,
    fontSize: 11,
    color: C.muted,
    lineHeight: 1.45,
    fontWeight: 800,
  },

  alertCardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "flex-start",
  },

  alertBadgeColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "flex-end",
    flexShrink: 0,
  },

  alertStatusBadge: {
    padding: "4px 9px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 950,
    textTransform: "uppercase",
  },

  alertStatusBadgeOpen: {
    background: C.accentLight,
    color: C.accent,
  },

  alertStatusBadgeResolved: {
    background: C.successBg,
    color: C.successText,
  },

  alertSeverityBadge: {
    padding: "4px 9px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 950,
    textTransform: "uppercase",
    background: C.neutralBg,
    color: C.muted,
  },

  alertSeverityBadgeHigh: {
    background: C.accentLight,
    color: C.accent,
  },

  copyHint: {
    marginTop: 8,
    color: C.accentDark,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.45,
  },

  miniMeta: {
    marginTop: 8,
    fontSize: 11,
    color: C.muted,
    fontWeight: 800,
  },

  contactCardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "flex-start",
  },

  copyBtn: {
    background: C.text,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "8px 13px",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 950,
    whiteSpace: "nowrap",
    minWidth: 92,
  },

  verifiedBadge: {
    marginLeft: 8,
    padding: "3px 7px",
    borderRadius: 8,
    background: C.accentLight,
    color: C.accent,
    fontSize: 10,
    fontWeight: 950,
  },

  inlineLink: {
    display: "inline-flex",
    marginTop: 10,
    color: C.accent,
    fontSize: 12,
    fontWeight: 950,
    textDecoration: "none",
  },

  documentCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  documentCardTitleWrap: {
    minWidth: 0,
    flex: 1,
  },
  documentHistoryButton: {
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.accent,
    borderRadius: 10,
    padding: "6px 12px",
    fontSize: 11,
    fontWeight: 950,
    cursor: "pointer",
    flexShrink: 0,
  },
  documentActionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center",
    marginTop: 10,
  },

  inlineLinkButton: {
    display: "inline-flex",
    alignItems: "center",
    padding: 0,
    background: "none",
    border: "none",
    color: C.accent,
    fontSize: 12,
    fontWeight: 950,
    cursor: "pointer",
    textDecoration: "none",
  },

  inlineLinkButtonDisabled: {
    opacity: 0.7,
    cursor: "wait",
  },

  highestBadge: {
    marginLeft: 8,
    padding: "3px 7px",
    borderRadius: 8,
    background: C.accentLight,
    color: C.accent,
    fontSize: 10,
    fontWeight: 950,
  },

  relativeTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
  },

  futureBadge: {
    height: 24,
    display: "inline-flex",
    alignItems: "center",
    padding: "0 9px",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accent,
    fontSize: 10,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },

  relativeEduList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 10,
  },

  relativeEduChip: {
    padding: "5px 8px",
    borderRadius: 999,
    background: "#fff",
    color: C.muted,
    fontSize: 11,
    fontWeight: 800,
  },

  emptyText: {
    color: C.muted,
    fontSize: 13,
    fontStyle: "italic",
    textAlign: "center",
    padding: "28px 0",
  },

  remarkBox: {
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${C.border}`,
    borderRadius: 28,
    overflow: "hidden",
    marginBottom: 18,
    boxShadow: `0 18px 54px ${C.shadowSoft}`,
  },

  remarkHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    padding: "24px 28px 0",
  },

  remarkEyebrow: {
    fontSize: 10,
    letterSpacing: 2.5,
    textTransform: "uppercase",
    color: C.accent,
    margin: "0 0 4px",
    fontWeight: 950,
  },

  remarkTitle: { fontSize: 20, fontWeight: 950, color: C.text, margin: 0 },
  remarkSubhead: {
    margin: "8px 0 0",
    color: C.muted,
    fontSize: 12,
    lineHeight: 1.55,
    fontWeight: 700,
    maxWidth: 560,
  },

  remarkCount: {
    fontSize: 11,
    color: C.muted,
    background: C.accentLight,
    padding: "5px 10px",
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    fontWeight: 900,
  },

  noteStatGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    padding: "18px 28px 0",
  },

  recommendationMetricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 8,
    padding: "14px 28px 0",
  },

  noteMetricCard: {
    background: C.accentLight,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  noteMetricCardCompact: {
    minWidth: 0,
    borderRadius: 14,
    padding: "10px 12px",
    gap: 2,
  },

  noteMetricCardAlert: {
    background: "#fff1ef",
    border: `1px solid ${C.borderBold}`,
  },

  noteMetricLabel: {
    fontSize: 10,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    color: C.muted,
    fontWeight: 900,
  },

  noteMetricLabelCompact: {
    fontSize: 9,
    letterSpacing: 1.1,
  },

  noteMetricValue: {
    fontSize: 22,
    lineHeight: 1.1,
    color: C.text,
    fontWeight: 950,
  },

  noteMetricValueCompact: {
    fontSize: 16,
    lineHeight: 1,
  },

  noteMetricHint: {
    fontSize: 11,
    color: C.muted,
    fontWeight: 700,
    lineHeight: 1.45,
  },

  remarkInputWrap: { padding: "20px 28px", borderBottom: `1px solid ${C.border}` },

  recommendationSearchModeRow: {
    display: "flex",
    gap: 18,
    alignItems: "center",
    marginBottom: 14,
    flexWrap: "wrap",
  },

  recommendationFilterRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.7fr) minmax(220px, 1fr)",
    gap: 16,
    alignItems: "end",
  },

  recommendationRadioLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    color: C.text,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },

  recommendationSearchPanel: {
    marginTop: 12,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    background: "#fff",
    overflow: "hidden",
  },

  recommendationSearchPanelFlat: {
    marginTop: 12,
  },

  recommendationSearchHint: {
    padding: "14px 16px",
    color: C.muted,
    fontSize: 12,
    fontWeight: 700,
  },

  recommendationSearchError: {
    padding: "14px 16px",
    color: C.accent,
    fontSize: 12,
    fontWeight: 800,
    background: "#fff6f4",
  },

  recommendationSearchList: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    padding: 12,
    background: C.inputBg,
    maxHeight: 340,
    overflowY: "auto",
  },

  recommendationSearchListFlat: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    maxHeight: 340,
    overflowY: "auto",
  },

  recommendationSearchItem: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 12,
    alignItems: "center",
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    padding: "13px 14px",
    boxShadow: `0 10px 24px ${C.shadowLight}`,
  },

  recommendationSearchItemFlat: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 12,
    alignItems: "center",
    background: "transparent",
    border: "none",
    borderBottom: `1px solid ${C.border}`,
    borderRadius: 0,
    padding: "12px 0",
    boxShadow: "none",
  },

  recommendationSearchItemFlatActive: {
    background: "transparent",
    border: "none",
    borderBottom: `1px solid ${C.borderBold}`,
  },

  recommendationSearchItemActive: {
    border: `1px solid ${C.borderBold}`,
    background: "linear-gradient(135deg, rgba(255,245,242,0.95), rgba(255,255,255,1))",
    boxShadow: `0 14px 28px ${C.shadowLight}`,
  },

  recommendationSearchItemDisabled: {
    cursor: "default",
    opacity: 0.88,
  },

  recommendationSearchItemMain: {
    border: "none",
    background: "transparent",
    padding: 0,
    textAlign: "left",
    width: "100%",
    cursor: "pointer",
  },

  recommendationSearchItemInner: {
    display: "flex",
    gap: 12,
    alignItems: "center",
  },

  recommendationSearchPhotoWrap: {
    width: 54,
    height: 54,
    flexShrink: 0,
    overflow: "hidden",
    borderRadius: 16,
  },

  recommendationSearchName: {
    color: C.text,
    fontSize: 13,
    fontWeight: 900,
  },

  recommendationSearchTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },

  recommendationSearchNumber: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 8px",
    borderRadius: 999,
    background: C.redLight,
    color: C.accent,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 0.3,
  },

  recommendationAddPill: {
    marginLeft: "auto",
    flexShrink: 0,
    borderRadius: 999,
    padding: "7px 11px",
    background: C.redLight,
    color: C.accent,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },

  recommendationAddPillDisabled: {
    background: C.soft,
    color: C.muted,
  },

  recommendationSearchMeta: {
    marginTop: 6,
    color: C.muted,
    fontSize: 11,
    lineHeight: 1.45,
    fontWeight: 700,
  },

  recommendationSearchActionRow: {
    display: "flex",
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
    justifyContent: "flex-end",
    whiteSpace: "nowrap",
  },

  recommendationAddPillButton: {
    border: "none",
    borderRadius: 999,
    padding: "8px 12px",
    background: "linear-gradient(135deg, rgba(215,60,46,0.12), rgba(215,60,46,0.2))",
    color: C.accent,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    cursor: "pointer",
  },

  recommendationCopyPill: {
    border: "none",
    borderRadius: 999,
    padding: "8px 10px",
    background: "rgba(37,41,53,0.06)",
    color: C.muted,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 0.4,
    cursor: "pointer",
  },

  recommendationSelectedWrap: {
    marginTop: 14,
    border: `1px solid ${C.border}`,
    borderRadius: 22,
    background: `linear-gradient(135deg, ${C.inputBg}, rgba(255,255,255,0.96))`,
    padding: 14,
  },

  recommendationSelectedHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "center",
    marginBottom: 12,
  },

  recommendationSelectedTitle: {
    color: C.text,
    fontSize: 14,
    fontWeight: 950,
  },

  recommendationSelectedHint: {
    marginTop: 4,
    color: C.muted,
    fontSize: 11,
    fontWeight: 700,
  },

  recommendationSelectedGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },

  recommendationSelectedCard: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 10,
    alignItems: "center",
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    background: "#fff",
    padding: 10,
    boxShadow: `0 10px 26px ${C.shadowLight}`,
  },

  recommendationSelectedMain: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    gap: 12,
    alignItems: "center",
    border: "none",
    background: "transparent",
    textAlign: "left",
    padding: 0,
  },

  recommendationSelectedControls: {
    alignSelf: "start",
  },

  recommendationSelectedQuickAction: {
    border: "none",
    borderRadius: 999,
    background: "rgba(37,41,53,0.06)",
    color: C.muted,
    padding: "8px 10px",
    cursor: "pointer",
    fontSize: 10,
    fontWeight: 900,
  },

  recommendationSelectedRemove: {
    border: `1px solid ${C.border}`,
    borderRadius: 999,
    background: C.inputBg,
    color: C.muted,
    padding: "7px 10px",
    cursor: "pointer",
    fontSize: 10,
    fontWeight: 900,
  },

  recommendationSelectedNote: {
    gridColumn: "1 / -1",
    width: "100%",
    minHeight: 58,
    resize: "vertical",
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    background: C.inputBg,
    color: C.text,
    padding: "10px 12px",
    fontSize: 12,
    lineHeight: 1.45,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
  },

  recommendationTargetCard: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "flex-start",
    marginTop: 12,
    background: C.blueLight,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    padding: "14px 16px",
  },

  recommendationTargetMain: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    minWidth: 0,
  },

  recommendationTargetPhotoWrap: {
    width: 58,
    height: 58,
    flexShrink: 0,
    overflow: "hidden",
    borderRadius: 18,
  },

  recommendationTargetTitle: {
    color: C.text,
    fontSize: 14,
    fontWeight: 900,
  },

  recommendationProfileLink: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    minWidth: 0,
    flex: 1,
    padding: 0,
    margin: 0,
    background: "none",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    color: "inherit",
  },

  recommendationProfileLinkText: {
    minWidth: 0,
  },

  recommendationTargetMeta: {
    marginTop: 6,
    color: C.muted,
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 700,
  },

  followUpContextCard: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "flex-start",
    background: C.blueLight,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    padding: "14px 16px",
    marginBottom: 14,
  },

  followUpContextTitle: {
    color: C.text,
    fontSize: 12,
    fontWeight: 900,
  },

  followUpContextMeta: {
    marginTop: 4,
    color: C.muted,
    fontSize: 11,
    fontWeight: 700,
  },

  followUpContextBody: {
    marginTop: 8,
    color: C.text,
    fontSize: 13,
    lineHeight: 1.55,
    fontWeight: 700,
  },

  followUpContextActions: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    alignItems: "flex-end",
    flexShrink: 0,
  },

  followUpResolveLabel: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },

  noteComposerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 14,
  },

  noteField: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  noteFieldLabel: {
    fontSize: 10,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    color: C.muted,
    fontWeight: 900,
  },

  noteSelect: {
    width: "100%",
    height: 42,
    borderRadius: 14,
    border: `1.5px solid ${C.border}`,
    padding: "0 12px",
    fontSize: 13,
    color: C.text,
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
  },

  noteInput: {
    width: "100%",
    height: 42,
    borderRadius: 14,
    border: `1.5px solid ${C.border}`,
    padding: "0 12px",
    fontSize: 13,
    color: C.text,
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
  },

  remarkInput: {
    width: "100%",
    minHeight: 82,
    borderRadius: 16,
    border: `1.5px solid ${C.border}`,
    padding: "12px 14px",
    fontSize: 14,
    color: C.text,
    background: C.inputBg,
    resize: "vertical",
    outline: "none",
    boxSizing: "border-box",
    lineHeight: 1.6,
  },

  remarkActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 10,
    gap: 12,
  },

  remarkCharCount: { fontSize: 11, color: C.muted, fontWeight: 800 },

  noteActionRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
  },

  noteGhostBtn: {
    background: "#fff",
    color: C.muted,
    border: `1px solid ${C.border}`,
    padding: "10px 16px",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
  },

  remarkBtn: {
    background: C.text,
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 950,
  },

  remarkBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
  remarkList: { padding: "8px 0" },

  noteFiltersBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    padding: "18px 28px 6px",
  },

  noteFilterSelect: {
    minWidth: 160,
    height: 40,
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    padding: "0 12px",
    fontSize: 12,
    color: C.text,
    background: "#fff",
    outline: "none",
  },
  notePagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "16px 28px 0",
    borderTop: `1px solid ${C.border}`,
    marginTop: 12,
    flexWrap: "wrap",
  },
  notePaginationInfo: {
    fontSize: 12,
    color: C.muted,
    fontWeight: 700,
  },
  notePaginationControls: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  notePaginationButton: {
    minWidth: 38,
    height: 36,
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
    padding: "0 12px",
  },
  notePaginationButtonActive: {
    background: C.accent,
    color: "#fff",
    borderColor: C.accent,
  },
  notePaginationButtonDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },

  remarkItem: {
    display: "flex",
    gap: 14,
    padding: "16px 28px",
    borderBottom: `1px solid ${C.border}`,
  },

  remarkItemFirst: {},
  remarkItemLeft: { flexShrink: 0 },

  remarkAvatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    overflow: "hidden",
    background: C.accentLight,
    border: `1.5px solid ${C.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    color: C.accentDark,
    fontWeight: 950,
  },

  remarkItemRight: { flex: 1 },

  remarkItemTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },

  remarkAuthor: { fontSize: 13, fontWeight: 950, color: C.text },
  remarkTime: { fontSize: 11, color: C.muted, fontWeight: 800 },
  recommendationNoteTitle: {
    marginTop: 10,
    marginBottom: 4,
    fontSize: 10,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    color: C.muted,
    fontWeight: 900,
  },
  remarkText: { fontSize: 14, color: C.text, lineHeight: 1.6, margin: 0 },
  noteRecommendationContext: {
    margin: "10px 0 8px",
    padding: "9px 11px",
    borderRadius: 14,
    background: "linear-gradient(135deg, rgba(255,245,242,0.96), rgba(255,255,255,0.98))",
    border: `1px solid ${C.border}`,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
  },
  noteRecommendationBadge: {
    padding: "4px 8px",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accentDark,
    fontSize: 10,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  noteRecommendationText: {
    color: C.text,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.4,
  },
  noteRecommendationMeta: {
    color: C.muted,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.4,
  },

  noteCardActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 10,
  },

  recommendationCardMeta: {
    marginTop: 8,
    color: C.muted,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.5,
  },

  recommendationTimeline: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  recommendationTimelineItem: {
    background: C.accentLight,
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    padding: "11px 12px",
  },

  recommendationTimelineTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
  },

  recommendationTimelineBody: {
    marginTop: 8,
    color: C.text,
    fontSize: 13,
    lineHeight: 1.55,
    fontWeight: 700,
  },

  recommendationTimelineActor: {
    fontSize: 11,
    fontWeight: 800,
    color: C.text,
    alignSelf: "center",
  },

  recommendationDownloadRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginTop: 14,
    paddingTop: 12,
    borderTop: `1px solid ${C.border}`,
  },

  recommendationOutcomeComposer: {
    marginTop: 14,
    paddingTop: 14,
    borderTop: `1px solid ${C.border}`,
  },

  recommendationOutcomeActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginTop: 10,
  },
  recommendationTaskSummaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginTop: 14,
    paddingTop: 12,
    borderTop: `1px solid ${C.border}`,
    flexWrap: "wrap",
  },
  recommendationTaskSummaryText: {
    fontSize: 12,
    color: C.muted,
    fontWeight: 800,
    lineHeight: 1.5,
  },
  recommendationTaskList: {
    display: "grid",
    gap: 10,
    marginTop: 12,
  },
  recommendationTaskItem: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    padding: "12px 14px",
    display: "grid",
    gap: 8,
  },
  recommendationTaskToggle: {
    border: "none",
    background: "transparent",
    padding: 0,
    margin: 0,
    textAlign: "left",
    cursor: "pointer",
    color: "inherit",
  },
  recommendationTaskTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  },
  recommendationTaskTitle: {
    fontSize: 14,
    fontWeight: 900,
    color: C.text,
    lineHeight: 1.45,
  },
  recommendationTaskBadgeRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  recommendationTaskBody: {
    fontSize: 13,
    color: C.text,
    lineHeight: 1.6,
    margin: 0,
  },
  recommendationTaskBodyMuted: {
    fontSize: 13,
    color: C.muted,
    lineHeight: 1.6,
    margin: 0,
    fontWeight: 700,
  },
  recommendationTaskMetaRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    color: C.muted,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.5,
  },
  recommendationTaskMeta: {
    color: C.muted,
    fontSize: 11,
    fontWeight: 800,
  },
  recommendationTaskHistoryList: {
    display: "grid",
    gap: 10,
    marginTop: 4,
  },
  recommendationTaskHistoryEmpty: {
    fontSize: 12,
    color: C.muted,
    lineHeight: 1.6,
    fontWeight: 700,
    paddingTop: 2,
  },
  recommendationTaskHistoryRow: {
    display: "grid",
    gridTemplateColumns: "14px minmax(0, 1fr)",
    gap: 10,
    alignItems: "stretch",
  },
  recommendationTaskHistoryDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    background: C.accent,
    marginTop: 14,
    boxShadow: `0 0 0 4px ${C.accentLight}`,
  },
  recommendationTaskHistoryCard: {
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    background: C.surfaceWarm,
    padding: "12px 14px",
    display: "grid",
    gap: 6,
  },
  recommendationTaskHistoryTitle: {
    fontSize: 13,
    color: C.text,
    fontWeight: 900,
    lineHeight: 1.5,
  },
  recommendationTaskHistoryMeta: {
    fontSize: 12,
    color: C.muted,
    fontWeight: 800,
    lineHeight: 1.5,
  },
  recommendationTaskHistoryNote: {
    fontSize: 13,
    color: C.text,
    lineHeight: 1.6,
  },
  recommendationTaskChevron: {
    color: C.accent,
    fontSize: 13,
    fontWeight: 900,
    lineHeight: 1,
  },
  recommendationTaskModalBody: {
    display: "grid",
    gap: 14,
    padding: "20px 32px 4px",
  },
  recommendationTaskTabRow: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
    padding: 6,
    borderRadius: 999,
    background: C.accentLight,
    border: `1px solid ${C.border}`,
    margin: "18px 28px 0",
  },
  recommendationTaskTabBtn: {
    border: "none",
    background: "transparent",
    color: C.muted,
    borderRadius: 999,
    padding: "10px 16px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    width: "100%",
    textAlign: "center",
  },
  recommendationTaskTabBtnActive: {
    background: C.accent,
    color: "#fff",
    boxShadow: `0 10px 24px ${C.shadowStrong}`,
  },
  recommendationSectionTabRow: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 0,
    margin: "18px 28px 0",
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    overflow: "hidden",
    background: "#fff",
  },
  recommendationSectionTabBtn: {
    border: "none",
    background: "transparent",
    color: C.muted,
    borderRadius: 0,
    padding: "14px 16px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    width: "100%",
    textAlign: "center",
  },
  recommendationSectionTabBtnActive: {
    background: C.accent,
    color: "#fff",
    boxShadow: "none",
  },
  recommendationTaskModalText: {
    fontSize: 13,
    color: C.muted,
    lineHeight: 1.6,
    margin: "8px 0 0",
    fontWeight: 700,
  },
  recommendationTaskTextarea: {
    minHeight: 112,
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    padding: "12px 14px",
    fontSize: 13,
    color: C.text,
    background: "#fff",
    outline: "none",
    resize: "vertical",
    boxSizing: "border-box",
    width: "100%",
    fontFamily: "inherit",
    lineHeight: 1.6,
  },

  noteFollowUpBtn: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    color: C.accent,
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 900,
    cursor: "pointer",
  },

  noteSecondaryBtn: {
    background: C.neutralBg,
    border: `1px solid ${C.border}`,
    color: C.text,
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 850,
    cursor: "pointer",
  },

  noteInlineActionGroup: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
  },

  noteInlineInput: {
    height: 36,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    padding: "0 10px",
    fontSize: 12,
    color: C.text,
    background: "#fff",
    outline: "none",
  },

  noteMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 6,
  },

  noteTypeBadge: {
    padding: "4px 8px",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accentDark,
    fontSize: 10,
    fontWeight: 900,
    textTransform: "uppercase",
  },

  noteOutcomeBadge: {
    padding: "4px 8px",
    borderRadius: 999,
    background: C.neutralBg,
    color: C.muted,
    fontSize: 10,
    fontWeight: 900,
    textTransform: "uppercase",
  },

  noteStatusBadge: {
    padding: "4px 8px",
    borderRadius: 999,
    background: C.blueLight,
    color: C.text,
    fontSize: 10,
    fontWeight: 900,
    textTransform: "uppercase",
  },

  noteStatusBadgeCompleted: {
    background: C.successBg,
    color: C.successText,
  },

  noteStatusBadgeCancelled: {
    background: C.neutralBg,
    color: C.muted,
  },

  noteFollowUpBadge: {
    padding: "4px 8px",
    borderRadius: 999,
    background: C.blueLight,
    color: C.text,
    fontSize: 10,
    fontWeight: 900,
  },

  noteFollowUpBadgeDue: {
    background: "#fff1ef",
    color: C.accent,
  },

  remarkEmpty: { padding: "40px 28px", textAlign: "center" },
  remarkEmptyIcon: { fontSize: 28, display: "block", marginBottom: 10, opacity: 0.55 },
  remarkEmptyText: { fontSize: 13, color: C.muted, fontStyle: "italic", margin: 0 },

  suggestionBox: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    boxShadow: `0 18px 44px ${C.shadowStrong}`,
    zIndex: 50,
    maxHeight: 240,
    overflowY: "auto",
  },

  suggestionHeader: {
    padding: "9px 14px 7px",
    fontSize: 10,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    color: C.muted,
    borderBottom: `1px solid ${C.border}`,
    fontWeight: 950,
  },

  suggestionItem: {
    padding: "10px 14px",
    fontSize: 13,
    color: C.text,
    cursor: "pointer",
    borderBottom: `1px solid ${C.soft}`,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  suggestionItemHovered: { background: C.accentLight, color: C.accentDark },
  suggestionDot: { color: C.accent, fontSize: 16, lineHeight: 1 },

  gallery: {
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${C.border}`,
    borderRadius: 28,
    padding: "24px 28px",
    boxShadow: `0 18px 54px ${C.shadowSoft}`,
    marginBottom: 24,
  },

  galleryHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },

  galleryTitle: {
    fontSize: 12,
    letterSpacing: 1.8,
    textTransform: "uppercase",
    color: C.accentDark,
    margin: 0,
    fontWeight: 950,
  },

  galleryAddBtn: {
    background: "#fff",
    border: `1px dashed ${C.accent}`,
    color: C.accent,
    borderRadius: 999,
    padding: "7px 14px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
  },

  galleryHelp: { fontSize: 12, color: C.muted, marginBottom: 14, fontWeight: 700 },
  galleryGrid: { display: "flex", flexWrap: "wrap", gap: 12 },
  galleryGridInTab: { display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 4 },

  galleryItem: {
    position: "relative",
    borderRadius: 18,
    overflow: "hidden",
    border: `2px solid ${C.border}`,
    transition: "border-color .15s, transform .15s, box-shadow .15s, opacity .15s",
    background: "transparent",
    padding: 0,
  },

  galleryItemClickable: { cursor: "pointer" },
  galleryItemLoading: { opacity: 0.7 },
  galleryItemPrimary: { border: `2px solid ${C.accent}` },

  galleryImg: { width: 146, height: 146, objectFit: "cover", display: "block" },

  galleryImgFallback: {
    width: 146,
    height: 146,
    background: C.accentLight,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 32,
    color: C.muted,
  },

  galleryBadge: {
    position: "absolute",
    bottom: 8,
    left: 8,
    background: C.accent,
    color: "#fff",
    fontSize: 10,
    fontWeight: 950,
    padding: "4px 8px",
    borderRadius: 999,
    textTransform: "uppercase",
  },

  gallerySetBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    background: "rgba(17,24,39,0.82)",
    color: "#fff",
    fontSize: 10,
    fontWeight: 950,
    padding: "4px 8px",
    borderRadius: 999,
    textTransform: "uppercase",
  },

  galleryUpdatingBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    background: C.accent,
    color: "#fff",
    fontSize: 10,
    fontWeight: 950,
    padding: "4px 8px",
    borderRadius: 999,
    textTransform: "uppercase",
  },
  photoPreviewBackdrop: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(10, 12, 18, 0.82)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1600,
    backdropFilter: "blur(10px)",
    animation: "fadeIn 0.2s ease both",
    padding: 16,
    boxSizing: "border-box",
    overflowY: "auto",
    overflowX: "hidden",
  },
  photoPreviewModal: {
    background: "rgba(255,255,255,0.98)",
    borderRadius: 28,
    width: "calc(100vw - 32px)",
    maxWidth: 1480,
    height: "calc(100vh - 32px)",
    maxHeight: 980,
    overflow: "hidden",
    boxShadow: "0 28px 90px rgba(37,41,53,0.28)",
    animation: "slideUp 0.25s ease both",
    display: "flex",
    flexDirection: "column",
    margin: "auto",
    boxSizing: "border-box",
  },
  photoPreviewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "24px 28px 18px",
    borderBottom: `1px solid ${C.border}`,
  },
  photoPreviewToolbar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  photoPreviewTitle: {
    fontSize: 24,
    fontWeight: 950,
    color: C.text,
    margin: 0,
  },
  photoPreviewBody: {
    padding: 16,
    background: "rgba(37,41,53,0.04)",
    display: "flex",
    alignItems: "stretch",
    justifyContent: "stretch",
    minHeight: 0,
    flex: 1,
  },
  photoPreviewViewport: {
    flex: 1,
    overflow: "auto",
    borderRadius: 20,
    background: "rgba(255,255,255,0.72)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  photoPreviewImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "auto",
    objectFit: "contain",
    borderRadius: 20,
    display: "block",
    boxShadow: "0 18px 44px rgba(37,41,53,0.16)",
    transformOrigin: "center center",
    transition: "transform 0.18s ease",
  },
  photoPreviewFallback: {
    width: "100%",
    minHeight: 260,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 20,
    background: C.soft,
    color: C.muted,
    fontWeight: 800,
    fontSize: 15,
  },
  photoPreviewFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    padding: "18px 28px 24px",
    borderTop: `1px solid ${C.border}`,
  },
  photoPreviewMeta: {
    fontSize: 13,
    color: C.muted,
    fontWeight: 800,
  },
  photoPreviewActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  photoZoomButton: {
    width: 42,
    height: 42,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontSize: 20,
    fontWeight: 900,
    lineHeight: 1,
  },
  photoZoomResetButton: {
    height: 42,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
    padding: "0 14px",
  },
  photoZoomButtonDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },
  photoPreviewCloseButton: {
    width: 42,
    height: 42,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.muted,
    cursor: "pointer",
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1,
  },

  noPhotoNudge: {
    background: "rgba(255,255,255,0.84)",
    border: `1px dashed ${C.border}`,
    borderRadius: 28,
    padding: 40,
    textAlign: "center",
    boxShadow: `0 14px 38px ${C.shadowLight}`,
  },

  noPhotoIcon: { fontSize: 34, color: C.accent, display: "block", marginBottom: 10 },
  noPhotoText: { color: C.muted, fontSize: 13, margin: "0 0 16px", fontWeight: 800 },

  noPhotoBtn: {
    background: C.accent,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "10px 22px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 950,
  },

  overlayBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(37,41,53,0.62)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1400,
    backdropFilter: "blur(7px)",
    animation: "fadeIn 0.2s ease both",
    padding: 20,
    overscrollBehavior: "contain",
  },

  overlayCard: {
    background: C.card,
    borderRadius: 28,
    width: "100%",
    maxWidth: 620,
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 28px 90px rgba(37,41,53,0.28)",
    animation: "slideUp 0.25s ease both",
    display: "flex",
    flexDirection: "column",
  },

  alertsOverlayCard: {
    background: C.card,
    borderRadius: 28,
    width: "100%",
    maxWidth: 920,
    maxHeight: "90vh",
    overflow: "hidden",
    boxShadow: "0 28px 90px rgba(37,41,53,0.28)",
    animation: "slideUp 0.25s ease both",
    display: "flex",
    flexDirection: "column",
  },

  overlayHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "28px 32px 20px",
    borderBottom: `1px solid ${C.border}`,
  },

  overlayEyebrow: {
    fontSize: 10,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    color: C.accent,
    margin: "0 0 4px",
    fontWeight: 950,
  },

  overlayTitle: { fontSize: 24, fontWeight: 950, color: C.text, margin: 0 },

  alertsOverlaySub: {
    margin: "8px 0 0",
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.55,
    fontWeight: 700,
  },

  overlayClose: {
    background: "none",
    border: "none",
    fontSize: 30,
    color: C.muted,
    cursor: "pointer",
    lineHeight: 1,
    padding: 0,
  },

  alertsOverlayBody: {
    padding: "20px 32px 28px",
    overflowY: "auto",
  },

  alertsOverlayList: {
    display: "grid",
    gap: 14,
  },

  dropZone: {
    margin: "20px 32px 0",
    border: `2px dashed ${C.border}`,
    borderRadius: 20,
    padding: "26px 0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
    background: C.accentLight,
  },

  dropIcon: { fontSize: 30, color: C.accent, fontWeight: 950 },
  dropText: { fontSize: 14, color: C.accent, fontWeight: 950 },
  dropSub: { fontSize: 11, color: C.muted, fontWeight: 700 },
  processingDropZone: {
    opacity: 0.78,
    cursor: "progress",
  },
  processingInlineNote: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    marginTop: 4,
    fontSize: 12,
    color: C.accent,
    fontWeight: 900,
  },
  processingSpinner: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: `2px solid ${C.border}`,
    borderTopColor: C.accent,
    display: "inline-block",
    animation: "spin 0.8s linear infinite",
  },

  overlayHint: {
    fontSize: 12,
    color: C.muted,
    margin: "14px 32px 10px",
    textAlign: "center",
    fontWeight: 800,
  },

  previewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(124px, 1fr))",
    gap: 10,
    padding: "0 32px 20px",
  },

  previewCard: {
    position: "relative",
    borderRadius: 16,
    overflow: "hidden",
    border: `2px solid ${C.border}`,
    cursor: "pointer",
    transition: "border-color .15s",
    background: C.soft,
  },

  previewCardPrimary: { border: `2px solid ${C.accent}`, boxShadow: `0 0 0 3px ${C.accentLight}` },
  previewImg: { width: "100%", height: 116, objectFit: "cover", display: "block" },

  primaryBadge: {
    position: "absolute",
    top: 7,
    left: 7,
    background: C.accent,
    color: "#fff",
    fontSize: 10,
    fontWeight: 950,
    padding: "3px 7px",
    borderRadius: 999,
  },

  previewRemove: {
    position: "absolute",
    top: 7,
    right: 7,
    background: "rgba(37,41,53,0.7)",
    border: "none",
    color: "#fff",
    borderRadius: "50%",
    width: 23,
    height: 23,
    fontSize: 16,
    lineHeight: "23px",
    textAlign: "center",
    cursor: "pointer",
    padding: 0,
  },

  previewName: {
    fontSize: 10,
    color: C.muted,
    padding: "5px 7px",
    background: C.accentLight,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontWeight: 700,
  },

  overlayFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 32px 24px",
    borderTop: `1px solid ${C.border}`,
    marginTop: "auto",
  },

  uploadStatus: { fontSize: 12, color: C.accent, fontWeight: 950 },
  uploadHint: { fontSize: 12, color: C.muted, fontWeight: 800 },

  overlayCancel: {
    background: "none",
    border: `1px solid ${C.border}`,
    color: C.muted,
    borderRadius: 999,
    padding: "10px 18px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
  },

  overlaySubmit: {
    background: C.text,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "10px 22px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 950,
  },

  watermarkModalBackdrop: {
    ...{
      position: "fixed",
      inset: 0,
      background: "rgba(37,41,53,0.62)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(7px)",
      animation: "fadeIn 0.2s ease both",
      padding: 20,
    },
  },
  biodataModalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(37,41,53,0.62)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1400,
    backdropFilter: "blur(7px)",
    animation: "fadeIn 0.2s ease both",
    padding: 20,
    overscrollBehavior: "contain",
  },
  biodataModalCard: {
    background: C.card,
    borderRadius: 28,
    width: "100%",
    maxWidth: 620,
    boxShadow: "0 28px 90px rgba(37,41,53,0.28)",
    animation: "slideUp 0.25s ease both",
    display: "flex",
    flexDirection: "column",
  },
  documentHistoryModalCard: {
    background: C.card,
    borderRadius: 28,
    width: "100%",
    maxWidth: 620,
    maxHeight: "90vh",
    boxShadow: "0 28px 90px rgba(37,41,53,0.28)",
    animation: "slideUp 0.25s ease both",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  biodataModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    padding: "28px 32px 18px",
    borderBottom: `1px solid ${C.border}`,
  },
  biodataOptionList: {
    display: "grid",
    gap: 10,
    padding: "20px 32px",
  },
  biodataOptionRowDisabled: {
    opacity: 0.5,
  },
  biodataOptionError: {
    padding: "12px 14px",
    borderRadius: 14,
    background: "#fff3f0",
    border: "1px solid rgba(210,58,43,0.22)",
    color: C.accent,
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 800,
  },
  documentHistoryBody: {
    display: "grid",
    gap: 14,
    padding: "20px 32px",
    overflowY: "auto",
  },
  documentHistoryMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    fontSize: 12,
    color: C.muted,
    fontWeight: 800,
  },
  documentHistoryList: {
    display: "grid",
    gap: 12,
  },
  documentHistoryItem: {
    display: "grid",
    gap: 6,
    padding: "14px 16px",
    borderRadius: 18,
    border: `1px solid ${C.border}`,
    background: C.soft,
  },
  documentHistoryItemTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  documentHistoryActor: {
    fontSize: 14,
    color: C.text,
    fontWeight: 950,
  },
  documentHistoryActionBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "5px 10px",
    background: C.accentLight,
    color: C.accent,
    fontSize: 11,
    fontWeight: 950,
  },
  documentHistoryItemMeta: {
    fontSize: 12,
    color: C.muted,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  watermarkModalCard: {
    background: C.card,
    borderRadius: 28,
    width: "100%",
    maxWidth: 620,
    boxShadow: "0 28px 90px rgba(37,41,53,0.28)",
    animation: "slideUp 0.25s ease both",
    display: "flex",
    flexDirection: "column",
  },
  watermarkModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    padding: "28px 32px 18px",
    borderBottom: `1px solid ${C.border}`,
  },
  watermarkModalText: {
    margin: "8px 0 0",
    color: C.muted,
    fontSize: 14,
    lineHeight: 1.6,
    fontWeight: 700,
  },
  watermarkOptionList: {
    display: "grid",
    gap: 10,
    padding: "20px 32px",
  },
  watermarkOptionRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    borderRadius: 16,
    background: C.soft,
    border: `1px solid ${C.border}`,
    color: C.text,
    fontSize: 14,
    fontWeight: 800,
  },
  watermarkEmptyState: {
    padding: "14px 16px",
    borderRadius: 16,
    background: C.soft,
    border: `1px solid ${C.border}`,
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.6,
    fontWeight: 700,
  },
  watermarkModalFooter: {
    display: "grid",
    gap: 12,
    padding: "0 32px 24px",
  },
  watermarkFileHint: {
    fontSize: 12,
    color: C.muted,
    fontWeight: 800,
    wordBreak: "break-word",
  },
  watermarkActionRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
  },
  overlaySubmitDisabled: { opacity: 0.45, cursor: "not-allowed" },
};
